'use client';

import { getBotgoVideo25InputDuration, validateBotgoVideo25Inputs } from '@/lib/botgo-video-25';
import { validateKlingV4Inputs, getKlingV4MaxFrames, getKlingV4InputDuration } from '@/config/kling-v4';
import { PLATFORM_PROMPT_LIMIT, countPromptCharacters, getPromptLengthError } from '@/lib/prompt-length';
import { isWanVideoModel, getWanInputVideoDuration, validateWanVideoInputs } from '@/lib/wan-video';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { useRouter, usePathname } from 'next/navigation';
import { useGenerationRunner } from '@/hooks/generation/useGenerationRunner';
import { usePublicVisibility } from '@/hooks/usePublicVisibility';
import { useCampaignConfigs } from '@/hooks/useCampaignConfigs';
import { getPricingSpotlightState } from '@/config/site-campaigns';
import { resolveModelCreditDiscount } from '@/lib/subscription-credit-discounts';
import { getPlanIdentity } from '@/lib/plan-identity';
import PublicVisibilityToggle from '@/components/social/PublicVisibilityToggle';
import ContentFilteringControl from '@/components/generation/ContentFilteringControl';
import { isBotgoContentFilteringModel } from '@/lib/generation/content-filtering';
import { useContentFiltering } from '@/hooks/generation/useContentFiltering';
import { resolveRequestCommunityVisibility } from '@/lib/social/public-visibility-client';
import { buildStudioGenerationParams } from '@/components/generation/shared/build-studio-generation-params';
import { buildRemixGenerationParams } from '@/components/generation/video/build-remix-generation-params';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import GenerationModeControl from '@/components/generation/GenerationModeControl';
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMessages, useTranslations } from 'next-intl';

// 导入Shadcn/UI组件
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Slider } from "@/components/ui/slider";
import { useToast } from '@/components/ToastProvider';
import {
  readSharedGeneratorState,
  SHARED_GENERATOR_STATE_EVENT,
  toGeneratorHydrationDetail,
  writeSharedGeneratorState,
} from '@/lib/generation/shared-generator-state';
import {
  IconChevronDown,
  IconChevronUp,
  IconX,
  IconBan,
  IconDeviceMobile,
  IconDeviceTv,
  IconSquare,
  IconLayoutGridAdd,
  IconVideo,
  IconAt,
  IconMusic,
  IconInfoCircle,
  IconBolt,
  IconPhoto,
  IconPhotoEdit,
  IconArrowsRightLeft,
  IconDots,
  IconRefresh,
  IconPlus,
  IconUpload,
  IconWand,
  IconArrowUp,
  IconClock,
  IconFolderOpen,
  IconCheck,
} from '@tabler/icons-react';

import TurnstileWidget from '@/components/TurnstileWidget';
import TopUpModal from '@/components/TopUpModal';
import InsufficientCreditsModal from '@/components/InsufficientCreditsModal';
import AuthModal from '@/components/AuthModal';
import PricingModal from '@/components/PricingModal';
import { isModelAllowedForPlan, resolvePlanCapabilities } from '@/lib/plan-metadata-capabilities';
import { MINIMAX_H3_IMAGE_PRICING } from '@/lib/minimax-h3-billing';
import {
  MODEL_REGISTRY,
  getModelCapabilities,
  getModelConfig,
  getAllowedDurationsForVideoRequest,
  getVideoCreditCost,
  getImageCreditCost,
  resolveModelKey,
} from '@/config/model-registry';
import {
  GEMINI_OMNI_VIP_DEFAULT_DURATION_SECONDS,
  getGeminiOmniCreditCost,
  isAutoVideoEditDurationMode,
  resolveAutoVideoEditBillingDuration,
} from '@/lib/gemini-omni-billing';
import {
  getInputVideoDurationSeconds,
  isSeedance20InputVideoBillingModel,
} from '@/lib/seedance20-billing';
import { MODEL_MENU_BADGE_STYLES } from '@/config/model-navigation';
import {
  getAcceptedMimeTypesForMode,
  getAllowedMediaTypesForMode,
  getMaxFileSizeBytesForMediaType,
  getMaxFileSizeLabelForMediaType,
  getMaxMediaUploadsForMode,
  getMaxUploadsByTypeForMode,
  getVideoInputRules,
  inferInputMediaType,
  isAcceptedInputFile,
  isSeedanceVideoModelKey,
  validateBotgoVideo25FlashInputAsset,
  validateSeedanceInputAsset,
  validateVideoInputAssetDuration,
} from '@/config/video-input-rules';
import { getClient } from '@/lib/supabase/client';
import { fetchCachedApi, CLIENT_CACHE_KEYS } from '@/lib/client-request-cache';
import { normalizeInputAssets } from '@/lib/generation/normalize-input-assets';
import { runGenerationPreflight } from '@/lib/generation/preflight-client';
import { resolveGenerationAccessState } from '@/lib/generation/unlimited-access-state';
import { generateSeedForModel, isValidVideoSeedForModel } from '@/lib/video-seed';
import {
  formatSlowLaneDelayRangeLabel,
  SLOW_LANE_ENTITLEMENT,
} from '@/config/slow-lane-profiles';
import { getErrorKey, getErrorMessageText, getErrorValues, localizeErrorMessage } from '@/utils/errorMapping';
import { sendGAEvent } from '@/utils/analytics';
import {
  GENERATOR_FULL_SURFACE_CLASS,
  GENERATOR_FULL_SURFACE_STYLE,
  GENERATOR_MENU_ACTION_CLASS,
  GENERATOR_MENU_INSET_CLASS,
  GENERATOR_MENU_ITEM_ACTIVE_CLASS,
  GENERATOR_MENU_ITEM_HOVER_CLASS,
  GENERATOR_MENU_ITEM_SELECTED_CLASS,
  GENERATOR_MENU_PANEL_CLASS,
  GENERATOR_MENU_TRACK_CLASS,
  GENERATOR_MENU_TRACK_SELECTED_CLASS,
  GENERATOR_TOOLBAR_CONTROL_CLASS,
  GENERATOR_TOOLTIP_SURFACE_CLASS,
  GENERATOR_UPLOAD_SLOT_CLASS,
} from '@/config/generator-surface';
import {
  ENABLE_VEO31_GENERATOR_EXTEND,
  isVeo31GeneratorExtendModelKey,
} from '@/config/veo31-extend-feature';
import {
  FREE_SUBSIDY_UPGRADE_VARIANTS,
  buildModelMenuGroups,
  DISABLED_GENERATOR_MODEL_KEYS,
  getModelMenuBadgeKeys,
  getAspectFromSize,
  HIDDEN_GENERATOR_MODEL_KEYS,
  IMAGE_EDITING_PROMPT_PLACEHOLDER_KEY_MAP,
  IMAGE_PROMPT_PLACEHOLDER_KEY_MAP,
  MODEL_ASPECT_RATIO_DISPLAY_ORDER,
  MODEL_DEFAULT_DURATION,
  MODEL_DEFAULT_IMAGE_BY_FUNCTION_MODE,
  MODEL_DEFAULT_RESOLUTION_LABEL,
  MODEL_DEFAULT_VIDEO_BY_FUNCTION_MODE,
  MODEL_DESCRIPTION_KEY_MAP,
  MODEL_MENU_GENERATION_TIME_KEY_MAP,
  MODEL_RESOLUTION_DISPLAY_ORDER,
  SOURCE_DRIVEN_VIDEO_FIXED_SPEC_MODELS_BY_MODE,
  SOURCE_DRIVEN_VIDEO_RESOLUTION_SPEC_MODELS_BY_MODE,
  NEW_BADGE_MODEL_KEYS,
  OUTPUT_COUNT_OPTIONS,
  resolvePreferredAspectRatio,
  resolvePreferredDuration,
  resolvePreferredImageModelForFunctionMode,
  resolvePreferredVideoModelForFunctionMode,
  BOTGO_VIDEO_SEED_MODELS,
  HAPPYHORSE_VIDEO_MODELS,
  isSeedDefaultEnabledVideoModel,
  KIEAI_VEO31_EXTEND_MODEL_KEYS,
  SEEDANCE_20_ADVANCED_VIDEO_MODELS,
  SEEDANCE_20_AUDIO_VIDEO_MODELS,
  SEEDANCE_LEGACY_VIDEO_MODELS,
  SEEDANCE_SEED_MODELS,
  sortModelsByDisplayOrder,
  supportsImageFunctionMode,
  supportsVideoFunctionMode,
  TURNSTILE_REVERIFY_SIGNAL,
  VEO_EXTEND_SELECTABLE_MODEL_KEYS,
  VEO_GCP_EXTEND_MODEL_KEYS,
  VEO_VIDEO_MODELS,
} from '@/components/generation/shared/studio-config';
import { VideoEditingModeIcon, VideoExtendModeIcon } from '@/components/generation/shared/video-mode-icons';
import { resolveStandaloneEnhanceResolution } from '@/lib/seedance-sr-plan.mjs';

// 在文件顶部添加防抖函数
const debounce = (func, wait) => {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

const MODEL_MENU_UNLIMITED_BADGE_STYLE = {
  borderColor: 'rgba(110, 231, 183, 0.65)',
  backgroundColor: 'rgba(16, 185, 129, 0.25)',
  color: 'rgb(209, 250, 229)',
  boxShadow: '0 0 10px rgba(16, 185, 129, 0.18)',
};

const EMPTY_MODEL_SET = new Set();

const normalizeValue = (value = '') => String(value ?? '').trim().toLowerCase();

const normalizeAspectRatioLabel = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'auto' || normalized === 'adaptive') return normalized;
  const match = String(value || '').match(/(\d+)\s*:\s*(\d+)/);
  if (!match) return null;
  return `${Number(match[1])}:${Number(match[2])}`;
};

const parseDimensionsFromSize = (value = '') => {
  const match = String(value || '').match(/(\d{3,5})\s*(?:x|X|\u00d7)\s*(\d{3,5})/);
  if (!match) return null;
  const width = Number(match[1]);
  const height = Number(match[2]);
  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
    return null;
  }
  return { width: Math.round(width), height: Math.round(height) };
};

const getAspectRatioFromDimensions = (widthValue, heightValue) => {
  const width = Number(widthValue);
  const height = Number(heightValue);
  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
    return null;
  }
  const ratio = width / height;
  if (Math.abs(ratio - 1) <= 0.03) return '1:1';
  if (ratio > 1) {
    if (Math.abs(ratio - (21 / 9)) <= 0.08) return '21:9';
    if (Math.abs(ratio - (4 / 3)) <= 0.06) return '4:3';
    return '16:9';
  }
  if (Math.abs(ratio - (3 / 4)) <= 0.06) return '3:4';
  return '9:16';
};

const getResolutionLabelFromDimensions = (widthValue, heightValue) => {
  const width = Number(widthValue);
  const height = Number(heightValue);
  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
    return null;
  }
  const shortEdge = Math.min(width, height);
  if (shortEdge >= 2000) return '4K';
  if (shortEdge >= 1000) return '1080p';
  if (shortEdge >= 650) return '720p';
  if (shortEdge >= 430) return '480p';
  return null;
};

const getResolutionLabelFromValue = (value) => {
  const normalized = normalizeValue(value);
  if (!normalized) return null;
  if (normalized.includes('4k') || normalized.includes('2160')) return '4K';
  if (normalized.includes('1080')) return '1080p';
  if (normalized.includes('720')) return '720p';
  if (normalized.includes('480')) return '480p';
  const dimensions = parseDimensionsFromSize(value);
  return dimensions ? getResolutionLabelFromDimensions(dimensions.width, dimensions.height) : null;
};

const parseDurationSeconds = (value) => {
  const numeric = Number(String(value ?? '').trim().replace(/s$/i, ''));
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const uniqueCompactValues = (values = []) => {
  const seen = new Set();
  return values.reduce((acc, value) => {
    const normalized = String(value ?? '').trim();
    if (!normalized) return acc;
    const key = normalizeValue(normalized);
    if (seen.has(key)) return acc;
    seen.add(key);
    acc.push(normalized);
    return acc;
  }, []);
};

const formatMenuRangeLabel = (values = []) => {
  const compactValues = uniqueCompactValues(values);
  if (compactValues.length === 0) return '';
  if (compactValues.length === 1) return compactValues[0];
  return `${compactValues[0]}-${compactValues[compactValues.length - 1]}`;
};

const getMenuResolutionValues = (modelKey) => {
  const caps = getModelCapabilities(modelKey) || {};
  const allowedResolutions = Array.isArray(caps.allowedResolutions)
    ? caps.allowedResolutions
    : [];
  const displayOrder = MODEL_RESOLUTION_DISPLAY_ORDER[modelKey] || [];
  const normalizedAllowed = new Set(allowedResolutions.map((value) => normalizeValue(value)));
  const orderedValues = displayOrder.length
    ? displayOrder.filter((value) => normalizedAllowed.size === 0 || normalizedAllowed.has(normalizeValue(value)))
    : allowedResolutions;

  return uniqueCompactValues(orderedValues.length
    ? orderedValues
    : [MODEL_DEFAULT_RESOLUTION_LABEL[modelKey]].filter(Boolean));
};

const getMenuDurationValues = (modelKey) => {
  const caps = getModelCapabilities(modelKey) || {};
  const allowedDurations = Array.isArray(caps.allowedDurations)
    ? caps.allowedDurations
    : [];
  const durationValues = allowedDurations
    .map(parseDurationSeconds)
    .filter((value) => Number.isFinite(value) && value > 0)
    .sort((a, b) => a - b);

  if (durationValues.length > 0) {
    return [...new Set(durationValues)];
  }

  const fallbackDuration = parseDurationSeconds(caps.defaultDuration || MODEL_DEFAULT_DURATION[modelKey]);
  return fallbackDuration ? [fallbackDuration] : [];
};

const formatMenuDurationLabel = (modelKey) => {
  const durationValues = getMenuDurationValues(modelKey);
  if (durationValues.length === 0) return '';
  if (durationValues.length === 1) return `${durationValues[0]}s`;
  return `${durationValues[0]}-${durationValues[durationValues.length - 1]}s`;
};

const resolveModelMenuMinimumCredits = ({ model, functionMode }) => {
  const modelKey = model?.key;
  if (!modelKey) return null;

  const modelType = model?.type || getModelConfig(modelKey)?.type;
  const resolutionValues = getMenuResolutionValues(modelKey);
  const resolutions = resolutionValues.length ? resolutionValues : [undefined];
  const durations = modelType === 'video' ? getMenuDurationValues(modelKey) : [undefined];
  const videoDurations = durations.length ? durations : [undefined];
  const caps = getModelCapabilities(modelKey) || {};
  const quality = Array.isArray(caps.allowedQualities) && caps.allowedQualities.length
    ? caps.allowedQualities[0]
    : caps.defaultQuality;
  let minimumCredits = Number.POSITIVE_INFINITY;

  resolutions.forEach((resolutionLabel) => {
    if (modelType === 'image') {
      const credits = getImageCreditCost({
        modelKey,
        resolution: resolutionLabel,
        resolutionLabel,
        quality,
      });
      if (Number.isFinite(credits) && credits > 0) {
        minimumCredits = Math.min(minimumCredits, credits);
      }
      return;
    }

    videoDurations.forEach((duration) => {
      const credits = getVideoCreditCost({
        modelKey,
        resolution: resolutionLabel,
        resolutionLabel,
        duration,
        functionMode,
      });
      if (Number.isFinite(credits) && credits > 0) {
        minimumCredits = Math.min(minimumCredits, credits);
      }
    });
  });

  return Number.isFinite(minimumCredits) ? minimumCredits : null;
};

const getCaseInsensitiveValue = (object, targetKey) => {
  if (!object || typeof object !== 'object') return undefined;
  const normalizedTarget = normalizeValue(targetKey);
  const match = Object.entries(object).find(([key]) => normalizeValue(key) === normalizedTarget);
  return match ? match[1] : undefined;
};

const getSizeForSpec = (sizesByAspect, aspectRatio, resolutionLabel) => {
  const aspectEntry = getCaseInsensitiveValue(sizesByAspect, aspectRatio);
  if (!aspectEntry || typeof aspectEntry !== 'object') return null;
  return getCaseInsensitiveValue(aspectEntry, resolutionLabel) || null;
};

const isInputVideoDurationError = (errorLike) => {
  const code = normalizeValue(getErrorKey(errorLike));
  const message = normalizeValue(getErrorMessageText(errorLike));
  return (
    code.includes('video_duration') ||
    code === 'total_video_duration' ||
    code === 'total_audio_duration' ||
    message.includes('reference video too long') ||
    message.includes('source video too long') ||
    message.includes('supports at most') ||
    (message.includes('must be') && message.includes('or shorter') && message.includes('received'))
  );
};

const VEO_EXTEND_SOURCE_MODEL_KEYS = new Set([
  'veo-3.1-fast-lite',
  'veo-3.1-lite-vip',
  'veo-3.1-fast',
  'veo-3.1-fast-vip',
  'veo-3.1',
  'veo-3.1-vip',
  'veo-3.1-lite-extend',
  'veo-3.1-fast-extend',
  'veo-3.1-extend',
  'veo-3.1-lite-extend-vip',
  'veo-3.1-fast-extend-vip',
  'veo-3.1-extend-vip',
]);

const isCompatibleVeoExtendSourceModelKey = (value) => {
  const resolved = resolveModelKey(value || '');
  return VEO_EXTEND_SOURCE_MODEL_KEYS.has(resolved);
};

const resolveVeoExtendSourceProviderRoute = (...values) => {
  const normalized = values
    .flatMap((value) => {
      if (!value || typeof value !== 'object') return [value];
      return [
        value.apiProvider,
        value.apiProviderSlug,
        value.apiProviderType,
        value.apiProviderModelName,
        value.sourceApiProvider,
        value.sourceApiProviderSlug,
        value.sourceApiProviderType,
        value.sourceApiProviderModelName,
      ];
    })
    .filter(Boolean)
    .join(' ')
    .trim()
    .toLowerCase();
  if (!normalized) return null;
  if (normalized.includes('kieai')) return 'kieai';
  return null;
};

const normalizeVeoExtendAspectRatioLabel = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return null;
  if (normalized.includes('16:9') || normalized.includes('16 / 9') || normalized.includes('landscape')) {
    return '16:9';
  }
  if (normalized.includes('9:16') || normalized.includes('9 / 16') || normalized.includes('portrait')) {
    return '9:16';
  }
  return null;
};

const resolveVeoExtendAspectRatioFromDimensions = (widthValue, heightValue) => {
  const width = Number(widthValue);
  const height = Number(heightValue);
  if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
    return width >= height ? '16:9' : '9:16';
  }
  return null;
};

const parseVeoExtendDimensionsFromText = (value) => {
  const match = String(value || '').match(/(\d{3,5})\s*(?:x|X|\u00d7)\s*(\d{3,5})/);
  if (!match) return null;
  const width = Number(match[1]);
  const height = Number(match[2]);
  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
    return null;
  }
  return { width, height };
};

const resolveVeoExtendDraftAspectRatio = ({ item = {}, asset = {} } = {}) => {
  const dimensionAspectRatio = resolveVeoExtendAspectRatioFromDimensions(
    asset.width ?? item.width ?? item.metadata?.width ?? item.metadata?.videoWidth,
    asset.height ?? item.height ?? item.metadata?.height ?? item.metadata?.videoHeight
  );
  if (dimensionAspectRatio) {
    return dimensionAspectRatio;
  }

  const dimensionCandidates = [
    asset.sourceResolution,
    asset.resolution,
    asset.size,
    item.resolution,
    item.size,
    item.metadata?.resolution,
    item.metadata?.size,
    item.metadata?.requestData?.resolution,
    item.metadata?.requestData?.size,
    item.metadata?.params?.resolution,
    item.metadata?.params?.size,
  ];

  for (const candidate of dimensionCandidates) {
    const dimensions = parseVeoExtendDimensionsFromText(candidate);
    const resolved = dimensions
      ? resolveVeoExtendAspectRatioFromDimensions(dimensions.width, dimensions.height)
      : null;
    if (resolved) return resolved;
  }

  const candidates = [
    asset.sourceAspectRatio,
    asset.aspectRatio,
    asset.aspect,
    asset.size,
    item.aspectRatio,
    item.aspect_ratio,
    item.metadata?.aspectRatio,
    item.metadata?.aspect_ratio,
    item.metadata?.requestData?.aspectRatioLabel,
    item.metadata?.requestData?.aspectRatio,
    item.metadata?.params?.aspect,
  ];

  for (const candidate of candidates) {
    const resolved = normalizeVeoExtendAspectRatioLabel(candidate);
    if (resolved) return resolved;
  }

  return null;
};

const resolveVeoExtendDraftResolution = ({ item = {}, asset = {} } = {}) => {
  const candidates = [
    asset.sourceResolution,
    asset.resolution,
    asset.size,
    item.resolution,
    item.size,
    item.metadata?.resolution,
    item.metadata?.requestData?.resolutionLabel,
    item.metadata?.requestData?.resolution,
  ];

  for (const candidate of candidates) {
    const normalized = String(candidate || '').trim().toLowerCase();
    if (!normalized) continue;
    if (normalized.includes('4k') || normalized.includes('2160')) return '4K';
    if (normalized.includes('1080')) return '1080p';
    if (normalized.includes('720')) return '720p';
  }

  const width = Number(asset.width ?? item.width ?? item.metadata?.width ?? item.metadata?.videoWidth);
  const height = Number(asset.height ?? item.height ?? item.metadata?.height ?? item.metadata?.videoHeight);
  if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
    const shortEdge = Math.min(width, height);
    if (shortEdge >= 2000) return '4K';
    if (shortEdge >= 1000) return '1080p';
    return '720p';
  }

  return '720p';
};

const createRandomSeed = (modelOrIsHappyhorse = false) => generateSeedForModel(modelOrIsHappyhorse);

const scrollIntoViewIfNeeded = (node, { margin = 80, block = 'center' } = {}) => {
  if (!node || typeof window === 'undefined') return;
  const rect = node.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  if (rect.top < margin || rect.bottom > viewportHeight - margin) {
    node.scrollIntoView({ behavior: 'smooth', block });
  }
};

const clampOutputCount = (value) => {
  const maxOutputCount = OUTPUT_COUNT_OPTIONS[OUTPUT_COUNT_OPTIONS.length - 1] || 1;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(1, Math.min(maxOutputCount, Math.floor(parsed)));
};

const resolveEffectiveOutputCount = (value, generationMode) => (
  generationMode === 'unlimited' ? 1 : clampOutputCount(value)
);

const translateStudioMessage = (t, key, values, fallback) => {
  if (typeof t !== 'function') return fallback;
  try {
    return t(key, values);
  } catch {
    return fallback;
  }
};

const IMAGE_REQUIRED_GENERAL_VIDEO_MODELS = new Set([
  'grok-imagine-video-1.5',
  'grok-imagine-video-1.5-special',
  'grok-imagine-video-1.5-vip',
]);

const OPTIONAL_END_FRAME_VIDEO_MODELS = new Set([
  'botgo-video-2.5',
  'kling-v4',
  'kling-v3',
]);

const buildGenerationRateLimitMessage = (message, t) => {
  const raw = String(message || '').trim();
  const match = raw.match(/(\d+)\s+request(?:s)?\s+per\s+(\d+)\s+minute(?:s)?/i);
  if (match) {
    const requests = Number(match[1]);
    const minutes = Number(match[2]);
    const fallback = `Generation limit: ${match[1]} request every ${match[2]} minutes. Try again later.`;
    return translateStudioMessage(t, 'rateLimit.exact', { requests, minutes }, fallback);
  }
  return translateStudioMessage(
    t,
    'rateLimit.generic',
    undefined,
    'Generation limit reached. Try again later.'
  );
};

const buildConcurrencyMessage = (t, { activeTaskCount, maxConcurrency, remainingSlots, message } = {}) => {
  const rawMessage = String(message || '').trim();
  const messageMatch = rawMessage.match(/allows up to\s+(\d+)\s+concurrent tasks?\. You currently have\s+(\d+)\s+active tasks?, so you can start\s+(\d+)\s+more/i);
  if (messageMatch) {
    return buildConcurrencyMessage(t, {
      maxConcurrency: Number(messageMatch[1]),
      activeTaskCount: Number(messageMatch[2]),
      remainingSlots: Number(messageMatch[3]),
    });
  }

  const active = Number(activeTaskCount);
  const max = Number(maxConcurrency);
  const remaining = Number(remainingSlots);
  const hasActiveAndMax = Number.isFinite(active) && Number.isFinite(max);
  const hasRemaining = Number.isFinite(remaining);

  if (hasActiveAndMax && hasRemaining) {
    if (remaining <= 0) {
      return translateStudioMessage(
        t,
        'concurrency.wait',
        { active, max },
        `Too many active tasks: ${active}/${max} running. Wait for a task to finish.`
      );
    }
    return translateStudioMessage(
      t,
      'concurrency.choose',
      { active, max, remaining },
      `Too many active tasks: ${active}/${max} running. Choose up to ${remaining} outputs or wait.`
    );
  }

  if (hasActiveAndMax) {
    return translateStudioMessage(
      t,
      'concurrency.reduce',
      { active, max },
      `Too many active tasks: ${active}/${max} running. Reduce outputs or wait.`
    );
  }

  return translateStudioMessage(
    t,
    'concurrency.generic',
    undefined,
    'Too many active tasks. Reduce outputs or wait.'
  );
};

const normalizeRecreateAssets = (detail = {}) => {
  return normalizeInputAssets({
    rawAssets: detail?.inputAssets ?? detail?.params?.inputAssets,
    fallbackImages: detail.imageUrl || detail.images || detail.params?.imageUrl || detail.params?.images,
    idPrefix: 'recreate-asset',
    fallbackPrefix: 'recreate-image',
  });
};

const buildResolutionOptionId = ({ aspectRatio, resolutionLabel, apiSize } = {}) =>
  `${String(aspectRatio || '').trim()}::${String(resolutionLabel || '').trim()}::${String(apiSize || '').trim()}`;

const VIDEO_FUNCTION_MODES = new Set(['general', 'frames', 'reference', 'editing', 'extend', 'enhance']);
const IMAGE_FUNCTION_MODES = new Set(['general', 'editing']);

const normalizeVideoFunctionMode = (value) => {
  const normalized = String(value || 'general').trim().toLowerCase();
  return VIDEO_FUNCTION_MODES.has(normalized) ? normalized : 'general';
};

const normalizeImageFunctionMode = (value) => {
  const normalized = String(value || 'general').trim().toLowerCase();
  return IMAGE_FUNCTION_MODES.has(normalized) ? normalized : 'general';
};

const normalizeFunctionModeForCreationType = (value, creationType = 'video') => (
  creationType === 'image'
    ? normalizeImageFunctionMode(value)
    : normalizeVideoFunctionMode(value)
);

const parseVideoFunctionMode = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return VIDEO_FUNCTION_MODES.has(normalized) ? normalized : null;
};

const resolveRecreateFunctionMode = ({ rawFunctionMode, targetModelKey, normalizedAssets = [] } = {}) => {
  const explicitMode = parseVideoFunctionMode(rawFunctionMode);
  const supportsMode = (mode) => supportsVideoFunctionMode({ modelKey: targetModelKey, functionMode: mode });
  const hasVideoAssets = normalizedAssets.some((asset) => asset.type === 'video');

  if (explicitMode && supportsMode(explicitMode) && !(explicitMode === 'general' && hasVideoAssets)) {
    return explicitMode;
  }

  if (hasVideoAssets && supportsMode('editing')) {
    return 'editing';
  }

  if (hasVideoAssets && supportsMode('extend')) {
    return 'extend';
  }

  const hasNonImageAssets = normalizedAssets.some((asset) => asset.type !== 'image');
  if ((hasNonImageAssets || normalizedAssets.length >= 3) && supportsMode('reference')) {
    return 'reference';
  }

  if (
    (explicitMode === 'frames' || (normalizedAssets.length === 2 && normalizedAssets.every((asset) => asset.type === 'image')))
    && supportsMode('frames')
  ) {
    return 'frames';
  }

  return supportsMode('general') ? 'general' : (explicitMode || 'general');
};

const filterRecreateAssetsForInputRules = (assets = [], inputRules = null, maxAssets = 0) => {
  if (!Array.isArray(assets) || !assets.length || !inputRules || inputRules.unsupported || maxAssets <= 0) {
    return [];
  }

  const allowedMediaTypes = Array.isArray(inputRules.allowedMediaTypes)
    ? inputRules.allowedMediaTypes
    : [];
  if (!allowedMediaTypes.length) {
    return [];
  }

  const allowedTypeSet = new Set(allowedMediaTypes);
  const maxAssetsByType = inputRules.maxAssetsByType || {};
  const countsByType = {};
  const filteredAssets = [];

  for (const asset of assets) {
    const assetType = asset?.type || 'image';
    if (!allowedTypeSet.has(assetType)) continue;

    const typeLimit = Number(maxAssetsByType[assetType]);
    const currentTypeCount = countsByType[assetType] || 0;
    if (Number.isFinite(typeLimit) && currentTypeCount >= typeLimit) continue;

    filteredAssets.push(asset);
    countsByType[assetType] = currentTypeCount + 1;

    if (filteredAssets.length >= maxAssets) break;
  }

  return filteredAssets;
};

const normalizePresetResolutionLabel = (value) => {
  const normalized = String(value || '').trim();
  return normalized || null;
};

const resolveDefaultResolutionOptionId = ({ modelKey, aspectRatio, preferredResolutionLabel } = {}) => {
  const caps = getModelCapabilities(modelKey) || {};
  const sizesByAspect = caps.allowedSizesByAspect || {};
  const ratioMap = sizesByAspect[aspectRatio] || {};
  const allowedResolutions = caps.allowedResolutions || [];
  const preferredLabel = normalizePresetResolutionLabel(preferredResolutionLabel)
    || MODEL_DEFAULT_RESOLUTION_LABEL[modelKey];
  const matchedPreferredLabel = preferredLabel
    ? allowedResolutions.find((label) => String(label).toLowerCase() === String(preferredLabel).toLowerCase())
    : null;
  const resolutionLabel = (
    matchedPreferredLabel
    && ratioMap[matchedPreferredLabel]
  )
    ? matchedPreferredLabel
    : allowedResolutions.find((label) => ratioMap[label]);

  if (!resolutionLabel) return '';

  return buildResolutionOptionId({
    aspectRatio,
    resolutionLabel,
    apiSize: ratioMap[resolutionLabel],
  });
};

const GROK_IMAGINE_MODE_OPTIONS = ['fun', 'normal', 'spicy'];

const hasPositiveNumericValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
};

const REFERENCE_MENTION_TYPE_LABELS = {
  image: 'Image',
  video: 'Video',
  audio: 'Audio',
};

const REFERENCE_MENTION_TYPE_ALIASES = {
  image: 'image',
  images: 'image',
  图片: 'image',
  video: 'video',
  videos: 'video',
  视频: 'video',
  audio: 'audio',
  audios: 'audio',
  音频: 'audio',
  音樂: 'audio',
  音乐: 'audio',
};

const REFERENCE_MENTION_PATTERN = /@(?:\s*)(image|images|video|videos|audio|audios|图片|视频|音频|音樂|音乐)\s*(\d+)/gi;

const REFERENCE_MENTION_MODEL_PREFIXES = ['seedance-', 'happyhorse-', 'kling-'];

const supportsReferenceMentionModel = (modelKey, functionMode) => {
  const normalizedKey = String(modelKey || '').toLowerCase();
  const normalizedMode = String(functionMode || '').toLowerCase();
  if (normalizedKey === 'grok-imagine-video-vip') {
    return normalizedMode === 'reference';
  }
  return isWanVideoModel(normalizedKey)
    || REFERENCE_MENTION_MODEL_PREFIXES.some((prefix) => normalizedKey.startsWith(prefix));
};

const normalizeReferenceMediaType = (mediaType) => {
  if (mediaType === 'video' || mediaType === 'audio') {
    return mediaType;
  }
  return 'image';
};

const normalizeReferenceMentionKey = (type, index) => {
  const normalizedType = REFERENCE_MENTION_TYPE_ALIASES[String(type || '').toLowerCase()] || null;
  const normalizedIndex = Number.parseInt(index, 10);
  if (!normalizedType || !Number.isFinite(normalizedIndex) || normalizedIndex < 1) {
    return null;
  }
  return `${REFERENCE_MENTION_TYPE_LABELS[normalizedType]}${normalizedIndex}`.toLowerCase();
};

const getReferenceMentionKeys = (text) => {
  const keys = new Set();
  const rawText = String(text || '');
  let match;

  REFERENCE_MENTION_PATTERN.lastIndex = 0;
  while ((match = REFERENCE_MENTION_PATTERN.exec(rawText)) !== null) {
    const normalizedKey = normalizeReferenceMentionKey(match[1], match[2]);
    if (normalizedKey) {
      keys.add(normalizedKey);
    }
  }

  return keys;
};

const getInvalidReferenceMentions = (text, availableKeys) => {
  const invalid = [];
  const seen = new Set();
  const rawText = String(text || '');
  let match;

  REFERENCE_MENTION_PATTERN.lastIndex = 0;
  while ((match = REFERENCE_MENTION_PATTERN.exec(rawText)) !== null) {
    const normalizedKey = normalizeReferenceMentionKey(match[1], match[2]);
    if (!normalizedKey || availableKeys.has(normalizedKey) || seen.has(normalizedKey)) {
      continue;
    }
    seen.add(normalizedKey);
    invalid.push(`@${REFERENCE_MENTION_TYPE_LABELS[REFERENCE_MENTION_TYPE_ALIASES[String(match[1] || '').toLowerCase()]]}${Number.parseInt(match[2], 10)}`);
  }

  return invalid;
};

const getActiveReferenceMention = (text, caretIndex) => {
  const rawText = String(text || '');
  const safeCaretIndex = Number.isFinite(Number(caretIndex))
    ? Math.max(0, Math.min(rawText.length, Number(caretIndex)))
    : rawText.length;
  const beforeCaret = rawText.slice(0, safeCaretIndex);
  const atIndex = beforeCaret.lastIndexOf('@');

  if (atIndex < 0) {
    return null;
  }

  const query = beforeCaret.slice(atIndex + 1);
  if (!/^[\p{L}\p{N}_-]*$/u.test(query)) {
    return null;
  }

  return {
    start: atIndex,
    end: safeCaretIndex,
    query,
  };
};

const getTextareaCaretMenuPosition = (textarea, caretIndex, menuWidth = 360) => {
  if (!textarea || typeof window === 'undefined' || typeof document === 'undefined') {
    return { left: 0, top: 44 };
  }

  const style = window.getComputedStyle(textarea);
  const mirror = document.createElement('div');
  const marker = document.createElement('span');
  const lineHeight = Number.parseFloat(style.lineHeight)
    || Number.parseFloat(style.fontSize) * 1.4
    || 20;

  Object.assign(mirror.style, {
    position: 'absolute',
    visibility: 'hidden',
    pointerEvents: 'none',
    top: '0',
    left: '-9999px',
    width: `${textarea.clientWidth}px`,
    boxSizing: style.boxSizing,
    fontFamily: style.fontFamily,
    fontSize: style.fontSize,
    fontWeight: style.fontWeight,
    letterSpacing: style.letterSpacing,
    lineHeight: style.lineHeight,
    padding: style.padding,
    border: style.border,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    wordBreak: style.wordBreak,
  });

  mirror.textContent = textarea.value.slice(0, caretIndex);
  marker.textContent = '\u200b';
  mirror.appendChild(marker);
  document.body.appendChild(mirror);

  const markerRect = marker.getBoundingClientRect();
  const mirrorRect = mirror.getBoundingClientRect();
  const rawLeft = markerRect.left - mirrorRect.left - textarea.scrollLeft;
  const rawTop = markerRect.top - mirrorRect.top - textarea.scrollTop + lineHeight + 8;
  document.body.removeChild(mirror);

  const maxLeft = Math.max(0, textarea.clientWidth - menuWidth);
  return {
    left: Math.max(0, Math.min(rawLeft, maxLeft)),
    top: Math.max(lineHeight + 8, rawTop),
  };
};

export default function GenerationStudio({
  onGenerationComplete,
  onGenerationStart,
  onGenerationError,
  onInputStateChange,
  queuedGenerationModeFallback = null,
  defaultModel: rawDefaultModel,
  defaultPresetRequest = 0,
  defaultFunctionMode = 'general',
  defaultResolutionLabel = null,
  preferDefaultPreset = false,
  fixedCreationType = null,
  creationSessionId = null,
  creationEntryPoint = null,
  hideTitle = false,
  centerTitle = false,
  openDropdownUp = false,
  generatorInstanceId = null,
}) {
  const t = useTranslations('GenerationStudio');
  const tErrors = useTranslations('Errors');
  const messages = useMessages();
  const generationStudioMessages = useMemo(() => messages?.GenerationStudio || {}, [messages]);
  const hasGenerationStudioMessage = useCallback((key) => {
    let cursor = generationStudioMessages;

    for (const segment of key.split('.')) {
      if (!cursor || typeof cursor !== 'object' || !Object.prototype.hasOwnProperty.call(cursor, segment)) {
        return false;
      }
      cursor = cursor[segment];
    }

    return typeof cursor === 'string';
  }, [generationStudioMessages]);
  const getGenerationStudioText = useCallback((key, fallback = '') => {
    if (!key || !hasGenerationStudioMessage(key)) {
      return fallback;
    }

    try {
      const value = t(key);
      if (value && value !== key && value !== `GenerationStudio.${key}`) {
        return value;
      }
    } catch {
      // Fall through to the provided fallback.
    }

    return fallback;
  }, [hasGenerationStudioMessage, t]);
  const requestedDefaultModel = resolveModelKey(rawDefaultModel || '') || rawDefaultModel;
  const defaultModel = getModelConfig(requestedDefaultModel)?.retired ? 'botgo-video-2.5' : requestedDefaultModel;
  const defaultVideoModelKey = MODEL_DEFAULT_VIDEO_BY_FUNCTION_MODE.general || 'botgo-video-2.5-flash';
  const defaultImageModelKey = MODEL_DEFAULT_IMAGE_BY_FUNCTION_MODE.general || 'botgo-image-2.5-flash';
  const initialModelKey = defaultModel || defaultVideoModelKey;
  const initialModelConfig = getModelConfig(initialModelKey);
  const lockedCreationType = fixedCreationType === 'image' || fixedCreationType === 'video'
    ? fixedCreationType
    : null;
  const initialCreationType = lockedCreationType || initialModelConfig?.type || 'video';
  const initialAspectRatio = resolvePreferredAspectRatio({
    modelKey: initialModelKey,
    modelType: initialModelConfig?.type,
  }) || '9:16';
  const initialResolutionId = resolveDefaultResolutionOptionId({
    modelKey: initialModelKey,
    aspectRatio: initialAspectRatio,
    preferredResolutionLabel: defaultResolutionLabel,
  });
  const initialFunctionMode = normalizeFunctionModeForCreationType(defaultFunctionMode, initialCreationType);

  // 定义验证schema
  const formSchema = z.object({
    prompt: z.string()
      .min(1, t('errors.promptRequired'))
      .refine((value) => countPromptCharacters(value) <= PLATFORM_PROMPT_LIMIT, t('errors.maxChars')),
    style: z.string(),
    size: z.string(),
    color: z.string(),
    lighting: z.string(),
    composition: z.string(),
    negativePrompt: z.string().optional()
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      prompt: '',
      style: 'none',
      size: initialAspectRatio,
      color: 'none',
      lighting: 'none',
      composition: 'none',
      negativePrompt: ''
    }
  });

  const { control, handleSubmit, formState: { errors }, setValue, watch, trigger, clearErrors, getFieldState } = form;
  const router = useRouter();
  const pathname = usePathname();
  const supabase = getClient();

  const { runGeneration, isLoading, error, additionalInfo } = useGenerationRunner();
  const handleGenerationComplete = onGenerationComplete;

  // Public Visibility 单开关（PR-05）。初始 dirty=false：用户未改动时，
  // 生成请求不携带 communityVisibility；只有实际改动后才发送 boolean，
  // 防止旧标签页覆盖服务器最新值。API available=false 时完全隐藏。
  const {
    available: publicVisibilityAvailable,
    value: publicVisibilityValue,
    dirty: publicVisibilityDirty,
    setLocalValue: setPublicVisibilityLocalValue,
    resetLocalValue: resetPublicVisibilityLocalValue,
  } = usePublicVisibility();

  const consumePublicVisibilityOverride = useCallback(() => {
    const communityVisibility = resolveRequestCommunityVisibility({
      dirty: publicVisibilityDirty,
      value: publicVisibilityValue,
    });
    resetPublicVisibilityLocalValue();
    return communityVisibility;
  }, [
    publicVisibilityDirty,
    publicVisibilityValue,
    resetPublicVisibilityLocalValue,
  ]);

  const runGenerationWithVisibility = useCallback((generationParams, communityVisibility) => {
    if (communityVisibility === undefined) {
      return runGeneration(generationParams);
    }
    return runGeneration({ ...generationParams, communityVisibility });
  }, [runGeneration]);

  // ✅ 处理需要升级的选项点击事件
  const handleUpgradeRequired = () => {
    router.push('/pricing');
  };
  const [showAspectOptions, setShowAspectOptions] = useState(false);
  const [showStyleOptions, setShowStyleOptions] = useState(false);
  const [showColorOptions, setShowColorOptions] = useState(false);
  const [showLightingOptions, setShowLightingOptions] = useState(false);
  const [showCompositionOptions, setShowCompositionOptions] = useState(false);
  const [, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);
  const pendingUploadSlotIndexRef = useRef(null);
  const hydrateRecreatedAssetMetadataRef = useRef(async (asset) => asset);
  const recreateAssetHydrationRequestRef = useRef(0);
  const [hasPrompt, setHasPrompt] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [isTurnstileVerified, setIsTurnstileVerified] = useState(false);
  const [tokenTimestamp, setTokenTimestamp] = useState(0); // token获取时间戳
  const [tokenUsageCount, setTokenUsageCount] = useState(0); // token使用次数
  const [showTurnstileWidget, setShowTurnstileWidget] = useState(false); // 是否显示验证组件
  const [turnstileDisplay, setTurnstileDisplay] = useState('overlay');
  const [turnstileInstanceKey, setTurnstileInstanceKey] = useState(0);
  const [isPaidPlan, setIsPaidPlan] = useState(false);
  const [turnstileRequired, setTurnstileRequired] = useState(true);
  const [lastRequestTime, setLastRequestTime] = useState(0); // 上次请求时间
  const [requestsInLastMinute, setRequestsInLastMinute] = useState(0); // 最近一分钟的请求次数

  // 积分不足 / TopUp 相关状态
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalDetail, setUpgradeModalDetail] = useState('');
  const [upgradeModalVariant, setUpgradeModalVariant] = useState('default');
  const [selectedGenerationMode, setSelectedGenerationMode] = useState('credit');
  const [generationModeFallback, setGenerationModeFallback] = useState(null);

  // 验证相关常量
  const TOKEN_VALIDITY_MINUTES = 5; // token有效期（分钟）- 改为5分钟用于测试
  const MAX_REQUESTS_PER_MINUTE = 10; // 每分钟最大请求次数

  // 参考素材上传处理 - 支持图片 / 视频 / 音频
  const [uploadedAssets, setUploadedAssets] = useState([]);
  const [, setUploadError] = useState('');
  const [previewAsset, setPreviewAsset] = useState(null); // 预览素材
  const isAssetPreviewOpen = Boolean(previewAsset);
  const [outputCount, setOutputCount] = useState(1);
  const [generateAudio, setGenerateAudio] = useState(true);
  const [privacyUnrestricted, setPrivacyUnrestricted] = useState(false);
  const [contextIrEnabled, setContextIrEnabled] = useState(false);
  const [audioDescExpanded, setAudioDescExpanded] = useState(false);
  const [seed, setSeed] = useState(() => createRandomSeed(initialModelKey));
  const [seedEnabled, setSeedEnabled] = useState(() => isSeedDefaultEnabledVideoModel(initialModelKey));
  const [seedDescExpanded, setSeedDescExpanded] = useState(false);
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [webSearchDescExpanded, setWebSearchDescExpanded] = useState(false);

  useEffect(() => {
    if (!isAssetPreviewOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const handlePreviewKeyDown = (event) => {
      if (event.key === 'Escape') {
        setPreviewAsset(null);
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handlePreviewKeyDown);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener('keydown', handlePreviewKeyDown);
    };
  }, [isAssetPreviewOpen]);

  // 添加对Turnstile组件的引用
  const turnstileRef = useRef(null);

  // 添加对Textarea组件的引用，用于自动调整高度
  const textareaRef = useRef(null);
  const [referenceMentionState, setReferenceMentionState] = useState({
    open: false,
    query: '',
    start: 0,
    end: 0,
    source: null,
    anchor: null,
  });
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [assetPickerLoading, setAssetPickerLoading] = useState(false);
  const [assetPickerError, setAssetPickerError] = useState('');
  const [assetPickerItems, setAssetPickerItems] = useState([]);
  const [assetPickerActiveType, setAssetPickerActiveType] = useState('image');
  const [assetPickerSelectedKeys, setAssetPickerSelectedKeys] = useState([]);

  // 添加一个状态来防止重复验证和提交
  const [isProcessingVerification, setIsProcessingVerification] = useState(false);
  const verificationInProgressRef = useRef(false);
  const pendingRemixRef = useRef(null);

  // 在state部分添加已使用的token追踪
  const [, setUsedTokens] = useState(new Set());
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [userPlanCode, setUserPlanCode] = useState('free');
  const [planCapabilities, setPlanCapabilities] = useState(null);
  const [isBatchGenerating, setIsBatchGenerating] = useState(false);
  const { showToast } = useToast();
  const [inlineNotice, setInlineNotice] = useState(null);
  const [isPromptNoticeDismissed, setIsPromptNoticeDismissed] = useState(false);
  const [activeTaskKey, setActiveTaskKey] = useState(null);
  const isButtonBusy = isLoading || isProcessingVerification || isBatchGenerating;
  const lastQueuedTaskKeyRef = useRef(null);
  const lastToastAtRef = useRef(0);
  const lastGenerateAudioModelRef = useRef(null);
  const lastSeedModelRef = useRef(null);
  const lastWebSearchModelRef = useRef(null);
  const [sharedStateReady, setSharedStateReady] = useState(false);
  const sharedStateUpdatedAtRef = useRef(0);
  const sharedStateSuppressUntilRef = useRef(0);
  const generationModeRef = useRef('credit');
  const generationPreflightCacheRef = useRef(null);
  const generationModeConfigSignatureRef = useRef(null);
  const handledQueuedFallbackRef = useRef(null);
  const recreateGenerateAudioOverrideRef = useRef(null);
  const recreateSeedOverrideRef = useRef(null);
  const recreateWebSearchOverrideRef = useRef(null);
  const menuToggleActiveClassName = 'border-amber-600 bg-amber-600 shadow-[0_0_0_1px_rgba(245,158,11,0.18)]';

  const clearInlineNotice = useCallback(() => {
    setInlineNotice(null);
  }, []);

  const resetTurnstileVerificationState = useCallback(({ showWidget = false, display = null } = {}) => {
    setTurnstileToken('');
    setIsTurnstileVerified(false);
    setTokenTimestamp(0);
    setTokenUsageCount(0);
    if (display) {
      setTurnstileDisplay(display);
    }
    setShowTurnstileWidget(showWidget);
  }, []);

  const cleanupTurnstileWidget = useCallback((contextLabel, { logSuccess = false } = {}) => {
    if (!turnstileRef.current) return;

    setTimeout(() => {
      try {
        turnstileRef.current.remove();
        if (logSuccess) {
          console.log(`Successfully cleaned up Turnstile component after ${contextLabel}`);
        }
      } catch (err) {
        console.error(`Failed to clean up Turnstile component after ${contextLabel}:`, err);
      }
    }, 100);
  }, []);

  const remountTurnstileWidget = useCallback(() => {
    setTurnstileInstanceKey((prev) => prev + 1);
  }, []);

  const allModelEntries = useMemo(
    () => Object.entries(MODEL_REGISTRY).map(([key, config]) => ({ key, ...config })),
    []
  );

  const modelGroups = useMemo(() => ({
    video: allModelEntries.filter((model) => model.type === 'video'),
    image: allModelEntries.filter((model) => model.type === 'image'),
  }), [allModelEntries]);

  const findFreeMonthlyPlan = useCallback((plans = []) => (
    plans.find((plan) =>
      String(plan.name || '').trim().toLowerCase() === 'free' &&
      String(plan.billingCycle || '').trim().toLowerCase() === 'monthly'
    ) || null
  ), []);

  const applyPlanCapabilities = useCallback((plan, { planKey } = {}) => {
    if (!plan) {
      console.error('[GenerationStudio][HIGH_PRIORITY] Missing pricing plan data for capability resolution', {
        planId: null,
        planName: null,
        billingCycle: null,
        planKey: planKey || null,
      });
      setPlanCapabilities(null);
      return null;
    }

    try {
      const resolvedCapabilities = resolvePlanCapabilities({ plan, planKey });
      setPlanCapabilities(resolvedCapabilities);
      return resolvedCapabilities;
    } catch (error) {
      console.error('[GenerationStudio][HIGH_PRIORITY] Failed to resolve plan capabilities from pricing_plans.metadata', {
        message: error?.message || String(error),
        code: error?.code || null,
        details: error?.details || null,
      });
      setPlanCapabilities(null);
      return null;
    }
  }, []);

  const loadFreePlanCapabilities = useCallback(async () => {
    const freePlanResp = await fetchCachedApi('/api/pricing-plans', {
      cacheKey: CLIENT_CACHE_KEYS.pricingPlans,
      ttlMs: 60000,
    });

    if (!freePlanResp.ok) {
      setPlanCapabilities(null);
      throw new Error(freePlanResp.error || 'Failed to fetch pricing plans.');
    }

    const freePlan = findFreeMonthlyPlan(freePlanResp.data?.plans || []);
    if (!freePlan) {
      console.error('[GenerationStudio][HIGH_PRIORITY] Active Free monthly pricing plan is missing');
      setPlanCapabilities(null);
      return null;
    }

    applyPlanCapabilities(freePlan, { planKey: 'free' });
    return freePlan;
  }, [applyPlanCapabilities, findFreeMonthlyPlan]);

  const formatAllowedTypesLabel = useCallback((rules) => {
    const labels = (rules?.allowedExts || [])
      .map((ext) => ext.replace('.', '').toUpperCase())
      .filter(Boolean);
    return labels.length ? labels.join(', ') : 'JPG, PNG, WEBP';
  }, []);

  const getAssetTypeLabel = useCallback((mediaType) => {
    if (!mediaType) return t('mediaTypeSingular.image');
    return t(`mediaTypeSingular.${mediaType}`);
  }, [t]);

  const formatCreditsValue = useCallback((value) => {
    const numericValue = Number(value || 0);
    if (!Number.isFinite(numericValue)) return '0';
    const roundedValue = Math.round(numericValue * 10) / 10;
    return Number.isInteger(roundedValue)
      ? String(roundedValue)
      : String(roundedValue);
  }, []);

  const updateGenerationMode = useCallback((nextMode) => {
    const normalizedMode = nextMode === 'unlimited' ? 'unlimited' : 'credit';
    generationModeRef.current = normalizedMode;
    setSelectedGenerationMode(normalizedMode);
  }, []);

  const getAssetPreviewTarget = (asset) => {
    if (!asset?.url && !asset?.preview) return null;
    return {
      type: asset?.type || 'image',
      url: asset?.preview || asset?.url,
      name: asset?.name || '',
    };
  };

  const isUpgradeRequiredError = useCallback((error) => {
    const errorCode = error?.code || error?.cause?.code;
    if (errorCode === 'UPGRADE_REQUIRED' || FREE_SUBSIDY_UPGRADE_VARIANTS[errorCode]) return true;
    const normalized = String(error?.message || error || '').toLowerCase();
    if (!normalized) return false;
    if (normalized.includes('queued tasks') || normalized.includes('queue')) return false;
    return (
      normalized.includes('upgrade required') ||
      normalized.includes('plan supports') ||
      normalized.includes('plan does not support') ||
      normalized.includes('please upgrade') ||
      normalized.includes('not available on your plan')
    );
  }, []);

  const resolveUpgradeModalVariant = useCallback((error) => {
    const errorCode = error?.code || error?.cause?.code;
    return FREE_SUBSIDY_UPGRADE_VARIANTS[errorCode] || 'default';
  }, []);

  const isTurnstileReverifyError = useCallback((value) => {
    const normalized = getErrorMessageText(value).toLowerCase();
    if (!normalized) return false;
    return (
      normalized.includes('verification failed') ||
      normalized.includes('please complete verification') ||
      normalized.includes('turnstile') ||
      normalized.includes('timeout-or-duplicate')
    );
  }, []);

  const localizeKnownErrorMessage = useCallback((errorLike) => {
    if (!errorLike) return errorLike;
    const rawMessage = getErrorMessageText(errorLike);
    const localizedMessage = localizeErrorMessage(errorLike, tErrors);
    if (!localizedMessage) return rawMessage;
    const key = getErrorKey(errorLike);
    if (key === 'unknown') return rawMessage;
    return localizedMessage;
  }, [tErrors]);

  const formatUpgradeDescription = useCallback((text) => {
    const rawText = String(text || '').trim();
    if (!rawText) return { line1: '', line2: '' };

    const upgradeSentence = 'Please upgrade your plan.';
    if (rawText.endsWith(upgradeSentence)) {
      const line1 = rawText.slice(0, -upgradeSentence.length).trim();
      return { line1, line2: upgradeSentence };
    }

    return { line1: rawText, line2: '' };
  }, []);


  const watchedSize = watch('size');
  const watchedPrompt = watch('prompt');
  const upgradeDescriptionText = (() => {
    if (upgradeModalVariant === 'freeSubsidyUser') {
      return t('upgradeModal.freeSubsidyUserDescription');
    }
    if (upgradeModalVariant === 'freeSubsidyGroup') {
      return t('upgradeModal.freeSubsidyGroupDescription');
    }
    return upgradeModalDetail || t('upgradeModal.description');
  })();
  const upgradeModalConfirmText =
    upgradeModalVariant === 'freeSubsidyUser' || upgradeModalVariant === 'freeSubsidyGroup'
      ? t('upgradeModal.confirmUpgradeToContinue')
      : t('upgradeModal.confirm');
  const { line1: upgradeDescriptionLine1, line2: upgradeDescriptionLine2 } =
    formatUpgradeDescription(upgradeDescriptionText);

  const handleUpgradeModalOpenChange = useCallback((open) => {
    setShowUpgradeModal(open);
    if (!open) {
      setUpgradeModalVariant('default');
      setUpgradeModalDetail('');
    }
  }, []);

  // New State for 6-Level Menu
  const [creationType, setCreationType] = useState(() => {
    const defaultConfig = getModelConfig(defaultModel);
    return lockedCreationType || defaultConfig?.type || initialCreationType;
  }); // 'video' | 'image'
  const [functionMode, setFunctionMode] = useState(initialFunctionMode); // video: general/frames/reference/editing/extend; image: general/editing

  // Menu Mutex State
  const [activeMenu, setActiveMenu] = useState(null); // 'creationType' | 'functionMode' | 'model' | 'specs' | 'duration' | 'assetSource' | 'more' | null
  const moreMenuTriggerRef = useRef(null);
  const modelMenuRef = useRef(null);
  const [activeModelGroupKey, setActiveModelGroupKey] = useState(null);
  const creationTypeTriggerRef = useRef(null);
  const functionModeTriggerRef = useRef(null);
  const modelTriggerRef = useRef(null);
  const specsTriggerRef = useRef(null);
  const durationTriggerRef = useRef(null);
  const fpsTriggerRef = useRef(null);
  const assetSourceTriggerRef = useRef(null);

  // Toggle Menu Function
  const toggleMenu = (menuName) => {
    setActiveMenu((prev) => (prev === menuName ? null : menuName));
  };

  const resolvedDefaultModel = initialModelKey;

  // Existing States mapping
  const [selectedResolution, setSelectedResolution] = useState(initialResolutionId || '720x1280');
  // 用户主动点选的分辨率标签（如 '4K'），独立于当前比例，用于约束比例列表显示
  const [intendedResolutionLabel, setIntendedResolutionLabel] = useState(() => (
    normalizePresetResolutionLabel(defaultResolutionLabel)
  ));
  // 图片质量（仅对支持 quality 选择的模型生效，如 gpt-image-2-vip）
  const [selectedQuality, setSelectedQuality] = useState('low');
  const [selectedBackground, setSelectedBackground] = useState('auto');
  // 内容审核级别（仅对支持 moderation 选择的模型生效，如 gpt-image-2-vip）
  const [selectedModeration, setSelectedModeration] = useState('auto');
  const [selectedGrokMode, setSelectedGrokMode] = useState('normal');
  const [selectedModel, setSelectedModel] = useState(resolvedDefaultModel);
  useEffect(() => {
    setPrivacyUnrestricted(false);
  }, [selectedModel]);
  const [selectedDuration, setSelectedDuration] = useState(MODEL_DEFAULT_DURATION[resolvedDefaultModel] || '10s');
  const [selectedFps, setSelectedFps] = useState(30);
  const lastModelRef = useRef(selectedModel);
  const lastAspectModelRef = useRef(null);
  const lastResolutionModelRef = useRef(null);
  const vsrDefaultSourceRef = useRef(null);
  const lastDefaultPresetRef = useRef(null);
  const recreateAspectOverrideRef = useRef(null);
  const recreateResolutionOverrideRef = useRef(null);
  // Recreate 会先切模型、再回填时长；这里临时记住目标时长，避免被模型默认值立刻覆盖。
  const recreateDurationOverrideRef = useRef(null);
  // Recreate 时保护 moderation / imageQuality，防止模型切换 useEffect 覆盖。
  const recreateModerationOverrideRef = useRef(null);
  const recreateQualityOverrideRef = useRef(null);

  // Reset function mode when creation type changes
  useEffect(() => {
    if (lockedCreationType && creationType !== lockedCreationType) {
      setCreationType(lockedCreationType);
      return;
    }
    const normalizedMode = normalizeFunctionModeForCreationType(functionMode, creationType);
    if (normalizedMode !== functionMode) {
      setFunctionMode(normalizedMode);
    }

    if (creationType === 'image') {
      // Default to an image model if current is video
      const currentConfig = getModelConfig(selectedModel);
      if (currentConfig?.type === 'video') {
        setSelectedModel(MODEL_DEFAULT_IMAGE_BY_FUNCTION_MODE[normalizedMode] || defaultImageModelKey);
      }
    } else {
      // Default to a video model if current is image
      const currentConfig = getModelConfig(selectedModel);
      if (currentConfig?.type === 'image') {
        setSelectedModel(MODEL_DEFAULT_VIDEO_BY_FUNCTION_MODE[normalizedMode] || defaultVideoModelKey);
      }
    }
  }, [creationType, defaultImageModelKey, defaultVideoModelKey, functionMode, lockedCreationType, selectedModel]);

  useEffect(() => {
    if (!defaultModel) {
      lastDefaultPresetRef.current = null;
      return;
    }

    const nextConfig = getModelConfig(defaultModel);
    if (!nextConfig) return;

    const nextFunctionMode = normalizeFunctionModeForCreationType(defaultFunctionMode, nextConfig.type);
    const nextResolutionLabel = normalizePresetResolutionLabel(defaultResolutionLabel);
    const nextPresetKey = [
      defaultModel,
      defaultPresetRequest,
      nextFunctionMode,
      nextResolutionLabel || '',
    ].join('::');

    if (lastDefaultPresetRef.current === nextPresetKey) {
      return;
    }

    lastDefaultPresetRef.current = nextPresetKey;
    recreateResolutionOverrideRef.current = nextResolutionLabel
      ? {
        modelKey: defaultModel,
        resolutionLabel: nextResolutionLabel,
      }
      : null;

    setCreationType(nextConfig.type || 'video');
    setFunctionMode(nextFunctionMode);
    setSelectedModel(defaultModel);
  }, [defaultFunctionMode, defaultModel, defaultPresetRequest, defaultResolutionLabel]);

  // Derived Models List based on Creation Type
  const availableModels = useMemo(() => {
    let models = allModelEntries.filter((model) => {
      if (model.type !== creationType || model.retired) return false;
      if (DISABLED_GENERATOR_MODEL_KEYS.has(model.key)) return false;
      if (HIDDEN_GENERATOR_MODEL_KEYS.has(model.key) && model.key !== defaultModel) return false;
      if (!model.hiddenInGenerator) return true;
      // hiddenInGenerator 只屏蔽常规菜单；当落地页显式指定默认模型时，仍要允许展示该模型。
      return Boolean(defaultModel) && model.key === defaultModel;
    });

    if (creationType === 'video') {
      models = models.filter((model) => supportsVideoFunctionMode({
        modelKey: model.key,
        functionMode,
      }));

      if (functionMode === 'extend' && !ENABLE_VEO31_GENERATOR_EXTEND) {
        models = models.filter((model) => !isVeo31GeneratorExtendModelKey(model.key));
      }
    } else if (creationType === 'image') {
      models = models.filter((model) => supportsImageFunctionMode({
        modelKey: model.key,
        functionMode,
      }));
    }

    return sortModelsByDisplayOrder(models, creationType, functionMode);
  }, [allModelEntries, creationType, functionMode, defaultModel]);

  const modelMenuBadgeKeys = useMemo(
    () => getModelMenuBadgeKeys({ planKey: userPlanCode, capabilities: planCapabilities }),
    [userPlanCode, planCapabilities]
  );
  const modelMenuGroups = useMemo(
    () => buildModelMenuGroups(availableModels, creationType, modelMenuBadgeKeys),
    [availableModels, creationType, modelMenuBadgeKeys]
  );
  const selectedModelGroupKey = useMemo(() => (
    modelMenuGroups.find((group) => group.models.some((model) => model.key === selectedModel))?.key
      || modelMenuGroups[0]?.key
      || null
  ), [modelMenuGroups, selectedModel]);
  const activeModelGroup = useMemo(() => {
    const targetGroupKey = activeModelGroupKey || selectedModelGroupKey;
    return modelMenuGroups.find((group) => group.key === targetGroupKey)
      || modelMenuGroups[0]
      || null;
  }, [activeModelGroupKey, modelMenuGroups, selectedModelGroupKey]);
  useEffect(() => {
    if (activeMenu !== 'model') return;
    setActiveModelGroupKey(selectedModelGroupKey);
  }, [activeMenu, selectedModelGroupKey]);

  const getModelMenuDescription = useCallback((model) => {
    const descriptionKey = MODEL_DESCRIPTION_KEY_MAP[model.key];
    if (!descriptionKey) return model.generatorDescription || '';

    const keys = functionMode === 'extend'
      ? [`${descriptionKey}Extend`, `${descriptionKey}Edit`, descriptionKey]
      : functionMode === 'editing'
        ? [`${descriptionKey}Edit`, descriptionKey]
        : [descriptionKey];

    for (const key of keys) {
      if (!hasGenerationStudioMessage(key)) {
        continue;
      }

      try {
        const description = t(key);
        if (description && description !== key && description !== `GenerationStudio.${key}`) {
          return description;
        }
      } catch {
        // Fall through to the registry fallback.
      }
    }

    return model.generatorDescription || '';
  }, [functionMode, hasGenerationStudioMessage, t]);

  const getModelMenuGroupDescription = useCallback((group) => (
    getGenerationStudioText(group?.descriptionKey, '')
  ), [getGenerationStudioText]);

  const getModelMenuSpecs = useCallback((model) => {
    const modelType = model?.type || getModelConfig(model?.key)?.type;
    const resolutionLabel = formatMenuRangeLabel(getMenuResolutionValues(model?.key));
    const durationLabel = modelType === 'video' ? formatMenuDurationLabel(model?.key) : '';
    const generationTimeKey = modelType === 'image'
      ? 'modelMenu.generationTimes.image'
      : MODEL_MENU_GENERATION_TIME_KEY_MAP[model?.key] || 'modelMenu.generationTimes.standardVideo';
    const minimumCredits = resolveModelMenuMinimumCredits({ model, functionMode });

    return {
      resolution: resolutionLabel || getGenerationStudioText('modelMenu.specs.notAvailable', 'N/A'),
      duration: durationLabel,
      generationTime: getGenerationStudioText(generationTimeKey, modelType === 'image' ? '~10 sec' : '~1-2 min'),
      credits: Number.isFinite(minimumCredits)
        ? t('modelMenu.specs.creditsFrom', { credits: formatCreditsValue(minimumCredits) })
        : getGenerationStudioText('modelMenu.specs.creditsTbd', 'TBD'),
    };
  }, [formatCreditsValue, functionMode, getGenerationStudioText, t]);
  const handleModelMenuWheel = useCallback((event) => {
    const element = event.currentTarget;
    const deltaY = event.deltaY;
    const canScroll = element.scrollHeight > element.clientHeight + 1;
    const isAtTop = element.scrollTop <= 0;
    const isAtBottom = Math.ceil(element.scrollTop + element.clientHeight) >= element.scrollHeight;

    if (!canScroll || (deltaY < 0 && isAtTop) || (deltaY > 0 && isAtBottom)) {
      event.preventDefault();
    }

    event.stopPropagation();
  }, []);

  useEffect(() => {
    if (activeMenu !== 'model') return;

    const frameId = window.requestAnimationFrame(() => {
      const menuNode = modelMenuRef.current;
      if (!menuNode) return;

      if (creationType === 'image') {
        menuNode.scrollTop = 0;
        return;
      }

      const selectedItem = menuNode.querySelector(`[data-model-key="${selectedModel}"]`);
      if (selectedItem instanceof HTMLElement) {
        // Scroll the dropdown container to center the selected item
        // without affecting the window scroll position
        const itemTop = selectedItem.offsetTop;
        const itemHeight = selectedItem.offsetHeight;
        const containerHeight = menuNode.clientHeight;
        
        menuNode.scrollTop = itemTop - (containerHeight / 2) + (itemHeight / 2);
      }
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [activeMenu, selectedModel, availableModels, activeModelGroup?.key, creationType]);

  useEffect(() => {
    if (activeMenu && !openDropdownUp) {
      const triggerMap = {
        creationType: creationTypeTriggerRef.current,
        functionMode: functionModeTriggerRef.current,
        model: modelTriggerRef.current,
        specs: specsTriggerRef.current,
        duration: durationTriggerRef.current,
        fps: fpsTriggerRef.current,
        assetSource: assetSourceTriggerRef.current,
        more: moreMenuTriggerRef.current,
      };

      const triggerNode = triggerMap[activeMenu];
      if (triggerNode) {
        const frameId = window.requestAnimationFrame(() => {
          scrollIntoViewIfNeeded(triggerNode);
        });
        return () => window.cancelAnimationFrame(frameId);
      }
    }
  }, [activeMenu, openDropdownUp]);

  const handleFunctionModeSelect = useCallback((nextMode) => {
    const normalizedMode = normalizeFunctionModeForCreationType(nextMode, creationType);
    setFunctionMode(normalizedMode);

    if (creationType === 'image') {
      if (supportsImageFunctionMode({ modelKey: selectedModel, functionMode: normalizedMode })) {
        return;
      }
      const preferredImageModelKey = MODEL_DEFAULT_IMAGE_BY_FUNCTION_MODE[normalizedMode];
      if (preferredImageModelKey && selectedModel !== preferredImageModelKey) {
        setSelectedModel(preferredImageModelKey);
      }
      return;
    }

    if (creationType !== 'video') return;
    const preferredModelKey = MODEL_DEFAULT_VIDEO_BY_FUNCTION_MODE[normalizedMode];
    if (normalizedMode !== 'enhance' && supportsVideoFunctionMode({ modelKey: selectedModel, functionMode: normalizedMode })) return;
    if (normalizedMode === 'enhance' && preferredModelKey && selectedModel !== preferredModelKey) {
      setSelectedModel(preferredModelKey);
      return;
    }
    if (!preferredModelKey) return;
    if (selectedModel !== preferredModelKey) {
      setSelectedModel(preferredModelKey);
    }
  }, [creationType, selectedModel]);

  // Auto-switch model if current selection is invalid for the mode (e.g. Sora selected in Frames mode)
  useEffect(() => {
    if (availableModels.length > 0 && !availableModels.find(m => m.key === selectedModel)) {
      const preferredModelKey = creationType === 'image'
        ? resolvePreferredImageModelForFunctionMode({
          creationType,
          functionMode,
          availableModels,
        })
        : resolvePreferredVideoModelForFunctionMode({
          creationType,
          functionMode,
          availableModels,
        });
      setSelectedModel(preferredModelKey || availableModels[0].key);
    }
  }, [availableModels, selectedModel, creationType, functionMode]);


  const selectedModelConfig = useMemo(
    () => getModelConfig(selectedModel) || allModelEntries[0],
    [selectedModel, allModelEntries]
  );
  const selectedModelCaps = useMemo(
    () => getModelCapabilities(selectedModel) || {},
    [selectedModel]
  );

  const currentInputRules = useMemo(
    () => getVideoInputRules(selectedModel, { creationType, functionMode }),
    [selectedModel, creationType, functionMode]
  );
  const inputVideoPricingTooltipKey = (
    creationType === 'video'
    && (
      isSeedance20InputVideoBillingModel(selectedModel)
      || selectedModel?.startsWith('minimax-h3')
      || isWanVideoModel(selectedModel)
      || selectedModel === 'kling-v4'
      || selectedModel.startsWith('gemini-omni')
    )
    && !currentInputRules?.unsupported
    && currentInputRules?.allowedMediaTypes?.includes('video')
  )
    ? selectedModel === 'kling-v4'
      ? 'generationMode.klingV4InputVideoPricingTooltip'
      : selectedModel.startsWith('gemini-omni')
      ? 'generationMode.omniInputVideoPricingTooltip'
      : isWanVideoModel(selectedModel)
      ? 'generationMode.wanInputVideoPricingTooltip'
      : selectedModel?.startsWith('minimax-h3')
      ? 'generationMode.minimaxInputVideoPricingTooltip'
      : 'generationMode.seedanceInputVideoPricingTooltip'
    : null;
  const hasVeoExtendDraftSource =
    creationType === 'video'
    && functionMode === 'extend'
    && VEO_EXTEND_SELECTABLE_MODEL_KEYS.has(selectedModel)
    && uploadedAssets.some((asset) =>
      asset?.type === 'video'
      && asset?.sourceTaskId
      && isCompatibleVeoExtendSourceModelKey(asset.sourceModelKey)
    );
  const veoExtendSourceProviderRoute = hasVeoExtendDraftSource
    ? resolveVeoExtendSourceProviderRoute(uploadedAssets.find((asset) =>
      asset?.type === 'video'
      && asset?.sourceTaskId
      && isCompatibleVeoExtendSourceModelKey(asset.sourceModelKey)
    ))
    : null;
  const isKieAiVeoExtendDraftSource = veoExtendSourceProviderRoute === 'kieai';
  const canUseFileInput = Boolean(
    currentInputRules?.allowedMediaTypes?.length &&
    !currentInputRules.unsupported
  );
  const hasVideoReferenceInput = uploadedAssets.some((asset) => asset?.type === 'video');
  const inputVideoDurationSeconds = useMemo(
    () => (selectedModel === 'kling-v4' ? getKlingV4InputDuration : selectedModel === 'botgo-video-2.5' ? getBotgoVideo25InputDuration : getInputVideoDurationSeconds)(
      uploadedAssets.filter((asset) => asset?.status === 'complete')
    ),
    [selectedModel, uploadedAssets]
  );
  const inputImageCount = useMemo(
    () => uploadedAssets.filter((asset) => asset?.status === 'complete' && asset.url && asset.type === 'image').length,
    [uploadedAssets]
  );
  const hasKlingOmniVideoInput =
    creationType === 'video'
    && selectedModel === 'kling-v3-omni'
    && hasVideoReferenceInput;
  const hasKlingOmniVideoReference =
    hasKlingOmniVideoInput
    && functionMode === 'reference';
  const hasKlingOmniVideoExtend =
    creationType === 'video'
    && selectedModel === 'kling-v3-omni'
    && functionMode === 'extend';
  const isKlingOmniKeepAudioMode =
    creationType === 'video'
    && selectedModel === 'kling-v3-omni'
    && ['editing', 'extend'].includes(functionMode);
  const isKlingOmniFeatureVideoMode =
    creationType === 'video'
    && selectedModel === 'kling-v3-omni'
    && (
      functionMode === 'extend'
      || (functionMode === 'reference' && hasVideoReferenceInput)
    );
  const hasKlingOmniVideoReferencePricing =
    selectedModel === 'kling-v3-omni'
    && (
      functionMode === 'editing'
      || hasKlingOmniVideoReference
      || hasKlingOmniVideoExtend
    );
  const isAutoVideoEditDurationModeActive = isAutoVideoEditDurationMode({
    modelKey: selectedModel,
    functionMode,
  });
  const sourceDrivenVideoFixedSpecModels =
    SOURCE_DRIVEN_VIDEO_FIXED_SPEC_MODELS_BY_MODE[functionMode] || EMPTY_MODEL_SET;
  const sourceDrivenVideoResolutionSpecModels =
    SOURCE_DRIVEN_VIDEO_RESOLUTION_SPEC_MODELS_BY_MODE[functionMode] || EMPTY_MODEL_SET;
  const isSourceDrivenVideoSpecs =
    creationType === 'video'
    && (
      sourceDrivenVideoFixedSpecModels.has(selectedModel)
      || sourceDrivenVideoResolutionSpecModels.has(selectedModel)
    );
  const isSourceDrivenVideoFixedSpecs =
    (
      creationType === 'video'
      && sourceDrivenVideoFixedSpecModels.has(selectedModel)
    )
    || isKieAiVeoExtendDraftSource;
  const canToggleGenerateAudio =
    creationType === 'video'
    && SEEDANCE_20_AUDIO_VIDEO_MODELS.has(selectedModel)
    && (!isKlingOmniFeatureVideoMode || isKlingOmniKeepAudioMode);
  const audioOptionMessageKey = isKlingOmniKeepAudioMode ? 'keepAudio' : 'generateAudio';
  const audioOptionStateKey = `${selectedModel}:${audioOptionMessageKey}`;
  const canUseSeed =
    creationType === 'video' && (
      SEEDANCE_SEED_MODELS.has(selectedModel) ||
      isWanVideoModel(selectedModel) ||
      HAPPYHORSE_VIDEO_MODELS.has(selectedModel) ||
      BOTGO_VIDEO_SEED_MODELS.has(selectedModel) ||
      KIEAI_VEO31_EXTEND_MODEL_KEYS.has(selectedModel)
    );
  const isHappyhorseModel = HAPPYHORSE_VIDEO_MODELS.has(selectedModel);
  const isAlwaysOnSeedModel =
    isHappyhorseModel || isWanVideoModel(selectedModel) ||
    BOTGO_VIDEO_SEED_MODELS.has(selectedModel) ||
    KIEAI_VEO31_EXTEND_MODEL_KEYS.has(selectedModel);
  const canSelectQuality = Boolean(selectedModelCaps.allowedQualities?.length);
  const canSelectModeration = Boolean(selectedModelCaps.allowedModerations?.length);
  const canShowWebSearchOption =
    creationType === 'video'
    && functionMode === 'general'
    && Boolean(selectedModelCaps.supportsWebSearch);
  const canSelectGrokMode = creationType === 'video' && selectedModel === 'grok-imagine';
  const hasImageInputAssets = uploadedAssets.some((asset) => (asset?.type || 'image') === 'image');
  const isSeedance25GeneralFirstFrame =
    selectedModel === 'seedance-2.5'
    && functionMode === 'general'
    && hasImageInputAssets;
  const effectiveGrokMode =
    canSelectGrokMode
      ? (hasImageInputAssets && selectedGrokMode === 'spicy' ? 'normal' : selectedGrokMode)
      : undefined;
  const allowedMediaTypes = useMemo(
    () => getAllowedMediaTypesForMode({ modelName: selectedModel, creationType, functionMode }),
    [selectedModel, creationType, functionMode]
  );
  const maxUploadsByType = useMemo(
    () => getMaxUploadsByTypeForMode({ modelName: selectedModel, creationType, functionMode }),
    [selectedModel, creationType, functionMode]
  );
  const maxInputUploads = useMemo(() => {
    if (!canUseFileInput) return 0;
    const baseMaxUploads = getMaxMediaUploadsForMode({
      modelName: selectedModel,
      creationType,
      functionMode,
    });
    if (
      creationType === 'video'
      && selectedModel === 'kling-v3-omni'
      && functionMode === 'reference'
      && hasVideoReferenceInput
    ) {
      return Math.min(baseMaxUploads, 5);
    }
    return baseMaxUploads;
  }, [canUseFileInput, creationType, functionMode, hasVideoReferenceInput, selectedModel]);
  const isUploadDisabled = !canUseFileInput || uploadedAssets.length >= maxInputUploads;
  const canOpenAssetSourceMenu = canUseFileInput && maxInputUploads > 0;
  const requiresDraftSourceTaskId = Boolean(currentInputRules?.requireDraftSourceTaskId);
  const primarySourceVideoAsset = useMemo(() => (
    uploadedAssets.find((asset) => asset?.type === 'video' && asset?.status !== 'error') || null
  ), [uploadedAssets]);
  const hasPrimarySourceVideoAsset = Boolean(primarySourceVideoAsset);
  const sourceVideoDimensions = useMemo(() => {
    if (!primarySourceVideoAsset) return null;

    const directWidth = Number(primarySourceVideoAsset.width);
    const directHeight = Number(primarySourceVideoAsset.height);
    if (Number.isFinite(directWidth) && directWidth > 0 && Number.isFinite(directHeight) && directHeight > 0) {
      return { width: Math.round(directWidth), height: Math.round(directHeight) };
    }

    const dimensionCandidates = [
      primarySourceVideoAsset.sourceResolution,
      primarySourceVideoAsset.resolution,
      primarySourceVideoAsset.size,
      primarySourceVideoAsset.metadata?.resolution,
      primarySourceVideoAsset.metadata?.size,
      primarySourceVideoAsset.metadata?.requestData?.resolution,
      primarySourceVideoAsset.metadata?.requestData?.size,
      primarySourceVideoAsset.metadata?.params?.resolution,
      primarySourceVideoAsset.metadata?.params?.size,
    ];

    for (const candidate of dimensionCandidates) {
      const dimensions = parseDimensionsFromSize(candidate);
      if (dimensions) return dimensions;
    }

    return null;
  }, [primarySourceVideoAsset]);
  const sourceVideoAspectRatioLabel = useMemo(() => {
    if (!primarySourceVideoAsset) return null;
    const explicitAspect = [
      primarySourceVideoAsset.sourceAspectRatio,
      primarySourceVideoAsset.aspectRatio,
      primarySourceVideoAsset.aspect,
      primarySourceVideoAsset.metadata?.aspectRatio,
      primarySourceVideoAsset.metadata?.aspect_ratio,
      primarySourceVideoAsset.metadata?.requestData?.aspectRatioLabel,
      primarySourceVideoAsset.metadata?.requestData?.aspectRatio,
      primarySourceVideoAsset.metadata?.params?.aspectRatio,
      primarySourceVideoAsset.metadata?.params?.aspect,
    ].map(normalizeAspectRatioLabel).find(Boolean);
    if (explicitAspect && explicitAspect !== 'auto' && explicitAspect !== 'adaptive') {
      return explicitAspect;
    }
    return sourceVideoDimensions
      ? getAspectRatioFromDimensions(sourceVideoDimensions.width, sourceVideoDimensions.height)
      : null;
  }, [primarySourceVideoAsset, sourceVideoDimensions]);
  const sourceVideoResolutionLabel = useMemo(() => {
    if (!primarySourceVideoAsset) return null;
    const explicitResolution = [
      primarySourceVideoAsset.sourceResolution,
      primarySourceVideoAsset.resolution,
      primarySourceVideoAsset.metadata?.resolution,
      primarySourceVideoAsset.metadata?.requestData?.resolutionLabel,
      primarySourceVideoAsset.metadata?.requestData?.resolution,
      primarySourceVideoAsset.metadata?.params?.resolutionLabel,
      primarySourceVideoAsset.metadata?.params?.resolution,
      primarySourceVideoAsset.size,
      primarySourceVideoAsset.metadata?.size,
      primarySourceVideoAsset.metadata?.requestData?.size,
      primarySourceVideoAsset.metadata?.params?.size,
    ].map(getResolutionLabelFromValue).find(Boolean);
    if (explicitResolution) return explicitResolution;
    return sourceVideoDimensions
      ? getResolutionLabelFromDimensions(sourceVideoDimensions.width, sourceVideoDimensions.height)
      : null;
  }, [primarySourceVideoAsset, sourceVideoDimensions]);
  const sourceVideoDurationSeconds = useMemo(() => {
    if (!primarySourceVideoAsset) return null;
    const duration = [
      primarySourceVideoAsset.durationSeconds,
      primarySourceVideoAsset.duration,
      primarySourceVideoAsset.videoDuration,
      primarySourceVideoAsset.metadata?.durationSeconds,
      primarySourceVideoAsset.metadata?.duration,
      primarySourceVideoAsset.metadata?.videoDuration,
      primarySourceVideoAsset.metadata?.requestData?.durationSeconds,
      primarySourceVideoAsset.metadata?.requestData?.duration,
      primarySourceVideoAsset.metadata?.params?.durationSeconds,
      primarySourceVideoAsset.metadata?.params?.duration,
    ].map(parseDurationSeconds).find(Boolean);
    return duration ? Math.round(duration * 1000) / 1000 : null;
  }, [primarySourceVideoAsset]);
  const isVsrMode = creationType === 'video' && functionMode === 'enhance';
  const isVsrResolutionDisabled = useCallback((resolutionLabel) => {
    if (!isVsrMode || !sourceVideoDimensions) return false;
    try {
      resolveStandaloneEnhanceResolution({
        sourceWidth: sourceVideoDimensions.width,
        sourceHeight: sourceVideoDimensions.height,
        resolution: resolutionLabel,
        engine: selectedModel === 'botgo-vsr-fast' ? 'fast' : 'generative',
      });
      return false;
    } catch {
      return true;
    }
  }, [isVsrMode, selectedModel, sourceVideoDimensions]);
  const sourceDrivenHasConcreteSourceSpecs =
    isSourceDrivenVideoSpecs
    && hasPrimarySourceVideoAsset
    && Boolean(sourceVideoAspectRatioLabel);

  useEffect(() => {
    if (!canToggleGenerateAudio) {
      if (generateAudio) {
        setGenerateAudio(false);
      }
      lastGenerateAudioModelRef.current = null;
      recreateGenerateAudioOverrideRef.current = null;
      return;
    }

    if (lastGenerateAudioModelRef.current !== audioOptionStateKey) {
      const recreateOverride = recreateGenerateAudioOverrideRef.current;
      const nextGenerateAudio =
        recreateOverride && recreateOverride.modelKey === selectedModel
          ? Boolean(recreateOverride.value)
          : true;
      setGenerateAudio(nextGenerateAudio);
      recreateGenerateAudioOverrideRef.current = null;
      lastGenerateAudioModelRef.current = audioOptionStateKey;
    }
  }, [audioOptionStateKey, canToggleGenerateAudio, generateAudio, selectedModel]);

  useEffect(() => {
    if (!canSelectGrokMode && selectedGrokMode !== 'normal') {
      setSelectedGrokMode('normal');
    }
    if (canSelectGrokMode && hasImageInputAssets && selectedGrokMode === 'spicy') {
      setSelectedGrokMode('normal');
    }
  }, [canSelectGrokMode, hasImageInputAssets, selectedGrokMode]);

  useEffect(() => {
    if (!canUseSeed) {
      lastSeedModelRef.current = null;
      recreateSeedOverrideRef.current = null;
      return;
    }

    if (lastSeedModelRef.current !== selectedModel) {
      const recreateOverride = recreateSeedOverrideRef.current;
      let nextSeedEnabled = isSeedDefaultEnabledVideoModel(selectedModel);
      if (isAlwaysOnSeedModel) {
        nextSeedEnabled = true;
      } else if (recreateOverride && recreateOverride.modelKey === selectedModel) {
        nextSeedEnabled = Boolean(recreateOverride.value);
      }
      const nextSeed =
        recreateOverride && Number.isFinite(Number(recreateOverride.seed))
          ? Number(recreateOverride.seed)
          : createRandomSeed(selectedModel);

      setSeed(nextSeed);
      setSeedEnabled(nextSeedEnabled);
      recreateSeedOverrideRef.current = null;
      lastSeedModelRef.current = selectedModel;
    }
  }, [canUseSeed, isAlwaysOnSeedModel, selectedModel]);

  useEffect(() => {
    if (!canShowWebSearchOption) {
      if (creationType !== 'video' || !selectedModelCaps.supportsWebSearch) {
        lastWebSearchModelRef.current = null;
        recreateWebSearchOverrideRef.current = null;
      }
      return;
    }

    if (lastWebSearchModelRef.current !== selectedModel) {
      const recreateOverride = recreateWebSearchOverrideRef.current;
      const nextWebSearchEnabled =
        recreateOverride && recreateOverride.modelKey === selectedModel
          ? Boolean(recreateOverride.value)
          : false;
      setWebSearchEnabled(nextWebSearchEnabled);
      recreateWebSearchOverrideRef.current = null;
      lastWebSearchModelRef.current = selectedModel;
    }
  }, [canShowWebSearchOption, creationType, selectedModel, selectedModelCaps.supportsWebSearch]);

  const isWebSearchEligible = canShowWebSearchOption && uploadedAssets.length === 0;
  const shouldApplyWebSearch = isWebSearchEligible && webSearchEnabled;

  const promptPlaceholderText = useMemo(() => {
    if (creationType === 'image') {
      const imagePromptKey = functionMode === 'editing'
        ? (IMAGE_EDITING_PROMPT_PLACEHOLDER_KEY_MAP[selectedModel] || 'imageEditingDefault')
        : (IMAGE_PROMPT_PLACEHOLDER_KEY_MAP[selectedModel] || 'imageDefault');
      return t(`promptPlaceholders.${imagePromptKey}`);
    }

    if (creationType !== 'video') {
      return t('promptPlaceholder');
    }

    if (selectedModel === 'kling-v4') {
      const placeholderKey = {
        general: 'generalKlingV4',
        reference: 'referenceKlingV4',
        frames: 'framesKlingV4',
        editing: 'editingKlingV4',
        extend: 'extendKlingV4',
      }[functionMode];
      if (placeholderKey) {
        return t(`promptPlaceholders.${placeholderKey}`, {
          maxAssets: currentInputRules?.maxAssets || 0,
          maxImages: currentInputRules?.maxAssetsByType?.image || 0,
          maxVideos: currentInputRules?.maxAssetsByType?.video || 0,
          maxAudios: currentInputRules?.maxAssetsByType?.audio || 0,
        });
      }
    }

    const isSeedance20AdvancedMode = SEEDANCE_20_ADVANCED_VIDEO_MODELS.has(selectedModel);
    const isSeedanceLegacyMode = SEEDANCE_LEGACY_VIDEO_MODELS.has(selectedModel);
    const isVeoMode = VEO_VIDEO_MODELS.has(selectedModel);
    const isHappyhorseMode = HAPPYHORSE_VIDEO_MODELS.has(selectedModel);
    if (functionMode === 'general') {
      return IMAGE_REQUIRED_GENERAL_VIDEO_MODELS.has(selectedModel)
        ? t('promptPlaceholders.textImageVideoRequiredImage')
        : t('promptPlaceholders.textImageVideoOptionalImage');
    }

    if (functionMode === 'frames') {
      if (isVeoMode || OPTIONAL_END_FRAME_VIDEO_MODELS.has(selectedModel)) {
        return t('promptPlaceholders.framesOptionalEnd');
      }
      return isSeedance20AdvancedMode
        ? t('promptPlaceholders.framesSeedance20')
        : t('promptPlaceholders.framesDefault');
    }

    if (functionMode === 'reference') {
      if (selectedModel === 'botgo-video-2.5') return t('promptPlaceholders.referenceBotgoVideo25');
      if (selectedModel === 'kling-v3-omni') {
        return t('promptPlaceholders.referenceKlingOmni');
      }
      if (['gemini-omni-vip', 'gemini-omni-1.1-flash-vip'].includes(selectedModel)) {
        return t('promptPlaceholders.referenceOmniFlashVip');
      }
      if (['gemini-omni', 'gemini-omni-1.1-flash'].includes(selectedModel)) {
        return t('promptPlaceholders.referenceOmniFlash');
      }
      if (selectedModel === 'grok-imagine-video-vip') {
        return t('promptPlaceholders.referenceGrok');
      }
      if (isSeedance20AdvancedMode) {
        return t('promptPlaceholders.referenceSeedance20');
      }
      if (isVeoMode) {
        return t('promptPlaceholders.referenceVeo');
      }
      if (isSeedanceLegacyMode) {
        return t('promptPlaceholders.referenceSeedance15');
      }
      if (isHappyhorseMode) {
        return t('promptPlaceholders.referenceHappyhorse');
      }
      return t('promptPlaceholders.referenceImage', { maxImages: currentInputRules?.maxAssetsByType?.image || 3 });
    }

    if (functionMode === 'editing') {
      if (selectedModel === 'kling-v3-omni') {
        return t('promptPlaceholders.editingKlingOmni');
      }
      if (selectedModel === 'happyhorse-1.0-video-edit') {
        return t('promptPlaceholders.editingHappyhorse');
      }
      if (['gemini-omni-vip', 'gemini-omni-1.1-flash-vip'].includes(selectedModel)) {
        return t('promptPlaceholders.editingGeminiOmniVip');
      }
      if (['gemini-omni', 'gemini-omni-1.1-flash'].includes(selectedModel)) {
        return t('promptPlaceholders.editingGeminiOmni');
      }
      if (selectedModel === 'grok-imagine-video-vip') {
        return t('promptPlaceholders.editingGrok');
      }
      if (isSeedance20AdvancedMode) {
        return t('promptPlaceholders.editingSeedance20');
      }
      return t('promptPlaceholders.editingDefault');
    }

    if (functionMode === 'extend') {
      if (selectedModel === 'gemini-omni-1.1-flash-vip') return t('promptPlaceholders.extendGeminiOmni11');
      if (VEO_EXTEND_SELECTABLE_MODEL_KEYS.has(selectedModel)) {
        return t('promptPlaceholders.extendVeo31KieAi');
      }
      if (VEO_GCP_EXTEND_MODEL_KEYS.has(selectedModel)) {
        return t('promptPlaceholders.extendVeo31Gcp');
      }
      if (selectedModel === 'kling-v3-omni') {
        return t('promptPlaceholders.extendKlingOmni');
      }
      if (selectedModel === 'grok-imagine-video-vip') {
        return t('promptPlaceholders.extendGrok');
      }
      if (isSeedance20AdvancedMode) {
        return t('promptPlaceholders.extendSeedance20');
      }
      return t('promptPlaceholders.extendDefault');
    }

    return t('promptPlaceholder');
  }, [creationType, currentInputRules, functionMode, selectedModel, t]);

  const resolveAspectIcon = (ratio) => {
    const parts = String(ratio || '').split(':').map((val) => Number(val));
    if (parts.length === 2 && Number.isFinite(parts[0]) && Number.isFinite(parts[1]) && parts[0] > 0 && parts[1] > 0) {
      if (parts[0] === parts[1]) {
        return <IconSquare size={16} />;
      }
      return parts[0] > parts[1] ? <IconDeviceTv size={16} /> : <IconDeviceMobile size={16} />;
    }
    return <IconLayoutGridAdd size={16} />;
  };

  const aspectOptions = useMemo(() => {
    const ratios = selectedModelCaps.allowedAspectRatios || [];
    const miniMaxHasFrameInput = uploadedAssets.some((asset) => asset?.type === 'image');
    const modeAwareRatios = selectedModel === 'kling-v4'
      ? (['frames', 'editing', 'extend'].includes(functionMode) || (functionMode === 'general' && miniMaxHasFrameInput)
        ? ratios.filter((ratio) => ratio === 'adaptive')
        : functionMode === 'general' ? ratios.filter((ratio) => ratio !== 'adaptive') : ratios)
      : isWanVideoModel(selectedModel) && functionMode === 'extend'
      ? ratios.filter((ratio) => ratio === 'adaptive')
      : selectedModel === 'seedance-2.5'
      && ['frames', 'editing', 'extend'].includes(functionMode)
      ? ratios.filter((ratio) => ratio === 'adaptive')

      : selectedModel?.startsWith('minimax-h3')
      ? (
        functionMode === 'frames' || (functionMode === 'general' && miniMaxHasFrameInput)
          ? ratios.filter((ratio) => ratio === 'adaptive')
          : functionMode === 'general'
            ? ratios.filter((ratio) => ratio !== 'adaptive')
            : ratios
      )
      : ratios;
    // 若当前分辨率意图有比例约束（如 4K 仅支持6个比例），则过滤比例列表
    const resolutionConstraints = selectedModelCaps.resolutionAspectConstraints || {};
    const activeConstraint = intendedResolutionLabel && resolutionConstraints[intendedResolutionLabel];
    const effectiveRatios = sourceDrivenHasConcreteSourceSpecs
      ? modeAwareRatios
      : activeConstraint
        ? modeAwareRatios.filter(r => activeConstraint.includes(r))
        : modeAwareRatios;

    const displayOrder = MODEL_ASPECT_RATIO_DISPLAY_ORDER[selectedModel] || [];
    const orderMap = new Map(displayOrder.map((ratio, index) => [ratio, index]));
    const orderedRatios = [...effectiveRatios].sort((a, b) => {
      const aOrder = orderMap.has(a) ? orderMap.get(a) : Number.MAX_SAFE_INTEGER;
      const bOrder = orderMap.has(b) ? orderMap.get(b) : Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return String(a).localeCompare(String(b));
    });

    return orderedRatios.map((ratio) => ({
      id: ratio,
      label: ratio,
      icon: resolveAspectIcon(ratio),
      requiresUpgrade: false,
      isDisabled: isSeedance25GeneralFirstFrame && ratio !== 'adaptive',
    }));
  }, [
    intendedResolutionLabel,
    functionMode,
    isSeedance25GeneralFirstFrame,
    selectedModel,
    selectedModelCaps.allowedAspectRatios,
    selectedModelCaps.resolutionAspectConstraints,
    sourceDrivenHasConcreteSourceSpecs,
    uploadedAssets,
  ]);

  const hasDenseAspectOptions = aspectOptions.length > 4;

  const displayResolutionLabels = useMemo(() => {
    const resolutions = selectedModelCaps.allowedResolutions || [];
    const displayOrder = MODEL_RESOLUTION_DISPLAY_ORDER[selectedModel] || [];
    const orderMap = new Map(displayOrder.map((label, index) => [String(label).toLowerCase(), index]));
    const filteredResolutions = [...resolutions];

    return filteredResolutions.sort((a, b) => {
      const aLabel = String(a).toLowerCase();
      const bLabel = String(b).toLowerCase();
      const aOrder = orderMap.has(aLabel) ? orderMap.get(aLabel) : Number.MAX_SAFE_INTEGER;
      const bOrder = orderMap.has(bLabel) ? orderMap.get(bLabel) : Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return String(a).localeCompare(String(b), undefined, { numeric: true });
    });
  }, [selectedModelCaps.allowedResolutions, selectedModel]);

  const temporarilyDisabledResolutionSet = useMemo(() => (
    new Set((selectedModelConfig?.temporarilyDisabledResolutions || []).map((label) => normalizeValue(label)))
  ), [selectedModelConfig?.temporarilyDisabledResolutions]);

  const currentQualityOptions = useMemo(() => {
    const sizesByAspect = selectedModelCaps.allowedSizesByAspect || {};
    const ratioMap = sizesByAspect[watchedSize] || {};
    const resolutions = displayResolutionLabels;
    return resolutions.map((label) => {
      const size = ratioMap[label];
      if (!size) return null;
      return {
        id: buildResolutionOptionId({
          aspectRatio: watchedSize,
          resolutionLabel: label,
          apiSize: size,
        }),
        label: selectedModel?.startsWith('minimax-h3') ? label : `${label} (${size})`,
        apiSize: size,
        resolutionLabel: label,
        requiresUpgrade: false,
        isTemporarilyDisabled: temporarilyDisabledResolutionSet.has(normalizeValue(label)) || isVsrResolutionDisabled(label),
      };
    }).filter(Boolean);
  }, [
    displayResolutionLabels,
    selectedModelCaps.allowedSizesByAspect,
    selectedModel,
    isVsrResolutionDisabled,
    temporarilyDisabledResolutionSet,
    watchedSize,
  ]);

  const enabledQualityOptions = useMemo(
    () => currentQualityOptions.filter((option) => !option.isTemporarilyDisabled),
    [currentQualityOptions]
  );

  const preferredResolutionLabel = MODEL_DEFAULT_RESOLUTION_LABEL[selectedModel];
  const preferredResolutionOption = preferredResolutionLabel
    ? enabledQualityOptions.find(
      (res) => String(res.resolutionLabel).toLowerCase() === String(preferredResolutionLabel).toLowerCase()
    )
    : null;
  const currentResolutionObj =
    currentQualityOptions.find((res) => res.id === selectedResolution && !res.isTemporarilyDisabled)
    || preferredResolutionOption
    || enabledQualityOptions[0]
    || currentQualityOptions[0];
  const currentResolutionLabel = currentResolutionObj?.resolutionLabel || '';
  const specResolutionLabel =
    currentResolutionObj?.resolutionLabel
    || currentResolutionObj?.label?.split(' ')[0]
    || '';
  const sourceDrivenDisplayResolutionLabel =
    sourceVideoResolutionLabel
    || specResolutionLabel
    || MODEL_DEFAULT_RESOLUTION_LABEL[selectedModel]
    || '';
  const specTriggerAspectLabel = sourceDrivenHasConcreteSourceSpecs
    ? sourceVideoAspectRatioLabel
    : watchedSize;
  const specTriggerResolutionLabel = sourceDrivenHasConcreteSourceSpecs
    ? sourceDrivenDisplayResolutionLabel
    : specResolutionLabel;
  const specTriggerLabel = `${specTriggerAspectLabel}${specTriggerResolutionLabel ? ` ${specTriggerResolutionLabel}` : ''}`;
  const isSpecsMenuDisabled = false;

  const sourceDrivenPreferredResolutionOption = useMemo(() => {
    if (!sourceDrivenHasConcreteSourceSpecs) return null;
    const sizesByAspect = selectedModelCaps.allowedSizesByAspect || {};
    const aspect = sourceVideoAspectRatioLabel;
    const sourceResolution =
      sourceVideoResolutionLabel
      || MODEL_DEFAULT_RESOLUTION_LABEL[selectedModel]
      || displayResolutionLabels[0]
      || '';
    const sourceSize = getSizeForSpec(sizesByAspect, aspect, sourceResolution);
    if (sourceSize && !temporarilyDisabledResolutionSet.has(normalizeValue(sourceResolution))) {
      return {
        id: buildResolutionOptionId({
          aspectRatio: aspect,
          resolutionLabel: sourceResolution,
          apiSize: sourceSize,
        }),
        resolutionLabel: sourceResolution,
        apiSize: sourceSize,
      };
    }

    const fallbackResolution = displayResolutionLabels.find((label) => (
      !temporarilyDisabledResolutionSet.has(normalizeValue(label))
      && getSizeForSpec(sizesByAspect, aspect, label)
    ));
    if (!fallbackResolution) return null;
    const fallbackSize = getSizeForSpec(sizesByAspect, aspect, fallbackResolution);
    return {
      id: buildResolutionOptionId({
        aspectRatio: aspect,
        resolutionLabel: fallbackResolution,
        apiSize: fallbackSize,
      }),
      resolutionLabel: fallbackResolution,
      apiSize: fallbackSize,
    };
  }, [
    displayResolutionLabels,
    selectedModel,
    selectedModelCaps.allowedSizesByAspect,
    sourceDrivenHasConcreteSourceSpecs,
    sourceVideoAspectRatioLabel,
    sourceVideoResolutionLabel,
    temporarilyDisabledResolutionSet,
  ]);

  const isResolutionCompatibleForAspect = useCallback((resolutionLabel, aspectValue) => {
    if (!resolutionLabel || !aspectValue) return false;
    const normalizedAspect = normalizeAspectRatioLabel(aspectValue);
    const normalizedSourceAspect = normalizeAspectRatioLabel(sourceVideoAspectRatioLabel);
    const normalizedResolution = normalizeValue(resolutionLabel);
    const normalizedSourceResolution = normalizeValue(sourceDrivenDisplayResolutionLabel);

    if (!normalizedAspect) return false;
    if (sourceDrivenHasConcreteSourceSpecs && normalizedAspect !== normalizedSourceAspect) {
      return false;
    }
    if (temporarilyDisabledResolutionSet.has(normalizedResolution)) {
      return false;
    }
    if (
      sourceDrivenHasConcreteSourceSpecs
      && isSourceDrivenVideoFixedSpecs
      && normalizedResolution !== normalizedSourceResolution
    ) {
      return false;
    }

    return Boolean(getSizeForSpec(
      selectedModelCaps.allowedSizesByAspect,
      normalizedAspect,
      resolutionLabel
    ));
  }, [
    isSourceDrivenVideoFixedSpecs,
    selectedModelCaps.allowedSizesByAspect,
    sourceDrivenDisplayResolutionLabel,
    sourceDrivenHasConcreteSourceSpecs,
    sourceVideoAspectRatioLabel,
    temporarilyDisabledResolutionSet,
  ]);

  const isAspectCompatible = useCallback((aspectValue) => (
    displayResolutionLabels.some((resolution) => isResolutionCompatibleForAspect(resolution, aspectValue))
  ), [displayResolutionLabels, isResolutionCompatibleForAspect]);

  const getCompatibleAspectForResolution = useCallback((resolutionLabel) => {
    if (isResolutionCompatibleForAspect(resolutionLabel, watchedSize)) {
      return watchedSize;
    }
    return aspectOptions.find((option) =>
      isResolutionCompatibleForAspect(resolutionLabel, option.id)
    )?.id || null;
  }, [aspectOptions, isResolutionCompatibleForAspect, watchedSize]);

  const getDefaultResolutionForAspect = useCallback((aspectValue) => {
    if (sourceVideoResolutionLabel && isResolutionCompatibleForAspect(sourceVideoResolutionLabel, aspectValue)) {
      return sourceVideoResolutionLabel;
    }
    const preferredResolution = MODEL_DEFAULT_RESOLUTION_LABEL[selectedModel];
    if (preferredResolution && isResolutionCompatibleForAspect(preferredResolution, aspectValue)) {
      return preferredResolution;
    }
    return displayResolutionLabels.find((label) => isResolutionCompatibleForAspect(label, aspectValue))
      || displayResolutionLabels[0]
      || '720p';
  }, [
    displayResolutionLabels,
    isResolutionCompatibleForAspect,
    selectedModel,
    sourceVideoResolutionLabel,
  ]);

  const modelDurationValues = useMemo(() => {
    return getAllowedDurationsForVideoRequest({
      modelKey: selectedModel,
      resolutionLabel: currentResolutionLabel,
      functionMode,
    });
  }, [
    currentResolutionLabel,
    selectedModel,
    functionMode,
  ]);
  const isSourceDrivenVideoDurationLocked =
    creationType === 'video'
    && (functionMode === 'editing' || functionMode === 'enhance')
    && hasPrimarySourceVideoAsset
    && (isAutoVideoEditDurationModeActive || functionMode === 'enhance');

  const currentDurationOptions = useMemo(() => {
    if (isSourceDrivenVideoDurationLocked) {
      if (isVsrMode) {
        const seconds = Math.round(sourceVideoDurationSeconds || 5);
        return [{ id: `${seconds}s`, label: `${seconds}s`, value: seconds, requiresUpgrade: false, isSourceLocked: true }];
      }
      const fallbackDuration =
        sourceVideoDurationSeconds
        || parseDurationSeconds(MODEL_DEFAULT_DURATION[selectedModel])
        || parseDurationSeconds(selectedModelCaps.defaultDuration)
        || parseDurationSeconds(selectedModelCaps.allowedDurations?.[0])
        || GEMINI_OMNI_VIP_DEFAULT_DURATION_SECONDS;
      const lockedDurationValue = resolveAutoVideoEditBillingDuration({
        modelKey: selectedModel,
        inputAssets: uploadedAssets,
        fallbackDuration,
      });
      const resolvedDuration = lockedDurationValue || Math.round(fallbackDuration);
      return [{
        id: `${resolvedDuration}s`,
        label: `${resolvedDuration}s`,
        value: resolvedDuration,
        requiresUpgrade: false,
        isSourceLocked: true,
      }];
    }

    const durations = isWanVideoModel(selectedModel)
      ? modelDurationValues.filter((seconds) => seconds + getWanInputVideoDuration(uploadedAssets) <= 30)
      : modelDurationValues;
    if (!durations.length) return [];

    return durations.map((duration) => ({
      id: `${duration}s`,
      label: `${duration}s`,
      value: duration,
      requiresUpgrade: false,
    }));
  }, [
    isSourceDrivenVideoDurationLocked,
    isVsrMode,
    modelDurationValues,
    selectedModelCaps.defaultDuration,
    selectedModelCaps.allowedDurations,
    selectedModel,
    sourceVideoDurationSeconds,
    uploadedAssets,
  ]);
  const hasDenseDurationOptions =
    ['gemini-omni-vip', 'gemini-omni-1.1-flash-vip'].includes(selectedModel) || currentDurationOptions.length > 8;
  const durationGridCols =
    currentDurationOptions.length > 20
      ? 'grid-cols-5'
      : currentDurationOptions.length > 12
        ? 'grid-cols-4'
        : 'grid-cols-3';

  const currentDurationObj = currentDurationOptions.find(dur => dur.id === selectedDuration) || currentDurationOptions[0];
  const effectiveDurationValue = useMemo(() => {
    if (isVsrMode) return Math.round(sourceVideoDurationSeconds || 0);
    if (isSourceDrivenVideoDurationLocked) {
      return resolveAutoVideoEditBillingDuration({
        modelKey: selectedModel,
        inputAssets: uploadedAssets,
        fallbackDuration: currentDurationObj?.value || GEMINI_OMNI_VIP_DEFAULT_DURATION_SECONDS,
      });
    }
    return currentDurationObj?.value || null;
  }, [currentDurationObj?.value, isSourceDrivenVideoDurationLocked, isVsrMode, selectedModel, sourceVideoDurationSeconds, uploadedAssets]);

  const { canUseNoContentFiltering, noContentFiltering, noContentFilteringRequest, setNoContentFiltering } = useContentFiltering({
    modelKey: selectedModel,
    subscription: currentSubscription?.subscription,
  });
  const currentPlanTier = currentSubscription?.subscription?.plan?.tier ?? 0;
  const currentPlanBillingCycle = currentSubscription?.subscription?.plan?.billingCycle ?? null;
  const canUpgradeForInsufficientCredits =
    currentPlanTier < 4 || (currentPlanTier === 4 && currentPlanBillingCycle !== 'yearly');

  // 实时监听 additionalInfo 变化，传递进度给父组件
  useEffect(() => {
    if (additionalInfo && handleGenerationComplete) {
      // 传递进度/任务事件给父组件，附带当前任务 key
      handleGenerationComplete(null, {
        ...additionalInfo,
        taskKey: activeTaskKey
      });
    }
    // 不依赖 handleGenerationComplete，避免父组件函数引用变化导致循环
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalInfo, activeTaskKey]);

  // 初始化和更新选中的宽高比（根据模型和宽高比变化）
  useEffect(() => {
    if (isSeedance25GeneralFirstFrame) {
      if (watchedSize !== 'adaptive') {
        setValue('size', 'adaptive');
      }
      return;
    }

    const modelChanged = lastAspectModelRef.current !== selectedModel;
    if (
      sourceDrivenHasConcreteSourceSpecs
      && sourceVideoAspectRatioLabel
      && aspectOptions.some((opt) => normalizeAspectRatioLabel(opt.id) === normalizeAspectRatioLabel(sourceVideoAspectRatioLabel))
    ) {
      if (watchedSize !== sourceVideoAspectRatioLabel) {
        setValue('size', sourceVideoAspectRatioLabel);
        return;
      }
      if (modelChanged) {
        lastAspectModelRef.current = selectedModel;
      }
      return;
    }

    const recreateAspectOverride = recreateAspectOverrideRef.current;
    if (
      recreateAspectOverride
      && recreateAspectOverride.modelKey === selectedModel
      && aspectOptions.some((opt) => opt.id === recreateAspectOverride.aspectId)
    ) {
      if (watchedSize !== recreateAspectOverride.aspectId) {
        setValue('size', recreateAspectOverride.aspectId);
        return;
      }

      if (modelChanged) {
        lastAspectModelRef.current = selectedModel;
      }
      recreateAspectOverrideRef.current = null;
      return;
    }

    if (modelChanged) {
      lastAspectModelRef.current = selectedModel;
      const preferredAspect = resolvePreferredAspectRatio({
        modelKey: selectedModel,
        modelType: selectedModelConfig?.type,
      });
      if (preferredAspect && aspectOptions.some((opt) => opt.id === preferredAspect)) {
        if (watchedSize !== preferredAspect) {
          setValue('size', preferredAspect);
        }
        return;
      }
    }

    if (aspectOptions.length > 0 && !aspectOptions.some(opt => opt.id === watchedSize)) {
      setValue('size', aspectOptions[0].id);
    }
  }, [
    aspectOptions,
    isSeedance25GeneralFirstFrame,
    selectedModel,
    selectedModelConfig?.type,
    setValue,
    sourceDrivenHasConcreteSourceSpecs,
    sourceVideoAspectRatioLabel,
    watchedSize,
  ]);

  // 模型切换时重置分辨率标签意图，避免旧意图影响新模型
  useEffect(() => {
    const resolutionOverride = recreateResolutionOverrideRef.current;
    if (
      resolutionOverride?.modelKey === selectedModel
      && resolutionOverride.resolutionLabel
    ) {
      setIntendedResolutionLabel(resolutionOverride.resolutionLabel);
      return;
    }

    setIntendedResolutionLabel(null);
  }, [selectedModel]);

  // 模型切换时，若新模型不支持 quality / moderation 选择则重置为默认值
  // Recreate 场景下优先应用 ref 覆盖值，避免被默认值覆盖。
  useEffect(() => {
    const caps = getModelCapabilities(selectedModel) || {};

    // --- quality ---
    const qualityOverride = recreateQualityOverrideRef.current;
    if (
      qualityOverride
      && qualityOverride.modelKey === selectedModel
      && caps.allowedQualities?.length
      && caps.allowedQualities.includes(qualityOverride.value)
    ) {
      if (selectedQuality !== qualityOverride.value) {
        setSelectedQuality(qualityOverride.value);
      }
      recreateQualityOverrideRef.current = null;
    } else {
      recreateQualityOverrideRef.current = null;
      if (!caps.allowedQualities?.length) {
        setSelectedQuality('low');
      }
    }

    // --- moderation ---
    const moderationOverride = recreateModerationOverrideRef.current;
    if (
      moderationOverride
      && moderationOverride.modelKey === selectedModel
      && caps.allowedModerations?.length
      && caps.allowedModerations.includes(moderationOverride.value)
    ) {
      if (selectedModeration !== moderationOverride.value) {
        setSelectedModeration(moderationOverride.value);
      }
      recreateModerationOverrideRef.current = null;
    } else {
      recreateModerationOverrideRef.current = null;
      if (!caps.allowedModerations?.length) {
        setSelectedModeration('auto');
      }
    }
  }, [selectedModel]);

  useEffect(() => {
    if (!isVsrMode || !primarySourceVideoAsset || !sourceVideoDimensions) {
      vsrDefaultSourceRef.current = null;
      return;
    }
    const sourceKey = `${selectedModel}:${primarySourceVideoAsset.url}:${sourceVideoDimensions.width}x${sourceVideoDimensions.height}`;
    if (vsrDefaultSourceRef.current === sourceKey || !enabledQualityOptions.length) return;
    const resolutionOrder = { '720p': 0, '1080p': 1, '2k': 2, '4k': 3 };
    const lowest = [...enabledQualityOptions].sort((a, b) => (
      (resolutionOrder[String(a.resolutionLabel).toLowerCase()] ?? 99)
      - (resolutionOrder[String(b.resolutionLabel).toLowerCase()] ?? 99)
    ))[0];
    vsrDefaultSourceRef.current = sourceKey;
    lastResolutionModelRef.current = selectedModel;
    setSelectedResolution(lowest.id);
    setIntendedResolutionLabel(lowest.resolutionLabel);
  }, [enabledQualityOptions, isVsrMode, primarySourceVideoAsset, selectedModel, sourceVideoDimensions]);

  useEffect(() => {
    const modelChanged = lastResolutionModelRef.current !== selectedModel;
    if (sourceDrivenPreferredResolutionOption) {
      if (selectedResolution !== sourceDrivenPreferredResolutionOption.id) {
        setSelectedResolution(sourceDrivenPreferredResolutionOption.id);
        return;
      }
      if (
        sourceDrivenPreferredResolutionOption.resolutionLabel
        && intendedResolutionLabel !== sourceDrivenPreferredResolutionOption.resolutionLabel
      ) {
        setIntendedResolutionLabel(sourceDrivenPreferredResolutionOption.resolutionLabel);
      }
      if (modelChanged) {
        lastResolutionModelRef.current = selectedModel;
      }
      return;
    }

    const recreateResolutionOverride = recreateResolutionOverrideRef.current;
    const recreateResolutionOption = recreateResolutionOverride
      ? enabledQualityOptions.find((option) => (
        option.id === recreateResolutionOverride.resolutionId
        || (
          recreateResolutionOverride.resolutionLabel
          && String(option.resolutionLabel).toLowerCase() === String(recreateResolutionOverride.resolutionLabel).toLowerCase()
        )
        || (
          recreateResolutionOverride.apiSize
          && option.apiSize === recreateResolutionOverride.apiSize
          && (
            !recreateResolutionOverride.resolutionLabel
            || option.resolutionLabel === recreateResolutionOverride.resolutionLabel
          )
        )
      ))
      : null;
    if (currentQualityOptions.length > 0) {
      if (
        recreateResolutionOverride
        && recreateResolutionOverride.modelKey === selectedModel
        && recreateResolutionOption
      ) {
        if (selectedResolution !== recreateResolutionOption.id) {
          setSelectedResolution(recreateResolutionOption.id);
          return;
        }
        if (
          recreateResolutionOption.resolutionLabel
          && intendedResolutionLabel !== recreateResolutionOption.resolutionLabel
        ) {
          setIntendedResolutionLabel(recreateResolutionOption.resolutionLabel);
        }

        if (modelChanged) {
          lastResolutionModelRef.current = selectedModel;
        }
        recreateResolutionOverrideRef.current = null;
        return;
      }

      if (modelChanged) {
        lastResolutionModelRef.current = selectedModel;
      }

      if (modelChanged) {
        const preferredResolutionLabel = MODEL_DEFAULT_RESOLUTION_LABEL[selectedModel];
        const preferredOption = enabledQualityOptions.find(
          (option) => option.resolutionLabel === preferredResolutionLabel
        );
        if (preferredOption && selectedResolution !== preferredOption.id) {
          setSelectedResolution(preferredOption.id);
          return;
        }
      }

      // 检查当前选中的分辨率是否在新的选项中
      const isCurrentResolutionValid = enabledQualityOptions.some(res => res.id === selectedResolution);
      if (!isCurrentResolutionValid) {
        // 比例切换时，优先保持相同的分辨率档位（如从 16:9 4K 切换到 9:16 时继续选 4K）
        const preferredLabel = intendedResolutionLabel;
        const sameLabelOption = preferredLabel && enabledQualityOptions.find(r => r.resolutionLabel === preferredLabel);
        const modelDefaultOption = preferredResolutionLabel
          ? enabledQualityOptions.find(
            (r) => String(r.resolutionLabel).toLowerCase() === String(preferredResolutionLabel).toLowerCase()
          )
          : null;
        const fallbackOption = sameLabelOption || modelDefaultOption || enabledQualityOptions[0] || currentQualityOptions[0];
        if (fallbackOption) {
          setSelectedResolution(fallbackOption.id);
        }
      }
    }
  }, [
    currentQualityOptions,
    enabledQualityOptions,
    intendedResolutionLabel,
    preferredResolutionLabel,
    selectedModel,
    selectedResolution,
    sourceDrivenPreferredResolutionOption,
  ]);

  // 更新时长选项（根据模型变化）
  useEffect(() => {
    const modelChanged = lastModelRef.current !== selectedModel;
    if (modelChanged) {
      lastModelRef.current = selectedModel;
    }

    if (!currentDurationOptions.length) {
      if (selectedDuration) {
        setSelectedDuration('');
      }
      if (activeMenu === 'duration') {
        setActiveMenu(null);
      }
      return;
    }

    if (isSourceDrivenVideoDurationLocked) {
      const lockedDurationId = currentDurationOptions[0]?.id || '';
      if (selectedDuration !== lockedDurationId) {
        setSelectedDuration(lockedDurationId);
      }
      if (activeMenu === 'duration') {
        setActiveMenu(null);
      }
      return;
    }

    const roundedSourceDurationSeconds = sourceVideoDurationSeconds
      ? Math.round(sourceVideoDurationSeconds)
      : null;
    const sourceDurationOption = roundedSourceDurationSeconds
      ? currentDurationOptions.find((dur) => Number(dur.value) === roundedSourceDurationSeconds)
      : null;
    if (
      creationType === 'video'
      && ['editing', 'extend'].includes(functionMode)
      && hasPrimarySourceVideoAsset
      && sourceDurationOption
    ) {
      if (selectedDuration !== sourceDurationOption.id) {
        setSelectedDuration(sourceDurationOption.id);
      }
      return;
    }

    if (modelChanged) {
      const recreateDurationOverride = recreateDurationOverrideRef.current;
      if (
        recreateDurationOverride
        && recreateDurationOverride.modelKey === selectedModel
        && currentDurationOptions.some((dur) => dur.id === recreateDurationOverride.durationId)
      ) {
        recreateDurationOverrideRef.current = null;
        if (selectedDuration !== recreateDurationOverride.durationId) {
          setSelectedDuration(recreateDurationOverride.durationId);
        }
        return;
      }

      recreateDurationOverrideRef.current = null;
      const nextDefault = resolvePreferredDuration({
        modelKey: selectedModel,
        modelType: selectedModelConfig?.type,
        availableOptions: currentDurationOptions,
      });
      if (selectedDuration !== nextDefault) {
        setSelectedDuration(nextDefault);
      }
      return;
    }

    const isCurrentDurationValid = currentDurationOptions.some(dur => dur.id === selectedDuration);
    if (!isCurrentDurationValid) {
      setSelectedDuration(resolvePreferredDuration({
        modelKey: selectedModel,
        modelType: selectedModelConfig?.type,
        availableOptions: currentDurationOptions,
      }));
    }
  }, [
    activeMenu,
    creationType,
    currentDurationOptions,
    functionMode,
    hasPrimarySourceVideoAsset,
    isSourceDrivenVideoDurationLocked,
    selectedDuration,
    selectedModel,
    selectedModelConfig?.type,
    sourceVideoDurationSeconds,
  ]);

  // REMOVED: Automatic clearing of uploaded images when switching modes/inputs to persist them.

  // 自动调整 Textarea 高度（智能高度：40px 最小 → 200px 最大 → 超出显示滚动条）
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.getBoundingClientRect();
      const scrollHeight = textarea.scrollHeight;
      const newHeight = Math.min(Math.max(scrollHeight, 40), 200);
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
    const frame = window.requestAnimationFrame(adjustTextareaHeight);
    return () => window.cancelAnimationFrame(frame);
  }, [watchedPrompt, uploadedAssets.length, promptPlaceholderText, adjustTextareaHeight]);

  useEffect(() => {
    window.addEventListener('resize', adjustTextareaHeight);
    document.addEventListener('botgo:remeasure-generator-prompt', adjustTextareaHeight);
    return () => {
      window.removeEventListener('resize', adjustTextareaHeight);
      document.removeEventListener('botgo:remeasure-generator-prompt', adjustTextareaHeight);
    };
  }, [adjustTextareaHeight]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || typeof ResizeObserver === 'undefined') return undefined;

    let previousWidth = textarea.getBoundingClientRect().width;
    const observer = new ResizeObserver(([entry]) => {
      const nextWidth = entry?.contentRect?.width
        ?? textarea.getBoundingClientRect().width;
      if (Math.abs(nextWidth - previousWidth) < 0.5) return;

      previousWidth = nextWidth;
      adjustTextareaHeight();
    });

    observer.observe(textarea);
    return () => observer.disconnect();
  }, [adjustTextareaHeight]);

  const { pricingSpotlight } = useCampaignConfigs();
  const autumnPromotionActive = getPricingSpotlightState(pricingSpotlight).isActive;
  const seedanceDiscount = resolveModelCreditDiscount({
    subscription: currentSubscription?.subscription,
    snapshot: currentSubscription?.effectiveEntitlements?.entitlementSnapshot,
    campaignActive: autumnPromotionActive,
    planKey: getPlanIdentity(currentSubscription?.subscription?.plan),
    billingCycle: currentSubscription?.subscription?.plan?.billingCycle,
    modelKey: selectedModel,
    resolution: currentResolutionObj?.apiSize,
    resolutionLabel: currentResolutionObj?.resolutionLabel,
    privacyUnrestricted,
  });

  const creditsPerOutput = useMemo(() => {
    const resolutionValue = currentResolutionObj?.apiSize || '';
    if (!selectedModelConfig) return 0;
    if (selectedModelConfig.type === 'video') {
      const durationValue = effectiveDurationValue || 0;
      return Number(getVideoCreditCost({
        modelKey: selectedModel,
        resolution: resolutionValue,
        resolutionLabel: currentResolutionObj?.resolutionLabel,
        duration: durationValue,
        generateAudio: canToggleGenerateAudio && generateAudio,
        contextIrEnabled,
        functionMode,
        hasVideoReference: hasKlingOmniVideoReferencePricing || (selectedModel === 'kling-v4' && hasVideoReferenceInput) || (selectedModel.startsWith('gemini-omni') && hasVideoReferenceInput),
        inputVideoDuration: inputVideoDurationSeconds,
        inputImageCount,
        privacyUnrestricted,
        creditDiscountBasisPoints: seedanceDiscount.basisPoints,
        fps: isVsrMode ? selectedFps : undefined,
      })) || 0;
    }
    return Number(getImageCreditCost({
      modelKey: selectedModel,
      resolution: resolutionValue,
      resolutionLabel: currentResolutionObj?.resolutionLabel,
      quality: selectedQuality,
      inputImageCount,
      functionMode,
    })) || 0;
  }, [
    seedanceDiscount.basisPoints,
    canToggleGenerateAudio,
    effectiveDurationValue,
    currentResolutionObj?.apiSize,
    currentResolutionObj?.resolutionLabel,
    functionMode,
    generateAudio,
    contextIrEnabled,
    hasKlingOmniVideoReferencePricing,
    hasVideoReferenceInput,
    inputVideoDurationSeconds,
    inputImageCount,
    isVsrMode,
    privacyUnrestricted,
    selectedFps,
    selectedModel,
    selectedModelConfig,
    selectedQuality,
  ]);

  const isUnlimitedOutputCountLocked = selectedGenerationMode === 'unlimited';
  const selectedResolutionLabel = currentResolutionObj?.resolutionLabel || currentResolutionObj?.label?.split(' ')[0] || null;
  const miniMaxImagePricing = MINIMAX_H3_IMAGE_PRICING[selectedModel];
  const creditPricingTooltip = [
    inputVideoPricingTooltipKey
      ? t(inputVideoPricingTooltipKey, { credits: getGeminiOmniCreditCost({ modelKey: selectedModel, resolution: selectedResolutionLabel, hasVideoReference: true }) })
      : null,
    miniMaxImagePricing ? t('generationMode.minimaxInputImagePricingTooltip', miniMaxImagePricing) : null,
    ['gpt-image-2.5-flare-vip', 'gpt-image-2.5-sunburst-vip'].includes(selectedModel)
      ? t('generationMode.gptImage25VipInputImagePricingTooltip') : null,
  ].filter(Boolean).join(' ');
  const canUseContextIr = selectedModel?.startsWith('minimax-h3');
  useEffect(() => {
    if (!canUseContextIr || isUnlimitedOutputCountLocked) setContextIrEnabled(false);
  }, [canUseContextIr, isUnlimitedOutputCountLocked]);
  const effectiveOutputCount = resolveEffectiveOutputCount(outputCount, selectedGenerationMode);
  const creditsRequired = creditsPerOutput * effectiveOutputCount;

  const selectedResolutionSize = currentResolutionObj?.apiSize || '';
  const isAutoAspectResolution = selectedResolutionSize === 'auto';
  const selectedDurationValue = selectedModelConfig?.type === 'video' ? effectiveDurationValue : null;
  const selectedAspectRatio = watchedSize;
  const selectedInputMode = useMemo(() => {
    if (!canUseFileInput || uploadedAssets.length === 0) return 'text';
    if (uploadedAssets.some((asset) => asset?.type === 'video')) return 'video';
    if (uploadedAssets.some((asset) => asset?.type === 'image')) return 'image';
    if (uploadedAssets.some((asset) => asset?.type === 'audio')) return 'audio';
    return 'text';
  }, [canUseFileInput, uploadedAssets]);
  const isPaidMember = userPlanCode !== 'free';
  const currentPlanPeriodStart = currentSubscription?.subscription?.currentPeriodStart
    || currentSubscription?.subscription?.startDate
    || currentSubscription?.entitlementPeriodStart
    || currentSubscription?.monthlyCreditsLastGrantedAt
    || null;

  const currentUnlimitedAccessState = useMemo(() => resolveGenerationAccessState({
    modelKey: selectedModel,
    unlimitedRules: planCapabilities?.normalizedUnlimitedRules || [],
    promotionalRules: planCapabilities?.normalizedPromotionalRules || [],
    periodStart: currentPlanPeriodStart,
    duration: selectedDurationValue,
    requestedResolution: selectedResolutionSize,
    requestedResolutionLabel: selectedResolutionLabel,
    requestedAspectRatioLabel: selectedAspectRatio,
    inputMode: selectedInputMode,
    functionMode: creationType === 'video' || creationType === 'image' ? functionMode : 'general',
    imageQuality: selectedQuality,
    background: selectedBackground,
    promotionalUsage: currentSubscription?.promotionalUsage || {},
  }), [
    planCapabilities?.normalizedUnlimitedRules,
    planCapabilities?.normalizedPromotionalRules,
    currentPlanPeriodStart,
    selectedModel,
    selectedDurationValue,
    selectedResolutionLabel,
    selectedResolutionSize,
    selectedAspectRatio,
    selectedInputMode,
    creationType,
    functionMode,
    selectedQuality,
    selectedBackground,
    currentSubscription?.promotionalUsage,
  ]);

  const canSelectUnlimitedMode = currentUnlimitedAccessState.canUseUnlimited;
  const creditModeCostLabel = formatCreditsValue(creditsRequired);
  const fallbackCreditModeCostLabel = formatCreditsValue(
    generationModeFallback?.creditsRequired ?? creditsRequired
  );
  const generationModeLockedDescription = isPaidMember
    ? t('generationMode.paidLockedFooter')
    : t('generationMode.freeFooter');
  const unlimitedPreviewEntitlementClass = useMemo(() => {
    if (currentUnlimitedAccessState.promotionalInfo?.type) {
      return isPaidMember
        ? SLOW_LANE_ENTITLEMENT.PAID_PROMOTION
        : SLOW_LANE_ENTITLEMENT.FREE_PROMOTION;
    }

    return isPaidMember
      ? SLOW_LANE_ENTITLEMENT.PAID_UNLIMITED
      : SLOW_LANE_ENTITLEMENT.FREE_UNLIMITED;
  }, [currentUnlimitedAccessState.promotionalInfo?.type, isPaidMember]);
  const unlimitedPreviewWaitLabel = useMemo(() => {
    const resourceType = selectedModelConfig?.type === 'image' ? 'image' : 'video';
    const previewProfileMinutes = currentSubscription?.slowLanePreviewProfiles?.[unlimitedPreviewEntitlementClass]?.[resourceType] || null;
    return formatSlowLaneDelayRangeLabel({
      entitlementClass: unlimitedPreviewEntitlementClass,
      resourceType,
      creditsRequired,
      profileMinutes: previewProfileMinutes,
    });
  }, [
    creditsRequired,
    currentSubscription?.slowLanePreviewProfiles,
    selectedModelConfig?.type,
    unlimitedPreviewEntitlementClass,
  ]);
  const generationModeUnlimitedSubtitle = t('generationMode.unlimitedSubtitle', {
    wait: unlimitedPreviewWaitLabel,
  });
  const unlimitedModeNote = currentUnlimitedAccessState.promotionalInfo?.type === 'count_based'
    ? (
      currentUnlimitedAccessState.promotionalInfo.remainingGenerations > 0
        ? t(
          currentUnlimitedAccessState.promotionalInfo.remainingGenerations === 1
            ? 'generationMode.promotionalRemainingOne'
            : 'generationMode.promotionalRemainingMany',
          { count: currentUnlimitedAccessState.promotionalInfo.remainingGenerations }
        )
        : t('generationMode.promotionalExhausted')
    )
    : null;
  const generationModeFallbackPrimaryLabel = t('generationMode.fallbackPrimary', {
    credits: fallbackCreditModeCostLabel,
  });

  useEffect(() => {
    if (!canSelectUnlimitedMode && generationModeRef.current !== 'credit') {
      updateGenerationMode('credit');
    }
  }, [canSelectUnlimitedMode, updateGenerationMode]);

  const generationModeConfigSignature = useMemo(() => JSON.stringify({
    creationType,
    functionMode,
    modelKey: selectedModel,
    resolution: selectedResolutionSize,
    resolutionLabel: selectedResolutionLabel,
    aspectRatio: selectedAspectRatio,
    duration: selectedDurationValue,
    inputMode: selectedInputMode,
    imageQuality: selectedQuality,
    background: selectedBackground,
    generateAudio: canToggleGenerateAudio && generateAudio,
    contextIrEnabled,
  }), [
    canToggleGenerateAudio,
    creationType,
    functionMode,
    generateAudio,
    contextIrEnabled,
    selectedAspectRatio,
    selectedDurationValue,
    selectedInputMode,
    selectedModel,
    selectedQuality,
    selectedBackground,
    selectedResolutionLabel,
    selectedResolutionSize,
  ]);

  useEffect(() => {
    if (generationModeConfigSignatureRef.current === null) {
      generationModeConfigSignatureRef.current = generationModeConfigSignature;
      return;
    }

    if (generationModeConfigSignatureRef.current === generationModeConfigSignature) return;

    generationModeConfigSignatureRef.current = generationModeConfigSignature;
    generationPreflightCacheRef.current = null;
    if (generationModeRef.current === 'unlimited') {
      updateGenerationMode('credit');
    }
  }, [generationModeConfigSignature, updateGenerationMode]);

  useEffect(() => {
    const signal = queuedGenerationModeFallback;
    const signalId = signal?.id || signal?.taskKey || signal?.taskId || signal?.videoId;
    if (!signalId || handledQueuedFallbackRef.current === signalId) return;

    handledQueuedFallbackRef.current = signalId;

    const numericCreditsRequired = Number(signal?.creditsRequired);
    const retryParams = signal?.params && typeof signal.params === 'object'
      ? {
          ...signal.params,
          generationMode: 'credit',
        }
      : null;

    updateGenerationMode('credit');
    setGenerationModeFallback({
      type: 'unlimited_mode_busy',
      creditsRequired: Number.isFinite(numericCreditsRequired)
        ? numericCreditsRequired
        : creditsRequired,
      title: t('generationMode.fallbackTitle'),
      message: signal?.message || t('generationMode.fallbackDescription'),
      retryPrompt: signal?.prompt || retryParams?.prompt || '',
      retryParams,
    });
  }, [creditsRequired, queuedGenerationModeFallback, t, updateGenerationMode]);

  // 直接获取prompt值
  const prompt = watch('prompt');
  const promptBeforeEnhanceRef = useRef('');
  useEffect(() => {
    const currentPrompt = String(form.getValues('prompt') || '');
    if (functionMode === 'enhance' && creationType === 'video') {
      if (currentPrompt !== 'Enhance video') {
        promptBeforeEnhanceRef.current = currentPrompt;
        setValue('prompt', 'Enhance video', { shouldValidate: true });
      }
    } else if (currentPrompt === 'Enhance video') {
      setValue('prompt', promptBeforeEnhanceRef.current, { shouldValidate: false });
    }
  }, [creationType, form, functionMode, setValue]);
  const isReferenceMentionEnabled =
    creationType === 'video'
    && ['reference', 'editing', 'extend'].includes(functionMode)
    && supportsReferenceMentionModel(selectedModel, functionMode);
  const referenceAssetDescriptors = useMemo(() => {
    const counters = {
      image: 0,
      video: 0,
      audio: 0,
    };

    return uploadedAssets
      .filter(Boolean)
      .slice(0, maxInputUploads || uploadedAssets.length)
      .map((asset, index) => {
        const mediaType = normalizeReferenceMediaType(asset.type);
        counters[mediaType] += 1;
        const referenceKey = `${REFERENCE_MENTION_TYPE_LABELS[mediaType]}${counters[mediaType]}`;

        return {
          asset,
          assetId: asset.id,
          assetIndex: index,
          mediaType,
          referenceKey,
          normalizedKey: referenceKey.toLowerCase(),
          isReady: asset.status === 'complete' && Boolean(asset.url),
          thumbnailUrl: mediaType === 'image' ? (asset.preview || asset.url) : null,
          previewUrl: asset.preview || null,
          mediaUrl: asset.url || null,
        };
      });
  }, [maxInputUploads, uploadedAssets]);

  const referenceMentionOptions = useMemo(
    () => referenceAssetDescriptors.filter((descriptor) => descriptor.isReady),
    [referenceAssetDescriptors]
  );

  const referenceAssetLabelById = useMemo(
    () => new Map(referenceAssetDescriptors.map((descriptor) => [descriptor.assetId, descriptor.referenceKey])),
    [referenceAssetDescriptors]
  );

  const availableReferenceMentionKeys = useMemo(
    () => new Set(referenceMentionOptions.map((option) => option.normalizedKey)),
    [referenceMentionOptions]
  );

  const promptReferenceKeys = useMemo(
    () => getReferenceMentionKeys(prompt),
    [prompt]
  );

  const referencedAssetIds = useMemo(
    () => new Set(
      referenceAssetDescriptors
        .filter((descriptor) => promptReferenceKeys.has(descriptor.normalizedKey))
        .map((descriptor) => descriptor.assetId)
    ),
    [promptReferenceKeys, referenceAssetDescriptors]
  );

  const filteredReferenceMentionOptions = useMemo(() => {
    if (!referenceMentionState.open) {
      return [];
    }

    const normalizedQuery = referenceMentionState.query.toLowerCase();
    return referenceMentionOptions
      .filter((option) => {
        if (!normalizedQuery) {
          return true;
        }
        return option.normalizedKey.includes(normalizedQuery)
          || option.mediaType.includes(normalizedQuery);
      })
      .slice(0, 8);
  }, [referenceMentionOptions, referenceMentionState.open, referenceMentionState.query]);

  const closeReferenceMentionPicker = useCallback(() => {
    setReferenceMentionState((prev) => (
      prev.open
        ? { ...prev, open: false, query: '', source: null, anchor: null }
        : prev
    ));
  }, []);

  const updateReferenceMentionPicker = useCallback((value, cursorPosition) => {
    if (!isReferenceMentionEnabled) {
      closeReferenceMentionPicker();
      return;
    }

    const activeMention = getActiveReferenceMention(value, cursorPosition);
    if (!activeMention) {
      closeReferenceMentionPicker();
      return;
    }

    setReferenceMentionState({
      open: true,
      query: activeMention.query,
      start: activeMention.start,
      end: activeMention.end,
      source: 'typing',
      anchor: getTextareaCaretMenuPosition(textareaRef.current, activeMention.end),
    });
  }, [closeReferenceMentionPicker, isReferenceMentionEnabled]);

  const insertReferenceMention = useCallback((option) => {
    const currentPrompt = String(watch('prompt') || '');
    const textarea = textareaRef.current;
    const fallbackStart = Number.isFinite(Number(textarea?.selectionStart))
      ? textarea.selectionStart
      : currentPrompt.length;
    const fallbackEnd = Number.isFinite(Number(textarea?.selectionEnd))
      ? textarea.selectionEnd
      : fallbackStart;
    const start = referenceMentionState.open ? referenceMentionState.start : fallbackStart;
    const end = referenceMentionState.open ? referenceMentionState.end : fallbackEnd;
    const prefix = currentPrompt.slice(0, start);
    const suffix = currentPrompt.slice(end);
    const needsLeadingSpace = prefix.length > 0 && !/\s$/.test(prefix);
    const needsTrailingSpace = suffix.length > 0 && !/^[\s.,;:!?)]/.test(suffix);
    const mentionText = `${needsLeadingSpace ? ' ' : ''}@${option.referenceKey}${needsTrailingSpace ? ' ' : ''}`;
    const nextPrompt = `${prefix}${mentionText}${suffix}`;
    const nextCursorPosition = prefix.length + mentionText.length;

    setValue('prompt', nextPrompt, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    closeReferenceMentionPicker();

    window.requestAnimationFrame(() => {
      const nextTextarea = textareaRef.current;
      if (nextTextarea) {
        nextTextarea.focus();
        nextTextarea.setSelectionRange(nextCursorPosition, nextCursorPosition);
      }
      adjustTextareaHeight();
    });
  }, [
    adjustTextareaHeight,
    closeReferenceMentionPicker,
    referenceMentionState.end,
    referenceMentionState.open,
    referenceMentionState.start,
    setValue,
    watch,
  ]);

  const handlePromptFieldChange = useCallback((event, onChange) => {
    onChange(event);
    updateReferenceMentionPicker(event.target.value, event.target.selectionStart);
    window.requestAnimationFrame(adjustTextareaHeight);
  }, [adjustTextareaHeight, updateReferenceMentionPicker]);

  const handlePromptFieldActivity = useCallback((event) => {
    updateReferenceMentionPicker(event.currentTarget.value, event.currentTarget.selectionStart);
  }, [updateReferenceMentionPicker]);

  const validateReferenceMentions = useCallback((promptText) => {
    if (!isReferenceMentionEnabled) {
      return true;
    }

    const invalidMentions = getInvalidReferenceMentions(promptText, availableReferenceMentionKeys);
    if (invalidMentions.length === 0) {
      return true;
    }

    const availableOptions = referenceMentionOptions
      .map((option) => `@${option.referenceKey}`)
      .join(', ');

    setInlineNotice({
      message: t('referenceMention.invalidTag', {
        tag: invalidMentions[0],
        options: availableOptions || t('referenceMention.noOptions'),
      }),
      variant: 'error',
      dismissible: true,
      source: 'referenceMention',
    });
    return false;
  }, [
    availableReferenceMentionKeys,
    isReferenceMentionEnabled,
    referenceMentionOptions,
    t,
  ]);

  // 监听提示词变化并设置hasPrompt
  useEffect(() => {
    setHasPrompt(!!prompt.trim());
  }, [prompt]);

  useEffect(() => {
    if (!isReferenceMentionEnabled) {
      closeReferenceMentionPicker();
    }
  }, [closeReferenceMentionPicker, isReferenceMentionEnabled]);

  useEffect(() => {
    clearInlineNotice();
  }, [
    clearInlineNotice,
    prompt,
    outputCount,
    selectedModel,
    creationType,
    functionMode,
    watchedSize,
  ]);

  useEffect(() => {
    // Revalidate edits without erasing a newly reported error on unchanged input.
    if (getFieldState('prompt').error) {
      void trigger('prompt');
      setIsPromptNoticeDismissed(false);
    }
  }, [getFieldState, prompt, trigger]);

  const hasGenerationInput = isVsrMode
    ? uploadedAssets.some((asset) => asset?.type === 'video' && asset?.status === 'complete' && asset?.url)
    : hasPrompt || uploadedAssets.length > 0;
  useEffect(() => {
    onInputStateChange?.({
      hasInput: hasGenerationInput,
      hasPrompt,
      assetCount: uploadedAssets.length,
      creationType,
      prompt: String(prompt || ''),
      assets: uploadedAssets.slice(0, 4).map((asset, index) => ({
        id: asset?.id || `compact-asset-${index}`,
        type: asset?.type || asset?.mediaType || 'image',
        mediaType: asset?.mediaType || asset?.type || 'image',
        url: asset?.url || asset?.preview || asset?.thumbnailUrl || '',
        preview: asset?.preview || asset?.thumbnailUrl || asset?.url || '',
        thumbnailUrl: asset?.thumbnailUrl || asset?.preview || null,
        name: asset?.name || '',
      })).filter((asset) => asset.url || asset.preview),
    });
  }, [creationType, hasGenerationInput, hasPrompt, onInputStateChange, prompt, uploadedAssets]);

  const isComingSoonModel = Boolean(selectedModelConfig?.comingSoon);
  const isGenerateDisabled = isButtonBusy || isComingSoonModel || !hasGenerationInput;
  const generateDisabledTooltip = isComingSoonModel
    ? t('comingSoonTooltip', { modelName: selectedModelConfig?.displayName || 'This model' })
    : t('generateDisabledTooltip');

  // 同步登录态，防止未登录直接生成
  useEffect(() => {
    let isMounted = true;
    const fetchSubscription = async () => {
      try {
        const response = await fetchCachedApi('/api/user/subscription', {
          cacheKey: CLIENT_CACHE_KEYS.subscription,
          ttlMs: 60000,
        });
        if (response.ok) {
          const data = response.data || {};
          setCurrentSubscription(data);

          // ✅ 解析套餐代码（用于限制前端选项）
          const planName = (data.planName || 'Free').toLowerCase();
          let nextPlanCode = 'free';
          if (planName.includes('ultra')) {
            nextPlanCode = 'ultra';
            setIsPaidPlan(true);
          } else if (planName.includes('max')) {
            nextPlanCode = 'max';
            setIsPaidPlan(true);
          } else if (planName.includes('pro')) {
            nextPlanCode = 'pro';
            setIsPaidPlan(true);
          } else if (planName.includes('plus')) {
            nextPlanCode = 'plus';
            setIsPaidPlan(true);
          } else {
            nextPlanCode = 'free';
            setIsPaidPlan(false);
          }

          setUserPlanCode(nextPlanCode);
          const effectiveCapabilities = data.effectiveEntitlements?.capabilities || null;
          const planForCapabilities = data.subscription?.plan || null;
          if (effectiveCapabilities) {
            setPlanCapabilities(effectiveCapabilities);
          } else if (planForCapabilities) {
            applyPlanCapabilities(planForCapabilities, { planKey: nextPlanCode });
          } else if (nextPlanCode === 'free') {
            await loadFreePlanCapabilities();
          } else {
            applyPlanCapabilities(null, { planKey: nextPlanCode });
          }

          const derivedPaid = nextPlanCode !== 'free';
          const required = typeof data.turnstileRequired === 'boolean'
            ? data.turnstileRequired
            : !derivedPaid;
          setTurnstileRequired(required);
        }
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
        setPlanCapabilities(null);
      }
    };

    const initSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (!isMounted) return;
        if (error) {
          console.error('Failed to get session:', error);
          setIsLoggedIn(false);
          setCurrentSubscription(null);
          setIsPaidPlan(false);
          setUserPlanCode('free');
          setPlanCapabilities(null);
          await loadFreePlanCapabilities();
          return;
        }
        setIsLoggedIn(!!data?.session);
        // 会话已就绪时拉取订阅，避免首屏 401 造成 plan 状态停留在 free
        if (data?.session) {
          await fetchSubscription();
        } else {
          setCurrentSubscription(null);
          setIsPaidPlan(false);
          setUserPlanCode('free');
          setPlanCapabilities(null);
          await loadFreePlanCapabilities();
        }
      } catch (authError) {
        if (isMounted) {
          console.error('Error initializing session:', authError);
          setIsLoggedIn(false);
          setCurrentSubscription(null);
          setIsPaidPlan(false);
          setUserPlanCode('free');
          setPlanCapabilities(null);
          loadFreePlanCapabilities().catch((planError) => {
            console.error('Failed to recover free plan capabilities after auth error:', planError);
          });
        }
      }
    };

    initSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (isMounted) {
        setIsLoggedIn(!!session);
        if (session) {
          setShowAuthModal(false);
          // 登录/会话变更后重新拉取订阅，确保前端权限及时更新
          fetchSubscription();
        } else {
          setCurrentSubscription(null);
          setIsPaidPlan(false);
          setUserPlanCode('free');
          setPlanCapabilities(null);
          setTurnstileRequired(true);
          loadFreePlanCapabilities().catch((error) => {
            console.error('Failed to load free plan capabilities after logout:', error);
          });
        }
      }
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, [applyPlanCapabilities, loadFreePlanCapabilities, supabase]);

  // 监听错误，积分不足时弹出充值弹窗
  // ✅ 优化：区分配置不支持和真正的积分不足
  useEffect(() => {
    if (error) {
      const normalizedError = error.toLowerCase();
      // 检查是否为配置不支持错误（每日免费积分限制）
      if (normalizedError.includes('daily free credits can only be used')) {
        // 显示配置提示，不弹出充值弹窗
        console.log('Configuration restriction detected - daily free credits limitation');
        // 错误消息已经很明确，不需要额外操作
      } else if (normalizedError.includes('insufficient credits')) {
        // 真正的积分不足：付费会员弹出充值，免费用户直接跳转价格页
        if (isPaidMember) {
          setShowInsufficientCreditsModal(true);
        } else {
          setShowInsufficientCreditsModal(false);
          setShowTopUpModal(false);
          router.push('/pricing');
        }
      }
    }
  }, [error, isPaidMember, router]);

  // 如果验证成功但没有显示验证组件，初始化token时间戳
  useEffect(() => {
    if (isTurnstileVerified && turnstileToken && tokenTimestamp === 0) {
      setTokenTimestamp(Date.now());
    }
  }, [isTurnstileVerified, turnstileToken, tokenTimestamp]);

  // 同步错误消息到父组件（page.js）的副作用已移除，改为在捕获错误时直接调用，
  // 并传递 taskKey 以避免 activeTaskKey 状态竞态问题

  // 监听从Gallery组件发送的recreateImage事件
  useEffect(() => {
    const handleRecreateImage = async (event) => {
      const detail = event?.detail || {};
      if (detail.targetGeneratorId && detail.targetGeneratorId !== generatorInstanceId) return;
      const prompt = detail.prompt || '';
      const rawAspect = detail.aspectRatio || detail.params?.aspect;
      const rawSize = detail.size || detail.params?.size;
      const rawDuration = detail.duration ?? detail.params?.duration;
      const rawModelName = detail.modelName || detail.params?.modelName;
      const normalizedAssets = normalizeRecreateAssets(detail);
      const rawFunctionMode = detail.functionMode || detail.params?.functionMode || null;
      const rawOutputCount = detail.outputCount ?? detail.params?.outputCount;
      const assetHydrationRequestId = recreateAssetHydrationRequestRef.current + 1;
      recreateAssetHydrationRequestRef.current = assetHydrationRequestId;

      // 1. 设置提示词
      setValue('prompt', prompt);
      setHasPrompt(Boolean(prompt.trim()));
      clearErrors('prompt');

      // 2. 设置模型
      const rawModelKey = detail.modelKey || detail.model || detail.params?.modelKey || detail.params?.model;
      const resolvedModelKey = resolveModelKey(rawModelKey || rawModelName || '');
      const resolvedModelConfig = resolvedModelKey ? getModelConfig(resolvedModelKey) : null;
      const targetCreationType = resolvedModelConfig?.type === 'image' ? 'image' : 'video';
      const targetModelKey = resolvedModelKey || selectedModel;
      if (!lockedCreationType && targetCreationType !== creationType) {
        setCreationType(targetCreationType);
      }
      if (lockedCreationType && targetCreationType !== lockedCreationType) return;
      let nextFunctionMode = functionMode;
      if (targetCreationType === 'video') {
        nextFunctionMode = resolveRecreateFunctionMode({
          rawFunctionMode,
          targetModelKey,
          normalizedAssets,
        });
      } else {
        const requestedImageMode = String(rawFunctionMode || 'general').trim().toLowerCase();
        nextFunctionMode = supportsImageFunctionMode({
          modelKey: targetModelKey,
          functionMode: requestedImageMode,
        })
          ? requestedImageMode
          : 'general';
      }
      if (nextFunctionMode !== functionMode) {
        setFunctionMode(nextFunctionMode);
      }
      if (resolvedModelKey) {
        setSelectedModel(resolvedModelKey);
      }

      const nextOutputCount = Number.isFinite(Number(rawOutputCount)) && OUTPUT_COUNT_OPTIONS.includes(Number(rawOutputCount))
        ? Number(rawOutputCount)
        : 1;
      setOutputCount(nextOutputCount);

      // 3. 解析尺寸/宽高比
      const durationValue = rawDuration ? Number(String(rawDuration).replace('s', '')) : null;
      recreateDurationOverrideRef.current = durationValue
        ? {
          modelKey: targetModelKey,
          durationId: `${durationValue}s`,
        }
        : null;
      const targetSize = rawSize || (rawAspect && rawAspect.includes('x') ? rawAspect : null);
      const rawResolutionLabel = detail.resolutionLabel || detail.params?.resolutionLabel || detail.resolution || detail.params?.resolution || null;

      const aspectFromSize = getAspectFromSize(targetSize, targetModelKey);
      const nextAspect = aspectFromSize || rawAspect;
      recreateAspectOverrideRef.current = nextAspect
        ? {
          modelKey: targetModelKey,
          aspectId: nextAspect,
        }
        : null;
      const recreateResolutionId = targetSize
        ? (
          rawResolutionLabel
            ? buildResolutionOptionId({
              aspectRatio: nextAspect || rawAspect || '',
              resolutionLabel: rawResolutionLabel,
              apiSize: targetSize,
            })
            : targetSize
        )
        : null;
      // Public Recipes can specify a resolution tier without a pixel size.
      // Keep that tier through mode/model changes just like an explicit size.
      recreateResolutionOverrideRef.current = targetSize || rawResolutionLabel
        ? {
          modelKey: targetModelKey,
          resolutionId: recreateResolutionId,
          apiSize: targetSize,
          resolutionLabel: rawResolutionLabel,
        }
        : null;

      // 还原图片质量级别（仅对支持 quality 选择的模型生效）
      // 使用 ref 覆盖保护，防止模型切换 useEffect 将值重置为默认值
      const rawImageQuality = detail.imageQuality || detail.params?.imageQuality;
      const targetCaps = getModelCapabilities(targetModelKey) || {};
      const targetAllowedQualities = targetCaps.allowedQualities || [];
      const background = detail.background || detail.params?.background || 'auto';
      setSelectedBackground(targetCaps.allowedBackgrounds?.includes(background) ? background : 'auto');
      if (targetAllowedQualities.length > 0 && rawImageQuality && targetAllowedQualities.includes(rawImageQuality)) {
        recreateQualityOverrideRef.current = { modelKey: targetModelKey, value: rawImageQuality };
        setSelectedQuality(rawImageQuality);
      } else if (targetAllowedQualities.length > 0) {
        const fallbackQuality = targetCaps.defaultQuality || targetAllowedQualities[0];
        recreateQualityOverrideRef.current = { modelKey: targetModelKey, value: fallbackQuality };
        setSelectedQuality(fallbackQuality);
      }

      // 还原内容审核级别（仅对支持 moderation 选择的模型生效）
      // 使用 ref 覆盖保护，防止模型切换 useEffect 将值重置为默认值
      const rawModeration = detail.moderation || detail.params?.moderation;
      const targetAllowedModerations = targetCaps.allowedModerations || [];
      if (targetAllowedModerations.length > 0 && rawModeration && targetAllowedModerations.includes(rawModeration)) {
        recreateModerationOverrideRef.current = { modelKey: targetModelKey, value: rawModeration };
        setSelectedModeration(rawModeration);
      } else if (targetAllowedModerations.length > 0) {
        const fallbackModeration = targetCaps.defaultModeration || targetAllowedModerations[0];
        recreateModerationOverrideRef.current = { modelKey: targetModelKey, value: fallbackModeration };
        setSelectedModeration(fallbackModeration);
      }

      if (nextAspect) {
        setValue('size', nextAspect);
      }

      if (recreateResolutionId) {
        setSelectedResolution(recreateResolutionId);
      }
      if (rawResolutionLabel) {
        setIntendedResolutionLabel(rawResolutionLabel);
      }

      if (durationValue) {
        setSelectedDuration(`${durationValue}s`);
      }

      const targetHasKlingOmniFeatureVideoReference =
        targetCreationType === 'video'
        && targetModelKey === 'kling-v3-omni'
        && (
          nextFunctionMode === 'extend'
          || (
            nextFunctionMode === 'reference'
            && normalizedAssets.some((asset) => asset?.type === 'video')
          )
        );
      const targetIsKlingOmniKeepAudioMode =
        targetCreationType === 'video'
        && targetModelKey === 'kling-v3-omni'
        && ['editing', 'extend'].includes(nextFunctionMode);
      const targetCanToggleGenerateAudio =
        targetCreationType === 'video'
        && SEEDANCE_20_AUDIO_VIDEO_MODELS.has(targetModelKey)
        && (!targetHasKlingOmniFeatureVideoReference || targetIsKlingOmniKeepAudioMode);
      const rawGenerateAudio = detail.generateAudio ?? detail.params?.generateAudio;
      const nextGenerateAudio = typeof rawGenerateAudio === 'boolean' ? rawGenerateAudio : true;
      recreateGenerateAudioOverrideRef.current = targetCanToggleGenerateAudio
        ? {
          modelKey: targetModelKey,
          value: nextGenerateAudio,
        }
        : null;
      setGenerateAudio(targetCanToggleGenerateAudio ? nextGenerateAudio : false);
      setContextIrEnabled(targetModelKey?.startsWith('minimax-h3') && (detail.contextIrEnabled ?? detail.params?.contextIrEnabled) === true && (detail.generationMode ?? detail.params?.generationMode) !== 'unlimited');

      const rawSeed = detail.seed ?? detail.params?.seed;
      const hasRequestedSeed = rawSeed !== undefined && rawSeed !== null && rawSeed !== '';
      const hasValidRequestedSeed = isValidVideoSeedForModel(rawSeed, targetModelKey);
      const targetCanUseSeed =
        targetCreationType === 'video' && (
          SEEDANCE_SEED_MODELS.has(targetModelKey) ||
          isWanVideoModel(targetModelKey) ||
          HAPPYHORSE_VIDEO_MODELS.has(targetModelKey) ||
          BOTGO_VIDEO_SEED_MODELS.has(targetModelKey) ||
          KIEAI_VEO31_EXTEND_MODEL_KEYS.has(targetModelKey)
        );
      const nextSeedValue =
        targetCanUseSeed && hasRequestedSeed && hasValidRequestedSeed
          ? Number(rawSeed)
          : createRandomSeed(targetModelKey);
      const nextSeedEnabled = targetCanUseSeed
        ? (hasRequestedSeed ? true : isSeedDefaultEnabledVideoModel(targetModelKey))
        : false;
      recreateSeedOverrideRef.current = targetCanUseSeed
        ? {
          modelKey: targetModelKey,
          value: nextSeedEnabled,
          seed: nextSeedValue,
        }
        : null;
      setSeed(nextSeedValue);
      setSeedEnabled(nextSeedEnabled);

      const requestedWebSearch = detail.webSearch === true || detail.params?.webSearch === true;
      const nextWebSearchEnabled =
        targetCreationType === 'video'
        && nextFunctionMode === 'general'
        && Boolean(targetCaps.supportsWebSearch)
        && normalizedAssets.length === 0
        && requestedWebSearch;
      recreateWebSearchOverrideRef.current = {
        modelKey: targetModelKey,
        value: nextWebSearchEnabled,
      };
      setWebSearchEnabled(nextWebSearchEnabled);

      const targetInputRules = getVideoInputRules(targetModelKey, {
        creationType: targetCreationType,
        functionMode: nextFunctionMode,
      });
      const targetCanUseFileInput = Boolean(
        targetInputRules?.allowedMediaTypes?.length &&
        !targetInputRules.unsupported
      );
      const targetMaxInputUploads = targetCanUseFileInput
        ? getMaxMediaUploadsForMode({
          modelName: targetModelKey,
          creationType: targetCreationType,
          functionMode: nextFunctionMode,
        })
        : 0;
      if (targetCanUseFileInput) {
        const trimmedAssets = filterRecreateAssetsForInputRules(
          normalizedAssets,
          targetInputRules,
          targetMaxInputUploads
        );
        const toUploadedAsset = (asset, index) => ({
          id: asset.id || `recreate-${Date.now()}-${index}`,
          url: asset.url,
          preview: asset.preview || asset.thumbnailUrl || asset.url,
          type: asset.type || 'image',
          status: 'complete',
          name: asset.name || null,
          mimeType: asset.mimeType || null,
          width: asset.width || null,
          height: asset.height || null,
          durationSeconds: asset.durationSeconds || null,
          fps: asset.fps || null,
          fileSizeBytes: asset.fileSizeBytes || null,
        });

        // First paint the selected Explore media immediately. Remote metadata
        // is useful for validation, but it must not block opening the generator.
        setUploadedAssets(trimmedAssets.map(toUploadedAsset));
        void Promise.all(
          trimmedAssets.map((asset) => hydrateRecreatedAssetMetadataRef.current(asset))
        ).then((hydratedAssets) => {
          if (recreateAssetHydrationRequestRef.current !== assetHydrationRequestId) return;
          setUploadedAssets(hydratedAssets.map(toUploadedAsset));
        });
      } else {
        setUploadedAssets([]);
      }

      // A blank prompt is expected for Remix/Edit/Extend. Do not surface a
      // validation error until the user actually submits the generator.
      if (prompt.trim()) await trigger('prompt');

    };

    // 添加事件监听器
    document.addEventListener('recreateImage', handleRecreateImage);

    // 清理函数
    return () => {
      document.removeEventListener('recreateImage', handleRecreateImage);
    };
  }, [
    setValue,
    setSelectedModel,
    setSelectedResolution,
    setSelectedDuration,
    setUploadedAssets,
    setCreationType,
    setFunctionMode,
    trigger,
    clearErrors,
    aspectOptions,
    maxInputUploads,
    creationType,
    lockedCreationType,
    functionMode,
    selectedModel,
    selectedModelConfig,
    canUseFileInput,
    generatorInstanceId,
  ]);

  const buildGenerationPreflightPayload = useCallback((requestedOutputs = effectiveOutputCount) => ({
    resourceType: selectedModelConfig?.type === 'image' ? 'image' : 'video',
    requestedOutputs: resolveEffectiveOutputCount(requestedOutputs, generationModeRef.current),
    modelKey: selectedModel,
    generationMode: generationModeRef.current,
    expectedCredits: creditsPerOutput,
    resolution: selectedResolutionSize,
    resolutionLabel: selectedResolutionLabel,
    aspectRatioLabel: selectedAspectRatio,
    duration: selectedDurationValue,
    inputMode: selectedInputMode,
    functionMode,
    imageQuality: selectedQuality,
    background: selectedBackground,
    generateAudio: canToggleGenerateAudio && generateAudio,
    contextIrEnabled,
    hasVideoReference: hasKlingOmniVideoReferencePricing || (selectedModel === 'kling-v4' && hasVideoReferenceInput) || (selectedModel.startsWith('gemini-omni') && hasVideoReferenceInput),
    inputVideoDuration: inputVideoDurationSeconds,
    inputImageCount,
    privacyUnrestricted,
    fps: isVsrMode ? selectedFps : undefined,
  }), [
    creditsPerOutput,
    canToggleGenerateAudio,
    functionMode,
    generateAudio,
    contextIrEnabled,
    hasKlingOmniVideoReferencePricing,
    hasVideoReferenceInput,
    inputVideoDurationSeconds,
    inputImageCount,
    privacyUnrestricted,
    isVsrMode,
    selectedFps,
    effectiveOutputCount,
    selectedAspectRatio,
    selectedDurationValue,
    selectedInputMode,
    selectedModel,
    selectedModelConfig?.type,
    selectedQuality,
    selectedBackground,
    selectedResolutionLabel,
    selectedResolutionSize,
  ]);

  const checkGenerationPreflight = useCallback(async (requestedOutputs = effectiveOutputCount) => {
    const requestPayload = buildGenerationPreflightPayload(requestedOutputs);
    const cacheKey = JSON.stringify(requestPayload);
    const cachedResult = generationPreflightCacheRef.current;

    // The subscription response already contains the user's effective
    // entitlements (including overrides). Reject an excluded model immediately
    // so Free users see the upgrade prompt before Turnstile is mounted.
    if (
      !isModelAllowedForPlan({ modelKey: selectedModel, planKey: userPlanCode, capabilities: planCapabilities })
    ) {
      const planDisplayName = currentSubscription?.planName
        || (userPlanCode === 'free' ? 'Free' : userPlanCode);
      const modelDisplayName = selectedModelConfig?.displayName || selectedModel;
      const description = t('upgradeModal.modelNotIncluded', {
        plan: planDisplayName,
        model: modelDisplayName,
      });

      generationPreflightCacheRef.current = null;
      setErrorMessage(description);
      setUpgradeModalDetail(description);
      setUpgradeModalVariant('default');
      setShowUpgradeModal(true);
      return false;
    }

    if (
      cachedResult?.key === cacheKey
      && cachedResult.allowed
      && Date.now() - cachedResult.checkedAt < 8000
    ) {
      return true;
    }

    const payload = await runGenerationPreflight(requestPayload);
    if (payload?.softFailed) {
      console.warn('[GenerationStudio] Generation preflight soft-failed; formal API validation remains authoritative', {
        reason: payload.reason,
      });
      return true;
    }

    if (payload?.allowed !== false) {
      generationPreflightCacheRef.current = {
        key: cacheKey,
        allowed: true,
        checkedAt: Date.now(),
      };
      return true;
    }

    generationPreflightCacheRef.current = null;
    const modelDisplayName = selectedModelConfig?.displayName || selectedModel;
    const planDisplayName = currentSubscription?.planName
      || (userPlanCode === 'free' ? 'Free' : userPlanCode);
    let description = payload.message || t('upgradeModal.description');
    if (payload.reason === 'model_not_in_plan') {
      description = t('upgradeModal.modelNotIncluded', {
        plan: planDisplayName,
        model: modelDisplayName,
      });
    } else if (payload.reason === 'unlimited_config_mismatch') {
      description = t(isPaidMember
        ? 'generationMode.paidLockedFooter'
        : 'generationMode.freeFooter');
    } else if (payload.reason === 'insufficient_credits') {
      description = tErrors('insufficient_credits');
    } else if (payload.reason === 'credit_price_changed') {
      description = tErrors('credit_price_changed');
    }
    setErrorMessage(description);

    if (payload.reason === 'auth_required') {
      setShowAuthModal(true);
      return false;
    }

    if (payload.reason === 'concurrency_full') {
      showToast({
        title: t('concurrency.title'),
        description: buildConcurrencyMessage(t, {
          message: description,
          activeTaskCount: payload.activeTaskCount,
          maxConcurrency: payload.maxConcurrency,
          remainingSlots: payload.remainingSlots,
        }),
        type: 'warning',
      });
      return false;
    }

    if (payload.reason === 'insufficient_credits') {
      if (isPaidMember) {
        setShowInsufficientCreditsModal(true);
      } else {
        setUpgradeModalDetail(description);
        setUpgradeModalVariant('default');
        setShowUpgradeModal(true);
      }
      return false;
    }

    if (payload.reason === 'model_not_in_plan' || payload.reason === 'unlimited_config_mismatch') {
      if (payload.reason === 'unlimited_config_mismatch') {
        updateGenerationMode('credit');
      }
      setUpgradeModalDetail(description);
      setUpgradeModalVariant('default');
      setShowUpgradeModal(true);
      return false;
    }

    showToast({
      title: t('toasts.genFailed'),
      description,
      type: 'warning',
    });
    return false;
  }, [
    buildGenerationPreflightPayload,
    currentSubscription?.planName,
    effectiveOutputCount,
    planCapabilities,
    selectedModel,
    selectedModelConfig?.displayName,
    showToast,
    t,
    tErrors,
    updateGenerationMode,
    userPlanCode,
  ]);

  // 处理生成图片的提交
  const onSubmit = async (data) => {
    try {
      setErrorMessage('');
      clearInlineNotice();

      if (selectedModelConfig?.comingSoon) {
        showToast({
          title: 'Coming soon',
          description: `${selectedModelConfig.displayName} is not available yet.`,
          type: 'info',
        });
        return;
      }

      // 检查是否有提示词
      if (!data.prompt.trim()) {
        return;
      }

      if (!validateReferenceMentions(data.prompt)) {
        return;
      }

      const submitOutputCount = resolveEffectiveOutputCount(outputCount, generationModeRef.current);
      if (turnstileRequired && submitOutputCount > 1) {
        showToast({
          title: t('moreMenu.outputNumberLimitTitle'),
          description: t('moreMenu.outputNumberLimitDescription'),
          type: 'warning'
        });
        return;
      }

      // 付费用户跳过 Turnstile，免费需验证 token
      if (turnstileRequired && !isTokenValid()) {
        console.log('Token invalid, reverification needed');
        // 使用固定居中的浮层，避免受页面当前滚动位置影响
        setTurnstileDisplay('overlay');
        remountTurnstileWidget();
        setShowTurnstileWidget(true);
        return;
      }

      // 检查请求频率
      if (!checkRequestRate()) {
        setErrorMessage(t('errors.tooManyRequests'));
        return;
      }

      // 更新token使用计数
      setTokenUsageCount(prev => prev + 1);

      const currentToken = turnstileToken;
      await runGenerationBatch({
        promptText: data.prompt,
        tokenOverride: currentToken,
      });
    } catch (error) {
      const rawErrorMessage = getErrorMessageText(error) || 'Error generating image';
      const displayErrorMessage = localizeKnownErrorMessage(error);
      const errorKey = getErrorKey(error);
      const errorValues = getErrorValues(error) || error?.details?.values || null;
      setErrorMessage(displayErrorMessage);

      if (errorKey === 'generation_rate_limit') {
        showToast({
          title: t('rateLimit.title'),
          description: buildGenerationRateLimitMessage(rawErrorMessage || displayErrorMessage, t),
          type: 'warning',
        });

        if (onGenerationError) {
          onGenerationError(error || rawErrorMessage, activeTaskKey);
        }
        return;
      }

      if (errorKey === 'plan_concurrency_limit' || String(rawErrorMessage || '').toLowerCase().includes('concurrent tasks')) {
        showToast({
          title: t('concurrency.title'),
          description: buildConcurrencyMessage(t, {
            message: rawErrorMessage || displayErrorMessage,
            activeTaskCount: errorValues?.activeTaskCount,
            maxConcurrency: errorValues?.maxConcurrency || errorValues?.count,
            remainingSlots: errorValues?.remainingSlots,
          }),
          type: 'warning',
        });

        if (onGenerationError) {
          onGenerationError(error || rawErrorMessage, activeTaskKey);
        }
        return;
      }

      if (isTurnstileReverifyError(rawErrorMessage) || isUpgradeRequiredError(error)) {
        console.warn('Generation handled error:', rawErrorMessage);
      } else {
        console.error('Generation error:', error);
      }

      showToast({
        title: t('toasts.genFailed'),
        description: displayErrorMessage,
        type: 'error'
      });

      if (onGenerationError) {
        onGenerationError(error || rawErrorMessage, activeTaskKey);
      }
    }
  };

  const resolveGenerationModeForSubmit = useCallback((requestedMode) => {
    if (requestedMode === 'unlimited') {
      return canSelectUnlimitedMode ? 'unlimited' : 'credit';
    }

    if (requestedMode === 'credit') {
      return 'credit';
    }

    return generationModeRef.current === 'unlimited' && canSelectUnlimitedMode
      ? 'unlimited'
      : 'credit';
  }, [canSelectUnlimitedMode]);

  // 处理Generate按钮点击 - 修改handleGenerateClick函数，每次都强制刷新token
  const handleGenerateClick = async (requestedMode = null) => {
    if (creationType === 'video') {
      const flashImageError = uploadedAssets
        .map((asset) => validateBotgoVideo25FlashInputAsset(asset, { modelName: selectedModel }))
        .find(Boolean);
      if (flashImageError) {
        setInlineNotice({
          message: t(`errors.${flashImageError.errorKey}`, flashImageError.values),
          variant: 'error',
          dismissible: true,
          source: 'inputImage',
        });
        return;
      }
    }
    if (creationType === 'video' && selectedModel === 'botgo-video-2.5') {
      const validationError = validateBotgoVideo25Inputs(uploadedAssets, { functionMode });
      if (validationError) { setErrorMessage(t(`errors.${validationError.errorKey}`, validationError.values)); return; }
    }
    if (creationType === 'video' && selectedModel === 'kling-v4') {
      const validationError = validateKlingV4Inputs(uploadedAssets, { functionMode, duration: currentDurationObj?.value });
      if (validationError) { setErrorMessage(t(`errors.${validationError.errorKey}`, validationError.values)); return; }
    }
    if (creationType === 'video' && isWanVideoModel(selectedModel)) {
      const validationError = validateWanVideoInputs(uploadedAssets, { duration: currentDurationObj?.value });
      if (validationError) { setErrorMessage(validationError.message); return; }
    }
    const nextMode = resolveGenerationModeForSubmit(requestedMode);
    const submitOutputCount = resolveEffectiveOutputCount(outputCount, nextMode);
    if (nextMode === 'unlimited' && outputCount !== 1) {
      setOutputCount(1);
    }
    updateGenerationMode(nextMode);

    // 清除所有错误消息
    setErrorMessage('');
    clearInlineNotice();
    setIsPromptNoticeDismissed(false);
    pendingRemixRef.current = null;
    setTurnstileDisplay('overlay');

    // 如果验证或生成处理正在进行中，直接返回
    if (isProcessingVerification || isLoading || isBatchGenerating) {
      console.log('Verification or generation in progress, ignoring click');
      return;
    }

    // 获取当前提示词
    const currentPrompt = watch('prompt');
    const isPromptValid = await trigger('prompt');

    if (!isPromptValid || !currentPrompt.trim()) {
      return;
    }

    const promptLengthError = getPromptLengthError({ modelKey: selectedModel, prompt: currentPrompt });
    if (promptLengthError) {
      setInlineNotice({
        message: t(`errors.${promptLengthError.errorKey}`, promptLengthError.values),
        variant: 'error',
        dismissible: true,
        source: 'promptLength',
      });
      return;
    }

    if (!validateReferenceMentions(currentPrompt)) {
      return;
    }

    if (turnstileRequired && submitOutputCount > 1) {
      showToast({
        title: t('moreMenu.outputNumberLimitTitle'),
        description: t('moreMenu.outputNumberLimitDescription'),
        type: 'warning'
      });
      return;
    }

    // 记录点击生成事件（需在所有分支 return 前上报，避免漏记）
    sendGAEvent('click_generate', {
      model_key: selectedModel,
      creation_type: creationType,
    });

    // 进入处理，避免重复点击
    setIsProcessingVerification(true);

    // 登录校验：未登录直接跳转 Pricing
    if (!isLoggedIn) {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Failed to fetch session before generate:', error);
          setIsProcessingVerification(false);
          setShowAuthModal(true);
          return;
        }
        if (!data?.session) {
          setIsProcessingVerification(false);
          setShowAuthModal(true);
          return;
        }
        setIsLoggedIn(true);
      } catch (authError) {
        console.error('Error checking session before generate:', authError);
        setIsProcessingVerification(false);
        setShowAuthModal(true);
        return;
      }
    }

    const preflightAllowed = await checkGenerationPreflight(submitOutputCount);
    if (!preflightAllowed) {
      setIsProcessingVerification(false);
      return;
    }

    // 付费用户跳过 Turnstile，直接提交
    if (!turnstileRequired) {
      setIsTurnstileVerified(true);
      setShowTurnstileWidget(false);
      await handleSubmit(onSubmit)();
      setIsProcessingVerification(false);
      return;
    }

    console.log('Generate button clicked, forcing token refresh');

    // 无论token是否有效，都强制刷新
    // 重置token相关的所有状态
    setTurnstileToken('');
    setIsTurnstileVerified(false);
    setTokenTimestamp(0);
    setTokenUsageCount(0);

    // 确保不显示错误消息
    setErrorMessage('');

    // 尝试清理现有Turnstile组件
    if (turnstileRef.current) {
      try {
        console.log('Destroying existing Turnstile component');
        turnstileRef.current.remove();
      } catch (error) {
        console.error('Failed to destroy Turnstile component:', error);
      }
    }

    // 完全隐藏并重新显示组件
    setShowTurnstileWidget(false);
    remountTurnstileWidget();

    // 增加延迟确保DOM更新完成
    setTimeout(() => {
      // 确保当前不在处理验证时才显示验证组件
      if (!verificationInProgressRef.current) {
        console.log('Redisplaying Turnstile verification component');
        setShowTurnstileWidget(true);
      }
    }, 120);
  };

  const handleGenerationModeChange = useCallback((nextMode) => {
    if (nextMode === 'unlimited' && !canSelectUnlimitedMode) {
      return;
    }
    if (nextMode === 'unlimited') {
      setOutputCount(1);
    }
    updateGenerationMode(nextMode);
  }, [canSelectUnlimitedMode, updateGenerationMode]);

  const handleGenerationModeUpgrade = useCallback(() => {
    setIsPricingModalOpen(true);
  }, []);

  const handleGenerationModeFallbackOpenChange = useCallback((nextOpen) => {
    if (!nextOpen) {
      setGenerationModeFallback(null);
    }
  }, []);

  const handleGenerationModeFallbackTryLater = useCallback(() => {
    setGenerationModeFallback(null);
  }, []);

  const handleGenerationModeFallbackPrimaryAction = async () => {
    const fallback = generationModeFallback;
    setGenerationModeFallback(null);
    updateGenerationMode('credit');
    await Promise.resolve();

    const retryPrompt = String(fallback?.retryPrompt || '').trim();
    const retryParams = fallback?.retryParams && typeof fallback.retryParams === 'object'
      ? {
          ...fallback.retryParams,
          generationMode: 'credit',
        }
      : null;

    if (retryPrompt && retryParams && !turnstileRequired) {
      const visibilityOverride = consumePublicVisibilityOverride();
      await executeSingleGeneration({
        promptText: retryPrompt,
        params: retryParams,
        visibilityOverride,
      });
      return;
    }

    await handleGenerateClick('credit');
  };

  // 检查token是否有效
  const isTokenValid = useCallback(() => {
    if (!turnstileRequired) return true;
    if (!turnstileToken) return false;

    // 检查时间有效性
    const now = Date.now();
    const elapsedMinutes = (now - tokenTimestamp) / (1000 * 60);

    return elapsedMinutes < TOKEN_VALIDITY_MINUTES;
  }, [turnstileToken, tokenTimestamp, TOKEN_VALIDITY_MINUTES, turnstileRequired]);

  // 检查请求频率是否超限
  const checkRequestRate = useCallback(() => {
    const now = Date.now();
    const oneMinuteAgo = now - 60 * 1000;

    // 如果上次请求是在一分钟之前，重置计数
    if (lastRequestTime < oneMinuteAgo) {
      setRequestsInLastMinute(1);
      setLastRequestTime(now);
      return true;
    }

    // 检查是否超过限制
    if (requestsInLastMinute >= MAX_REQUESTS_PER_MINUTE) {
      return false;
    }

    // 增加计数
    setRequestsInLastMinute(prev => prev + 1);
    setLastRequestTime(now);
    return true;
  }, [lastRequestTime, requestsInLastMinute, MAX_REQUESTS_PER_MINUTE]);

  // 统一处理“任务创建成功但状态轮询暂时没跟上”的场景：停转圈、提示延迟、继续保留任务轮询。
  const handlePollingFailed = useCallback(
    ({ message, taskId, returnedAdditionalInfo }) => {
      const rawFriendlyMessage =
        message ||
        `${t('toasts.genDelayed')} (Task ID: ${taskId || 'unknown'})`;
      const displayFriendlyMessage = localizeKnownErrorMessage(rawFriendlyMessage);

      setErrorMessage(displayFriendlyMessage);

      // 全局 Toast 提示任务状态延迟
      showToast({
        title: t('toasts.genDelayed'),
        description: displayFriendlyMessage,
        type: 'warning'
      });

      // 通知父组件，确保页面层能停止 loading，并让任务卡片继续保持轮询。
      if (handleGenerationComplete) {
        handleGenerationComplete(null, {
          ...(returnedAdditionalInfo || {}),
          taskPollingFailed: true,
          taskId,
          message: rawFriendlyMessage
        });
      }

      // 清理 token 和验证组件，允许后续重试
      resetTurnstileVerificationState();
      cleanupTurnstileWidget('polling failed', { logSuccess: true });
    },
    [
      cleanupTurnstileWidget,
      handleGenerationComplete,
      localizeKnownErrorMessage,
      resetTurnstileVerificationState,
      showToast,
      t,
    ]
  );

  const runRemixGeneration = useCallback(async ({
    prompt,
    taskId,
    size,
    duration,
    modelName,
    modelKey,
    functionMode = 'editing',
    aspectRatioLabel,
    resolutionLabel,
    generationMode,
    expectedCredits,
    outputCount,
    generateAudio,
    contextIrEnabled,
    seed,
    webSearch,
    inputAssets,
    tokenOverride,
  }) => {
    let taskKey;
    try {
      setErrorMessage('');
      if (!verificationInProgressRef.current) {
        verificationInProgressRef.current = true;
        setIsProcessingVerification(true);
      }

      const trimmedPrompt = (prompt || '').trim();
      if (!trimmedPrompt) {
        setErrorMessage(t('errors.promptRequired')); // Use existing prompt required error or generic
        return;
      }

      if (!checkRequestRate()) {
        setErrorMessage(t('errors.tooManyRequests'));
        return;
      }

      setTokenUsageCount(prev => prev + 1);

      const normalizedFunctionMode = String(functionMode || 'editing').trim().toLowerCase();
      const isExtendAction = normalizedFunctionMode === 'extend';
      const resolvedActionModelKey = resolveModelKey(modelKey || modelName || '') || selectedModel;
      const resolvedActionModelConfig = getModelConfig(resolvedActionModelKey) || selectedModelConfig;
      const actionInputAssets = Array.isArray(inputAssets) ? inputAssets : [];
      const remixRequest = isExtendAction
        ? {
          params: {
            prompt: trimmedPrompt,
            size,
            aspectRatioLabel,
            resolutionLabel,
            modelName: resolvedActionModelConfig?.displayName || modelName || resolvedActionModelKey,
            modelKey: resolvedActionModelKey,
            duration: duration || currentDurationObj?.value,
            functionMode: 'extend',
            inputAssets: actionInputAssets,
            generationMode,
            outputCount: outputCount || 1,
            generateAudio,
            contextIrEnabled,
            seed,
            webSearch,
            turnstileToken: tokenOverride || turnstileToken,
          },
        }
        : buildRemixGenerationParams({
          prompt: trimmedPrompt,
          taskId,
          size,
          duration,
          modelName,
          modelKey,
          functionMode: normalizedFunctionMode === 'reference' ? 'reference' : 'editing',
          aspectRatioLabel,
          resolutionLabel,
          generationMode,
          outputCount,
          generateAudio,
          contextIrEnabled,
          seed,
          webSearch,
          inputAssets: actionInputAssets,
          tokenOverride,
          currentQualityOptions,
          selectedResolution,
          selectedModel,
          selectedModelConfig,
          currentDurationValue: currentDurationObj?.value,
          turnstileToken,
        });
      const params = remixRequest?.params;
      if (!params) {
        return;
      }
      params.expectedCredits = expectedCredits;
      params.creationSessionId = creationSessionId || undefined;
      params.creationEntryPoint = creationEntryPoint || undefined;

      taskKey =
        (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `task-${Date.now()}-${Math.random()}`;
      params.clientTaskKey = taskKey;
      setActiveTaskKey(taskKey);

      setShowTurnstileWidget(false);

      const visibilityOverride = consumePublicVisibilityOverride();
      const result = await runGenerationWithVisibility(params, visibilityOverride);

      const {
        assetUrl,
        taskQueued,
        taskSubmitted,
        taskPollingFailed,
        taskId: returnedTaskId,
        previewUrl: resultPreviewUrl,
        message,
        note,
        additionalInfo: returnedAdditionalInfo
      } = result || {};
      const generatedAssetUrl = assetUrl;

      if (taskQueued || taskSubmitted) {
        if (onGenerationStart) {
          onGenerationStart({ taskKey, prompt: trimmedPrompt, params });
        }

        if (handleGenerationComplete) {
          handleGenerationComplete(null, {
            ...returnedAdditionalInfo,
            taskKey,
            prompt: trimmedPrompt,
            params,
            taskQueued: Boolean(taskQueued),
            taskSubmitted: true,
            previewUrl: resultPreviewUrl,
            message,
            note
          });
        }

        resetTurnstileVerificationState();
        cleanupTurnstileWidget('remix submit');
        return;
      }

      if (taskPollingFailed) {
        if (onGenerationStart) {
          onGenerationStart({ taskKey, prompt: trimmedPrompt, params });
        }

        handlePollingFailed({
          message,
          taskId: returnedTaskId,
          returnedAdditionalInfo: { ...returnedAdditionalInfo, taskKey, prompt: trimmedPrompt, params }
        });
        return;
      }

      if (generatedAssetUrl && handleGenerationComplete) {
        if (onGenerationStart) {
          onGenerationStart({ taskKey, prompt: trimmedPrompt, params });
        }

        handleGenerationComplete(generatedAssetUrl, { ...returnedAdditionalInfo, taskKey, prompt: trimmedPrompt, params });
        resetTurnstileVerificationState();
        cleanupTurnstileWidget('remix success');
      }
    } catch (error) {
      const fallbackMessage = getErrorMessageText(error) || t('toasts.remixFailed');
      const isHandledRemixError =
        (Number(error?.status) >= 400 && Number(error?.status) < 500)
        || isInputVideoDurationError(error);
      if (isHandledRemixError) {
        console.warn('Remix handled error:', fallbackMessage);
      } else {
        console.error('Remix generation error:', error);
      }
      const displayFallbackMessage = isInputVideoDurationError(error)
        ? fallbackMessage
        : localizeKnownErrorMessage(error);

      if (isTurnstileReverifyError(fallbackMessage)) {
        resetTurnstileVerificationState({
          showWidget: true,
          display: 'overlay',
        });
        remountTurnstileWidget();

        if (onGenerationError) {
          onGenerationError(TURNSTILE_REVERIFY_SIGNAL, taskKey);
        }
        return;
      }

      setErrorMessage(displayFallbackMessage);

      showToast({
        title: t('toasts.remixFailed'),
        description: displayFallbackMessage,
        type: 'error'
      });

      if (onGenerationError && !isHandledRemixError) {
        onGenerationError(error || fallbackMessage, taskKey);
      }
    } finally {
      verificationInProgressRef.current = false;
      setIsProcessingVerification(false);
    }
  }, [
    cleanupTurnstileWidget,
    checkRequestRate,
    consumePublicVisibilityOverride,
    creationEntryPoint,
    creationSessionId,
    currentDurationObj,
    currentQualityOptions,
    handleGenerationComplete,
    localizeKnownErrorMessage,
    onGenerationError,
    onGenerationStart,
    runGenerationWithVisibility,
    remountTurnstileWidget,
    selectedModel,
    selectedModelConfig,
    selectedResolution,
    showToast,
    resetTurnstileVerificationState,
    t,
    turnstileToken,
    handlePollingFailed,
    isTurnstileReverifyError,
  ]);

  useEffect(() => {
    const handleRemixVideo = async (event) => {
      const detail = event?.detail || {};
      if (detail.targetGeneratorId && detail.targetGeneratorId !== generatorInstanceId) return;
      const remixPrompt = detail.prompt || '';
      const remixTaskId = detail.taskId || detail.task_id || detail.remixTaskId;
      const detailInputAssets = Array.isArray(detail.inputAssets) ? detail.inputAssets : [];
      const rawRequestedFunctionMode = String(detail.functionMode || (detail.action === 'extend' ? 'extend' : 'editing')).trim().toLowerCase();
      const requestedFunctionMode = ['reference', 'editing', 'extend'].includes(rawRequestedFunctionMode)
        ? rawRequestedFunctionMode
        : 'editing';
      const isExtendAction = requestedFunctionMode === 'extend';
      const requestedGenerationMode =
        detail.generationMode === 'unlimited'
          ? 'unlimited'
          : 'credit';
      const settleRemixDispatch = () => {
        if (typeof detail.onSettled === 'function') {
          detail.onSettled();
        }
      };

      if (!isExtendAction && !remixTaskId && detailInputAssets.length === 0) {
        showToast({
          title: t('toasts.remixUnavailable'),
          description: requestedFunctionMode === 'reference'
            ? 'Missing source media for Reference to Video.'
            : 'Missing source video for editing.',
          type: 'error'
        });
        settleRemixDispatch();
        return;
      }

      if (isExtendAction && detailInputAssets.length === 0) {
        showToast({
          title: t('toasts.remixUnavailable'),
          description: 'Missing source video for extend.',
          type: 'error'
        });
        settleRemixDispatch();
        return;
      }

      const payload = {
        prompt: remixPrompt,
        taskId: remixTaskId,
        size: detail.size,
        duration: detail.duration,
        modelName: detail.modelName,
        modelKey: detail.modelKey,
        functionMode: requestedFunctionMode,
        aspectRatioLabel: detail.aspectRatioLabel,
        resolutionLabel: detail.resolutionLabel,
        generationMode: requestedGenerationMode,
        expectedCredits: detail.expectedCredits,
        outputCount: detail.outputCount,
        generateAudio: detail.generateAudio,
        seed: detail.seed,
        webSearch: detail.webSearch,
        inputAssets: detailInputAssets,
      };

      pendingRemixRef.current = payload;

      if (isProcessingVerification || isLoading) {
        pendingRemixRef.current = null;
        showToast({
          title: t('toasts.pleaseWait'),
          description: t('toasts.requestInProgress'),
          type: 'warning'
        });
        settleRemixDispatch();
        return;
      }

      if (!isLoggedIn) {
        try {
          const { data, error } = await supabase.auth.getSession();
          if (error) {
            console.error('Failed to fetch session before remix:', error);
            setShowAuthModal(true);
            pendingRemixRef.current = null;
            settleRemixDispatch();
            return;
          }
          if (!data?.session) {
            setShowAuthModal(true);
            pendingRemixRef.current = null;
            settleRemixDispatch();
            return;
          }
          setIsLoggedIn(true);
        } catch (authError) {
          console.error('Error checking session before remix:', authError);
          setShowAuthModal(true);
          pendingRemixRef.current = null;
          settleRemixDispatch();
          return;
        }
      }

      if (!turnstileRequired || isTokenValid()) {
        pendingRemixRef.current = null;
        await runRemixGeneration(payload);
        settleRemixDispatch();
        return;
      }

      setTurnstileDisplay('overlay');
      remountTurnstileWidget();
      setShowTurnstileWidget(true);
      settleRemixDispatch();
    };

    document.addEventListener('remixVideo', handleRemixVideo);
    return () => {
      document.removeEventListener('remixVideo', handleRemixVideo);
    };
  }, [generatorInstanceId, isProcessingVerification, isLoading, isLoggedIn, turnstileRequired, isTokenValid, remountTurnstileWidget, runRemixGeneration, showToast, supabase, t]);

  useEffect(() => {
    setSharedStateReady(false);
    const hydrate = (state) => {
      if (!state || state.sourceId === generatorInstanceId) return;
      const updatedAt = Number(state.updatedAt || 0);
      if (updatedAt && updatedAt <= sharedStateUpdatedAtRef.current) return;
      sharedStateUpdatedAtRef.current = updatedAt;
      sharedStateSuppressUntilRef.current = Date.now() + 900;
      const detail = toGeneratorHydrationDetail(state, generatorInstanceId);
      if (!detail) return;
      document.dispatchEvent(new CustomEvent('recreateImage', { detail }));
    };

    const storedState = readSharedGeneratorState();
    // Explicit launch presets must win over the previous page’s cached model/specs.
    if (storedState && !preferDefaultPreset) hydrate(storedState);

    const handleSharedState = (event) => hydrate(event.detail);
    window.addEventListener(SHARED_GENERATOR_STATE_EVENT, handleSharedState);
    const readyTimer = window.setTimeout(() => {
      setSharedStateReady(true);
    }, storedState && !preferDefaultPreset ? 950 : 0);

    return () => {
      window.clearTimeout(readyTimer);
      window.removeEventListener(SHARED_GENERATOR_STATE_EVENT, handleSharedState);
    };
  }, [generatorInstanceId, preferDefaultPreset]);

  useEffect(() => {
    if (!sharedStateReady || Date.now() < sharedStateSuppressUntilRef.current) return undefined;
    const timer = window.setTimeout(() => {
      const state = writeSharedGeneratorState({
        sourceId: generatorInstanceId,
        prompt: watchedPrompt,
        creationType,
        mediaType: creationType,
        modelKey: selectedModel,
        modelName: selectedModel,
        functionMode,
        aspect: watchedSize,
        aspectRatio: watchedSize,
        size: selectedResolutionSize,
        resolutionLabel: selectedResolutionLabel,
        duration: Number(String(selectedDuration || '').replace('s', '')) || null,
        outputCount,
        generationMode: selectedGenerationMode,
        generateAudio,
        imageQuality: selectedQuality,
        background: selectedBackground,
        moderation: selectedModeration,
        contextIrEnabled,
        seed: seedEnabled ? seed : null,
        webSearch: webSearchEnabled,
        inputAssets: uploadedAssets,
      });
      if (state?.updatedAt) sharedStateUpdatedAtRef.current = state.updatedAt;
    }, 140);
    return () => window.clearTimeout(timer);
  }, [
    creationType,
    functionMode,
    generateAudio,
    contextIrEnabled,
    generatorInstanceId,
    outputCount,
    seed,
    seedEnabled,
    selectedDuration,
    selectedGenerationMode,
    selectedModel,
    selectedQuality,
    selectedBackground,
    selectedModeration,
    selectedResolutionLabel,
    selectedResolutionSize,
    sharedStateReady,
    uploadedAssets,
    watchedPrompt,
    watchedSize,
    webSearchEnabled,
  ]);

  // 处理Turnstile验证成功 - 使用防抖
  const handleTurnstileVerifyDebounced = useCallback(
    debounce((token) => {
      // 检查是否已经在处理验证
      if (verificationInProgressRef.current) {
        console.log('Verification already in progress, ignoring duplicate callback');
        return;
      }

      // 标记正在处理验证
      verificationInProgressRef.current = true;
      setIsProcessingVerification(true);

      // 立即清除所有错误消息
      setErrorMessage('');

      console.log('Turnstile verification successful (debounced)');

      // 设置验证状态
      setTurnstileToken(token);
      setTokenTimestamp(Date.now());
      setTokenUsageCount(0);
      setIsTurnstileVerified(true);

      // 清除历史token记录，因为我们有了新的token
      setUsedTokens(new Set());

      const pendingRemix = pendingRemixRef.current;
      if (pendingRemix) {
        pendingRemixRef.current = null;
        runRemixGeneration({ ...pendingRemix, tokenOverride: token });
        return;
      }

      // 验证成功后自动提交生成
      console.log('Verification successful, submitting image generation request');

      const currentPrompt = watch('prompt');

      // 只有在提示词不为空时才进行生成
      if (currentPrompt && currentPrompt.trim()) {
        runGenerationBatch({
          promptText: currentPrompt,
          tokenOverride: token,
        }).finally(() => {
          verificationInProgressRef.current = false;
          setIsProcessingVerification(false);
        });
      } else {
        // 如果提示词为空，隐藏验证组件并重置状态
        setShowTurnstileWidget(false);
        verificationInProgressRef.current = false;
        setIsProcessingVerification(false);
      }
    }, 300),
    [
      watch,
      setUsedTokens,
      runRemixGeneration,
      runGenerationBatch,
    ]
  );

  // 原始验证成功处理函数，只做转发，不包含实际逻辑
  function handleTurnstileVerify(token) {
    // 即便正在处理，也交给内部的去重逻辑（verificationInProgressRef）判断
    handleTurnstileVerifyDebounced(token);
  }

  function handleTurnstileExpire() {
    handleTurnstileVerifyDebounced.cancel?.();
    setTurnstileToken('');
    setIsTurnstileVerified(false);
    setTokenTimestamp(0);
  }

  // 处理Turnstile验证错误
  function handleTurnstileError() {
    console.error('Turnstile verification callback failed');
    setIsTurnstileVerified(false);
    setTurnstileToken('');
    setTokenTimestamp(0);
    setIsProcessingVerification(false);
    verificationInProgressRef.current = false;
    pendingRemixRef.current = null;

    // 只有当没有进行中的生成过程时才显示错误消息
    if (!isLoading) {
      setErrorMessage(t('errors.verificationFailed'));
    }

    // Cloudflare owns retry timing. Resetting after 500 ms interrupts its
    // automatic recovery and can repeatedly remove the interactive challenge.
  }

  function handleTurnstileCancel() {
    handleTurnstileExpire();
    verificationInProgressRef.current = false;
    setIsProcessingVerification(false);
    pendingRemixRef.current = null;
    setShowTurnstileWidget(false);
  }

  const shouldShowTurnstileInline =
    showTurnstileWidget && turnstileRequired && turnstileDisplay !== 'overlay';
  const shouldShowTurnstileOverlay =
    showTurnstileWidget && turnstileRequired && turnstileDisplay === 'overlay';
  const turnstileOverlay = shouldShowTurnstileOverlay && typeof document !== 'undefined'
    ? createPortal(
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="min-w-[332px] rounded-2xl border border-white/10 bg-[#211A16]/95 p-4 shadow-2xl">
          <TurnstileWidget
            key={`turnstile-${turnstileInstanceKey}`}
            ref={turnstileRef}
            action="generate"
            onVerify={handleTurnstileVerify}
            onExpire={handleTurnstileExpire}
            onError={handleTurnstileError}
            theme="dark"
          />
          <button
            type="button"
            onClick={handleTurnstileCancel}
            disabled={isProcessingVerification}
            className="mt-2 w-full rounded-lg px-3 py-2 text-sm text-white/70 transition hover:bg-white/10 hover:text-white disabled:opacity-50"
          >
            {t('cancel')}
          </button>
        </div>
      </div>,
      document.body
    )
    : null;

  const handleRandom = () => {
    // 清除错误消息
    setErrorMessage('');

    // 扩展的AI图像提示词库
    const randomPrompts = [
      "A majestic lion standing on a mountain cliff overlooking a valley at sunset",
      "A futuristic city with flying cars and neon lights during nighttime",
      "A serene forest landscape with a small cabin by a lake, morning mist rising",
      "An astronaut riding a horse on Mars, photorealistic",
      "A fantasy dragon perched on a castle tower under a starry night sky",
      "An ancient temple hidden in a dense jungle with mystical symbols glowing",
      "A detailed portrait of a cyberpunk character with neon accents and futuristic implants",
      "A magical underwater city with bioluminescent plants and mermaid inhabitants",
      "A steampunk airship flying through a thunderstorm above Victorian-era London",
      "A surreal floating island with waterfalls cascading into the clouds below",
      "A cozy coffee shop at night with rain falling outside the windows",
      "A hyperrealistic still life of exotic fruits in dramatic lighting",
      "An abandoned space station with plants reclaiming the interior and sunlight streaming through windows",
      "A medieval knight battling a mythical creature in a foggy forest",
      "A tranquil Japanese garden with cherry blossoms falling into a koi pond",
      "A post-apocalyptic cityscape with nature reclaiming the concrete jungle",
      "An enchanted library with moving staircases and floating books",
      "A cosmic scene of planets aligned with a nebula in the background",
      "A traditional village nestled between terraced rice fields at harvest time",
      "A crystal palace throne room with magical light beams streaming through ornate windows",
      "masterpiece,best quality,1girl,moyou,seductive smile,(Turtleneck_sweater_dress:1.5),(Thigh-high_boots:1.4),(Wide-brim_hat:1.3),(Autumn_foliage_background:1.3)"
    ];

    // 获取一个随机提示词，避免连续选择同一个
    const currentPrompt = watch('prompt');
    let newPrompt;

    do {
      newPrompt = randomPrompts[Math.floor(Math.random() * randomPrompts.length)];
    } while (newPrompt === currentPrompt && randomPrompts.length > 1);

    setValue('prompt', newPrompt);
  };

  const buildGenerationParams = useCallback(({ promptText, tokenOverride } = {}) => {
    const trimmedPrompt = isVsrMode ? 'Enhance video' : String(promptText || '').trim();
    if (!trimmedPrompt) {
      return null;
    }

    const params = buildStudioGenerationParams({
      promptText: trimmedPrompt,
      currentQualityOptions,
      selectedResolution,
      selectedAspectRatio: watchedSize,
      selectedModel,
      selectedModelConfig,
      tokenOverride,
      turnstileToken,
      outputCount: resolveEffectiveOutputCount(outputCount, generationModeRef.current),
      currentDurationValue: effectiveDurationValue,
      functionMode,
      canToggleGenerateAudio,
      generateAudio,
      privacyUnrestricted,
      noContentFiltering: noContentFilteringRequest,
      contextIrEnabled,
      canUseSeed,
      seedEnabled,
      seed,
      shouldApplyWebSearch,
      canUseFileInput,
      uploadedAssets,
      maxInputUploads,
      imageQuality: canSelectQuality ? selectedQuality : undefined,
      background: selectedModelCaps.allowedBackgrounds ? selectedBackground : undefined,
      moderation: canSelectModeration ? selectedModeration : undefined,
      grokMode: effectiveGrokMode,
      generationMode: generationModeRef.current,
      fps: isVsrMode ? selectedFps : undefined,
    });
    return { ...params, expectedCredits: creditsPerOutput };
  }, [
    creditsPerOutput,
    canSelectModeration,
    canSelectQuality,
    canToggleGenerateAudio,
    canUseFileInput,
    canUseSeed,
    effectiveDurationValue,
    isVsrMode,
    currentQualityOptions,
    effectiveGrokMode,
    functionMode,
    generateAudio,
    privacyUnrestricted,
    noContentFilteringRequest,
    contextIrEnabled,
    maxInputUploads,
    outputCount,
    seed,
    seedEnabled,
    selectedModel,
    selectedFps,
    selectedModelCaps.allowedBackgrounds,
    selectedModeration,
    selectedQuality,
    selectedBackground,
    selectedModelConfig,
    selectedResolution,
    watchedSize,
    shouldApplyWebSearch,
    turnstileToken,
    uploadedAssets,
  ]);

  const incrementPromotionalUsageIfNeeded = useCallback(({ additionalInfo, modelKey }) => {
    if (!additionalInfo?.promotionalConsumed || !modelKey) return;

    setCurrentSubscription((prev) => {
      if (!prev) return prev;
      const currentUsage = prev.promotionalUsage?.[modelKey] || 0;
      return {
        ...prev,
        promotionalUsage: {
          ...(prev.promotionalUsage || {}),
          [modelKey]: currentUsage + 1,
        },
      };
    });
  }, []);

  const executeSingleGeneration = useCallback(async ({
    promptText,
    params,
    visibilityOverride,
  }) => {
    let taskKey;

    try {
      const nextParams = {
        ...params,
        creationSessionId: creationSessionId || params.creationSessionId || undefined,
        creationEntryPoint: creationEntryPoint || params.creationEntryPoint || undefined,
      };
      taskKey =
        (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `task-${Date.now()}-${Math.random()}`;
      nextParams.clientTaskKey = taskKey;
      setActiveTaskKey(taskKey);

      lastQueuedTaskKeyRef.current = taskKey;
      lastToastAtRef.current = Date.now();

      const nextModelKey = nextParams.modelKey || nextParams.modelName || selectedModel;
      const nextModelConfig = nextModelKey ? getModelConfig(nextModelKey) : null;
      const isImageRequest = nextModelConfig?.type === 'image';
      const requestedGenerationMode = String(nextParams.generationMode || '').toLowerCase();
      nextParams.clientQueueStage = 'submitting_request';
      let hasStartedClientDraft = false;
      const startClientDraft = () => {
        if (hasStartedClientDraft || !onGenerationStart) {
          return;
        }

        onGenerationStart({ taskKey, prompt: promptText, params: nextParams });
        hasStartedClientDraft = true;
      };

      if (requestedGenerationMode === 'unlimited') {
        updateGenerationMode('credit');
      }

      // Start the request before route handoff. The callback may navigate away,
      // but the already-issued request continues and is reconciled in My Assets.
      const generationPromise = runGenerationWithVisibility(nextParams, visibilityOverride);
      startClientDraft();
      const result = await generationPromise;
      const {
        assetUrl,
        taskQueued,
        taskSubmitted,
        taskPollingFailed,
        taskId,
        previewUrl: resultPreviewUrl,
        message,
        note,
        additionalInfo: returnedAdditionalInfo
      } = result || {};
      const generatedAssetUrl = assetUrl;

      if (taskQueued || taskSubmitted) {
        setErrorMessage('');
        clearInlineNotice();

        if (!isImageRequest) {
          startClientDraft();
        }

        if (handleGenerationComplete) {
          handleGenerationComplete(null, {
            ...returnedAdditionalInfo,
            taskKey,
            prompt: promptText,
            params: nextParams,
            taskQueued: Boolean(taskQueued),
            taskSubmitted: true,
            previewUrl: resultPreviewUrl,
            message,
            note
          });
        }

        incrementPromotionalUsageIfNeeded({
          additionalInfo: returnedAdditionalInfo,
          modelKey: nextParams.modelKey || selectedModel,
        });

        resetTurnstileVerificationState();
        cleanupTurnstileWidget('task submit');

        return true;
      }

      if (taskPollingFailed) {
        startClientDraft();

        handlePollingFailed({
          message,
          taskId,
          returnedAdditionalInfo: { ...returnedAdditionalInfo, taskKey, prompt: promptText, params: nextParams }
        });
        return false;
      }

      if (generatedAssetUrl && handleGenerationComplete) {
        setErrorMessage('');
        clearInlineNotice();
        incrementPromotionalUsageIfNeeded({
          additionalInfo: returnedAdditionalInfo,
          modelKey: nextParams.modelKey || selectedModel,
        });
        startClientDraft();
        handleGenerationComplete(generatedAssetUrl, { ...returnedAdditionalInfo, taskKey, prompt: promptText, params: nextParams });
        resetTurnstileVerificationState();
        cleanupTurnstileWidget('generation success');
      }

      return true;
    } catch (error) {
      if (isTurnstileReverifyError(error?.message)) {
        console.warn('Image generation requires Turnstile re-verification:', error?.message || error);
        resetTurnstileVerificationState({
          showWidget: !isLoading,
          display: 'overlay',
        });
        remountTurnstileWidget();

        if (taskKey && onGenerationError) {
          onGenerationError(TURNSTILE_REVERIFY_SIGNAL, taskKey);
        }

        return false;
      }

      if (isUpgradeRequiredError(error)) {
        console.warn('Image generation hit upgrade-required branch:', error?.message || error);
        setUpgradeModalVariant(resolveUpgradeModalVariant(error));
        setUpgradeModalDetail(error.message || '');
        setShowUpgradeModal(true);

        if (onGenerationError) {
          onGenerationError(`UPGRADE_REQUIRED: ${error.message || 'Upgrade required'}`, taskKey);
        }

        return false;
      }

      const rawErrorMessage = getErrorMessageText(error) || t('toasts.genFailed');
      const displayErrorMessage = localizeKnownErrorMessage(error);
      const errorKey = getErrorKey(error);
      const errorValues = getErrorValues(error) || error?.details?.values || null;
      setErrorMessage(displayErrorMessage);

      if (errorKey === 'generation_rate_limit') {
        showToast({
          title: t('rateLimit.title'),
          description: buildGenerationRateLimitMessage(rawErrorMessage || displayErrorMessage, t),
          type: 'warning',
        });

        if (onGenerationError) {
          onGenerationError(error || rawErrorMessage, taskKey);
        }

        return false;
      }

      if (errorKey === 'plan_concurrency_limit' || String(rawErrorMessage || '').toLowerCase().includes('concurrent tasks')) {
        showToast({
          title: t('concurrency.title'),
          description: buildConcurrencyMessage(t, {
            message: rawErrorMessage || displayErrorMessage,
            activeTaskCount: errorValues?.activeTaskCount,
            maxConcurrency: errorValues?.maxConcurrency || errorValues?.count,
            remainingSlots: errorValues?.remainingSlots,
          }),
          type: 'warning',
        });

        if (onGenerationError) {
          onGenerationError(error || rawErrorMessage, taskKey);
        }

        return false;
      }

      if (errorKey === 'unlimited_mode_busy') {
        setGenerationModeFallback({
          type: 'unlimited_mode_busy',
          creditsRequired: Number.isFinite(Number(errorValues?.creditsRequired))
            ? Number(errorValues.creditsRequired)
            : creditsRequired,
          title: t('generationMode.fallbackTitle'),
          message: displayErrorMessage,
        });

        if (onGenerationError) {
          onGenerationError(error || rawErrorMessage, taskKey);
        }

        return false;
      }

      if (isInputVideoDurationError(error)) {
        const durationErrorMessage = rawErrorMessage || displayErrorMessage;
        console.warn('Generation handled input video duration error:', durationErrorMessage);
        showToast({
          title: t('toasts.genFailed'),
          description: durationErrorMessage,
          type: 'error'
        });

        if (onGenerationError) {
          onGenerationError(error || durationErrorMessage, taskKey);
        }

        return false;
      }

      console.error('API call error:', error);

      showToast({
        title: t('toasts.genFailed'),
        description: displayErrorMessage,
        type: 'error'
      });

      if (onGenerationError) {
        onGenerationError(error || rawErrorMessage, taskKey);
      }

      return false;
    }
  }, [
    cleanupTurnstileWidget,
    clearInlineNotice,
    creationEntryPoint,
    creationSessionId,
    handleGenerationComplete,
    handlePollingFailed,
    isLoading,
    isPaidMember,
    isTurnstileReverifyError,
    isUpgradeRequiredError,
    localizeKnownErrorMessage,
    onGenerationError,
    onGenerationStart,
    incrementPromotionalUsageIfNeeded,
    remountTurnstileWidget,
    resetTurnstileVerificationState,
    runGenerationWithVisibility,
    resolveUpgradeModalVariant,
    showToast,
    t,
    selectedModel,
    creditsRequired,
    updateGenerationMode,
  ]);

  async function runGenerationBatch({ promptText, tokenOverride } = {}) {
    const trimmedPrompt = String(promptText || '').trim();
    if (!trimmedPrompt) {
      return;
    }

    if (creationType === 'video') {
      const flashImageError = uploadedAssets
        .map((asset) => validateBotgoVideo25FlashInputAsset(asset, { modelName: selectedModel }))
        .find(Boolean);
      if (flashImageError) {
        setInlineNotice({
          message: t(`errors.${flashImageError.errorKey}`, flashImageError.values),
          variant: 'error',
          dismissible: true,
          source: 'inputImage',
        });
        return;
      }
    }

    const baseParams = buildGenerationParams({
      promptText: trimmedPrompt,
      tokenOverride,
    });
    if (!baseParams) {
      return;
    }

    const totalOutputs = resolveEffectiveOutputCount(outputCount, generationModeRef.current);
    const preflightAllowed = await checkGenerationPreflight(totalOutputs);
    if (!preflightAllowed) {
      resetTurnstileVerificationState();
      cleanupTurnstileWidget('generation preflight blocked');
      return;
    }

    const batchGenerationId =
      (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `batch-${Date.now()}-${Math.random()}`;
    const visibilityOverride = consumePublicVisibilityOverride();
    setShowTurnstileWidget(false);
    setIsBatchGenerating(true);

    try {
      for (let index = 0; index < totalOutputs; index += 1) {
        const shouldContinue = await executeSingleGeneration({
          promptText: trimmedPrompt,
          params: {
            ...baseParams,
            batchGenerationId,
            batchIndex: index,
            batchSize: totalOutputs,
            outputCount: totalOutputs,
          },
          visibilityOverride,
        });

        if (!shouldContinue) {
          break;
        }
      }
    } finally {
      setIsBatchGenerating(false);
    }
  }

  // Bottom menus belong to the whole page interaction layer, so an outside click
  // must close them even when the pointer never enters the generator container.
  useEffect(() => {
    if (!activeMenu && !referenceMentionState.open) return undefined;

    const handleDocumentPointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-generator-interactive-portal="true"]')) return;

      if (
        !target.closest('.menu-dropdown-trigger')
        && !target.closest('.menu-dropdown-content')
      ) {
        setActiveMenu(null);
      }

      if (
        !target.closest('.reference-mention-trigger')
        && !target.closest('.reference-mention-menu')
        && target !== textareaRef.current
      ) {
        closeReferenceMentionPicker();
      }
    };

    const handleDocumentKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setActiveMenu(null);
      closeReferenceMentionPicker();
    };

    document.addEventListener('pointerdown', handleDocumentPointerDown);
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handleDocumentPointerDown);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [activeMenu, closeReferenceMentionPicker, referenceMentionState.open]);

  const pushUploadError = useCallback((message) => {
    setUploadError(message);
    showToast({
      title: t('toasts.genFailed'),
      description: message,
      type: 'error'
    });
  }, [showToast, t]);

  const readImageMetadata = useCallback(async (file) => {
    const { imageSize } = await import('image-size');
    const buffer = new Uint8Array(await file.arrayBuffer());
    const metadata = imageSize(buffer);

    if (!metadata?.width || !metadata?.height) {
      throw new Error('Could not read this image\'s dimensions. Please try another file.');
    }

    return {
      width: metadata.width,
      height: metadata.height,
    };
  }, []);

  const readVideoElementMetadata = useCallback((objectUrl) => new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error('Could not read this video\'s metadata. Please try another file.'));
    }, 10000);

    function cleanup() {
      window.clearTimeout(timeoutId);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute('src');
      video.load();
    }

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.onloadedmetadata = () => {
      const width = Number(video.videoWidth);
      const height = Number(video.videoHeight);
      const durationSeconds = Number(video.duration);
      cleanup();

      if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
        reject(new Error('Could not read this video\'s dimensions. Please try another file.'));
        return;
      }

      resolve({
        width: Math.round(width),
        height: Math.round(height),
        durationSeconds: Number.isFinite(durationSeconds) && durationSeconds > 0
          ? Math.round(durationSeconds * 1000) / 1000
          : 0,
        fps: null,
      });
    };
    video.onerror = () => {
      cleanup();
      reject(new Error('Could not read this video\'s metadata. Please try another file.'));
    };
    video.src = objectUrl;
    video.load();
  }), []);

  const readMp4Metadata = useCallback(async (file) => {
    const { readVideoMetadataFromBytes } = await import('@/lib/video-media-metadata');
    const metadata = readVideoMetadataFromBytes(new Uint8Array(await file.arrayBuffer()));
    if (!metadata?.width || !metadata?.height) {
      throw new Error('Could not read this video\'s dimensions. Please try another file.');
    }
    return metadata;
  }, []);

  const readVideoMetadata = useCallback(async (file, objectUrl, { requireFps = false } = {}) => {
    let elementMetadata = null;
    let mp4Metadata = null;
    let elementError = null;

    try {
      elementMetadata = await readVideoElementMetadata(objectUrl);
    } catch (error) {
      elementError = error;
    }

    const needsMp4Fallback =
      requireFps ||
      !elementMetadata?.width ||
      !elementMetadata?.height ||
      !elementMetadata?.durationSeconds;

    if (needsMp4Fallback) {
      try {
        mp4Metadata = await readMp4Metadata(file);
      } catch (error) {
        if (!elementMetadata) {
          throw elementError || error;
        }
      }
    }

    const width = Number(elementMetadata?.width || mp4Metadata?.width || 0);
    const height = Number(elementMetadata?.height || mp4Metadata?.height || 0);
    const durationSeconds = Number(elementMetadata?.durationSeconds || mp4Metadata?.durationSeconds || 0);
    const fps = Number(mp4Metadata?.fps || elementMetadata?.fps || 0);

    if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
      throw new Error('Could not read this video\'s dimensions. Please try another file.');
    }

    return {
      width: Math.round(width),
      height: Math.round(height),
      durationSeconds: Number.isFinite(durationSeconds) && durationSeconds > 0
        ? Math.round(durationSeconds * 1000) / 1000
        : null,
      fps: Number.isFinite(fps) && fps > 0
        ? Math.round(fps * 1000) / 1000
        : null,
    };
  }, [readMp4Metadata, readVideoElementMetadata]);

  const readAudioMetadata = useCallback((objectUrl) => new Promise((resolve, reject) => {
    const audio = document.createElement('audio');
    const cleanup = () => {
      audio.removeAttribute('src');
      audio.load();
    };

    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      const durationSeconds = Number(audio.duration);
      cleanup();
      if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
        reject(new Error('Could not read this audio file\'s duration. Please try another file.'));
        return;
      }
      resolve({
        durationSeconds: Math.round(durationSeconds * 1000) / 1000,
      });
    };
    audio.onerror = () => {
      cleanup();
      reject(new Error('Could not read this audio file\'s duration. Please try another file.'));
    };
    audio.src = objectUrl;
  }), []);

  const hydrateRecreatedAssetMetadata = useCallback(async (asset) => {
    if (!asset?.url || !asset?.type) {
      return asset;
    }

    const needsImageMetadata =
      asset.type === 'image' &&
      (!hasPositiveNumericValue(asset.width) || !hasPositiveNumericValue(asset.height));
    const needsVideoMetadata =
      asset.type === 'video' &&
      (
        !hasPositiveNumericValue(asset.width) ||
        !hasPositiveNumericValue(asset.height) ||
        !hasPositiveNumericValue(asset.durationSeconds) ||
        !hasPositiveNumericValue(asset.fps)
      );
    const needsAudioMetadata =
      asset.type === 'audio' &&
      !hasPositiveNumericValue(asset.durationSeconds);

    if (!needsImageMetadata && !needsVideoMetadata && !needsAudioMetadata) {
      return asset;
    }

    try {
      const response = await fetch('/api/upload/metadata', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url: asset.url,
          type: asset.type,
          name: asset.name || null,
          mimeType: asset.mimeType || null,
        }),
      });
      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        throw new Error(
          errorPayload?.error
          || errorPayload?.message
          || `HTTP ${response.status}`
        );
      }

      const payload = await response.json().catch(() => ({}));
      const metadata = payload?.metadata && typeof payload.metadata === 'object'
        ? payload.metadata
        : {};

      return {
        ...asset,
        name: asset.name || metadata.name || null,
        mimeType: asset.mimeType || metadata.mimeType || null,
        fileSizeBytes: Number.isFinite(Number(asset.fileSizeBytes))
          ? Number(asset.fileSizeBytes)
          : (Number.isFinite(Number(metadata.fileSizeBytes)) ? Number(metadata.fileSizeBytes) : null),
        ...metadata,
      };
    } catch (error) {
      console.warn('[recreate] failed to hydrate remote asset metadata', {
        url: asset.url,
        type: asset.type,
        message: error?.message || String(error),
      });
      return asset;
    }
  }, []);

  hydrateRecreatedAssetMetadataRef.current = hydrateRecreatedAssetMetadata;

  const openUploadDialog = useCallback((slotIndex = null) => {
    setActiveMenu(null);
    pendingUploadSlotIndexRef.current = Number.isInteger(slotIndex) ? slotIndex : null;
    fileInputRef.current?.click();
  }, []);

  const getDraftAssetType = useCallback((item) => {
    const mediaType = String(item?.mediaType || '').toLowerCase();
    if (mediaType === 'video' || mediaType === 'image' || mediaType === 'audio') {
      return mediaType;
    }
    if (item?.videoUrl) return 'video';
    if (item?.audioUrl) return 'audio';
    return 'image';
  }, []);

  const getDraftAssetUrl = useCallback((item) => {
    if (!item || typeof item !== 'object') return null;
    const assetType = getDraftAssetType(item);
    if (assetType === 'video') return item.videoUrl || null;
    if (assetType === 'audio') return item.audioUrl || item.audio_url || null;
    return item.imageUrl || item.thumbnailUrl || null;
  }, [getDraftAssetType]);

  const getDraftAssetKey = useCallback((item) => (
    `${getDraftAssetType(item)}-${item?.id || item?.taskId || getDraftAssetUrl(item) || 'asset'}`
  ), [getDraftAssetType, getDraftAssetUrl]);

  const getDefaultAssetPickerType = useCallback(() => {
    if (['editing', 'extend'].includes(functionMode) && allowedMediaTypes.includes('video')) {
      return 'video';
    }
    if (allowedMediaTypes.includes('image')) return 'image';
    if (allowedMediaTypes.includes('video')) return 'video';
    if (allowedMediaTypes.includes('audio')) return 'audio';
    return 'image';
  }, [allowedMediaTypes, functionMode]);

  const loadDraftAssets = useCallback(async () => {
    setAssetPickerLoading(true);
    setAssetPickerError('');
    try {
      const response = await fetch('/api/drafts/list?limit=80', {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        throw new Error(`Failed to load assets (${response.status})`);
      }
      const payload = await response.json().catch(() => ({}));
      const items = Array.isArray(payload?.items) ? payload.items : [];
      setAssetPickerItems(items.filter((item) => {
        const status = String(item?.status || '').toLowerCase();
        return ['completed', 'succeeded', 'success'].includes(status) && Boolean(getDraftAssetUrl(item));
      }));
    } catch (error) {
      const message = error?.message || t('assetPicker.loadFailed');
      setAssetPickerError(message);
    } finally {
      setAssetPickerLoading(false);
    }
  }, [getDraftAssetUrl, t]);

  const openAssetPicker = useCallback(() => {
    setActiveMenu(null);
    setAssetPickerSelectedKeys([]);
    setAssetPickerActiveType(getDefaultAssetPickerType());
    setAssetPickerOpen(true);
    loadDraftAssets();
  }, [getDefaultAssetPickerType, loadDraftAssets]);

  const closeAssetPicker = useCallback(() => {
    setAssetPickerOpen(false);
    setAssetPickerSelectedKeys([]);
    setAssetPickerError('');
  }, []);

  const handleAssetPickerTypeChange = useCallback((assetType) => {
    setAssetPickerActiveType(assetType);
    setAssetPickerSelectedKeys([]);
  }, []);

  const createDraftInputAsset = useCallback((item, assetType) => {
    const url = getDraftAssetUrl(item);
    if (!url) return null;

    return {
      id: `draft-${assetType}-${item.id}`,
      name: item.prompt || item.taskId || item.id,
      mimeType: assetType === 'video'
        ? 'video/mp4'
        : assetType === 'audio'
          ? 'audio/mpeg'
          : null,
      preview: assetType === 'video' ? (item.thumbnailUrl || null) : (item.thumbnailUrl || url),
      url,
      type: assetType,
      status: 'complete',
      width: null,
      height: null,
      durationSeconds: Number.isFinite(Number(item.duration)) ? Number(item.duration) : null,
      fps: null,
      fileSizeBytes: null,
      source: 'draft',
      sourceId: item.id,
      sourceTaskId: item.taskId || null,
      sourceModelKey: item.modelKey || null,
      sourceApiProvider: item.apiProvider || null,
      sourceApiProviderSlug: item.apiProviderSlug || null,
      sourceApiProviderType: item.apiProviderType || null,
      sourceApiProviderModelName: item.apiProviderModelName || null,
      sourceResolution: item.resolution || item.metadata?.resolution || item.metadata?.requestData?.resolution || item.metadata?.requestData?.size || null,
      sourceAspectRatio: item.aspectRatio || item.aspect_ratio || item.metadata?.aspectRatio || item.metadata?.aspect_ratio || item.metadata?.requestData?.aspectRatio || item.metadata?.requestData?.aspectRatioLabel || null,
    };
  }, [getDraftAssetUrl]);

  const handleConfirmDraftAssets = useCallback(async () => {
    const selectedKeys = new Set(assetPickerSelectedKeys);
    const selectedItems = assetPickerItems.filter((item) => selectedKeys.has(getDraftAssetKey(item)));
    if (selectedItems.length === 0) return;

    const assetType = assetPickerActiveType;

    if (!canUseFileInput || maxInputUploads === 0) {
      return;
    }

    if (!allowedMediaTypes.includes(assetType)) {
      pushUploadError(t('errors.uploadTypeLimit') + ` (${formatAllowedTypesLabel(currentInputRules)})`);
      return;
    }

    const existingAssets = uploadedAssets.filter(Boolean);
    const remainingTotalSlots = maxInputUploads - existingAssets.length;
    if (remainingTotalSlots <= 0 || selectedItems.length > remainingTotalSlots) {
      pushUploadError(t('errors.uploadCountLimit', { count: maxInputUploads, s: maxInputUploads > 1 ? 's' : '' }));
      return;
    }

    const currentTypeCount = existingAssets.filter((asset) => asset.type === assetType).length;
    const currentTypeLimit = maxUploadsByType?.[assetType];
    if (Number.isFinite(currentTypeLimit) && currentTypeCount + selectedItems.length > currentTypeLimit) {
      pushUploadError(t('errors.uploadTypeCountLimit', {
        count: currentTypeLimit,
        type: getAssetTypeLabel(assetType),
        s: currentTypeLimit > 1 ? 's' : '',
      }));
      return;
    }

    const hydratedAssets = [];
    let veoExtendSourceSelection = null;
    for (const item of selectedItems) {
      let isVeoExtendDraftSelection = false;
      if (
        creationType === 'video'
        && functionMode === 'extend'
        && VEO_EXTEND_SELECTABLE_MODEL_KEYS.has(selectedModel)
      ) {
        const isCompatibleSourceModel = isCompatibleVeoExtendSourceModelKey(item.modelKey);
        const sourceProviderRoute = resolveVeoExtendSourceProviderRoute(item);
        if (!item.taskId || !isCompatibleSourceModel || sourceProviderRoute !== 'kieai') {
          pushUploadError(t('errors.veo31KieExtendDraftRequired'));
          return;
        }
        isVeoExtendDraftSelection = true;
      }
      const draftAsset = createDraftInputAsset(item, assetType);
      if (!draftAsset) continue;
      const hydratedAsset = await hydrateRecreatedAssetMetadataRef.current(draftAsset);
      if (creationType === 'video') {
        const flashImageError = validateBotgoVideo25FlashInputAsset(hydratedAsset, { modelName: selectedModel });
        if (flashImageError) {
          pushUploadError(t(`errors.${flashImageError.errorKey}`, flashImageError.values));
          return;
        }
      }
      if (isVeoExtendDraftSelection) {
        veoExtendSourceSelection = {
          modelKey: selectedModel,
          sourceAspectRatio: resolveVeoExtendDraftAspectRatio({ item, asset: hydratedAsset }),
          sourceResolutionLabel: resolveVeoExtendDraftResolution({ item, asset: hydratedAsset }),
        };
      }
      if (isSeedanceVideoModelKey(selectedModel)) {
        const seedanceAssetError = validateSeedanceInputAsset(hydratedAsset, { modelName: selectedModel });
        if (seedanceAssetError) {
          pushUploadError(seedanceAssetError.message);
          return;
        }
      }
      if (isWanVideoModel(selectedModel)) {
        const wanError = validateWanVideoInputs([...uploadedAssets, hydratedAsset]);
        if (wanError) throw new Error(wanError.message);
      }
      const durationAssetError = validateVideoInputAssetDuration(hydratedAsset, {
        modelName: selectedModel,
        creationType,
        functionMode,
      });
      if (durationAssetError) {
        pushUploadError(durationAssetError.message);
        return;
      }
      hydratedAssets.push(hydratedAsset);
    }

    if (hydratedAssets.length === 0) return;
    if (veoExtendSourceSelection) {
      const caps = getModelCapabilities(veoExtendSourceSelection.modelKey) || {};
      const aspectRatio = veoExtendSourceSelection.sourceAspectRatio || caps.allowedAspectRatios?.[0] || '16:9';
      const resolutionLabel = caps.allowedResolutions?.includes(veoExtendSourceSelection.sourceResolutionLabel)
        ? veoExtendSourceSelection.sourceResolutionLabel
        : (caps.allowedResolutions?.[0] || '720p');
      const apiSize = caps.allowedSizesByAspect?.[aspectRatio]?.[resolutionLabel] || null;
      const resolutionId = buildResolutionOptionId({
        aspectRatio,
        resolutionLabel,
        apiSize,
      });

      if (aspectRatio) {
        recreateAspectOverrideRef.current = {
          modelKey: veoExtendSourceSelection.modelKey,
          aspectId: aspectRatio,
        };
      }
      if (resolutionId) {
        recreateResolutionOverrideRef.current = {
          modelKey: veoExtendSourceSelection.modelKey,
          resolutionId,
          apiSize,
          resolutionLabel,
        };
      }

      if (aspectRatio) {
        setValue('size', aspectRatio);
      }
      if (resolutionId) {
        setSelectedResolution(resolutionId);
      }
    }
    setUploadedAssets((prev) => [...prev.filter(Boolean), ...hydratedAssets]);
    closeAssetPicker();
  }, [
    assetPickerActiveType,
    assetPickerItems,
    assetPickerSelectedKeys,
    allowedMediaTypes,
    canUseFileInput,
    closeAssetPicker,
    createDraftInputAsset,
    currentInputRules,
    formatAllowedTypesLabel,
    getAssetTypeLabel,
    getDraftAssetKey,
    maxInputUploads,
    maxUploadsByType,
    pushUploadError,
    creationType,
    functionMode,
    selectedModel,
    setValue,
    t,
    uploadedAssets,
  ]);

  const assetPickerSelectedKeySet = useMemo(() => (
    new Set(assetPickerSelectedKeys)
  ), [assetPickerSelectedKeys]);

  const assetPickerTabs = useMemo(() => ([
    { type: 'image', label: t('assetPicker.images') },
    { type: 'video', label: t('assetPicker.videos') },
    { type: 'audio', label: t('assetPicker.audios') },
  ].map((tab) => ({
    ...tab,
    count: assetPickerItems.filter((item) => getDraftAssetType(item) === tab.type).length,
    allowed: allowedMediaTypes.includes(tab.type),
  }))), [allowedMediaTypes, assetPickerItems, getDraftAssetType, t]);

  const activeAssetPickerItems = useMemo(() => (
    assetPickerItems.filter((item) => {
      if (getDraftAssetType(item) !== assetPickerActiveType) return false;
      if (
        creationType === 'video'
        && functionMode === 'extend'
        && VEO_EXTEND_SELECTABLE_MODEL_KEYS.has(selectedModel)
        && assetPickerActiveType === 'video'
      ) {
        return Boolean(
          item?.taskId
          && isCompatibleVeoExtendSourceModelKey(item.modelKey)
          && resolveVeoExtendSourceProviderRoute(item) === 'kieai'
        );
      }
      return true;
    })
  ), [assetPickerActiveType, assetPickerItems, creationType, functionMode, getDraftAssetType, selectedModel]);

  const maxSelectableDraftAssets = useMemo(() => {
    if (!canUseFileInput || maxInputUploads === 0) return 0;
    const existingAssets = uploadedAssets.filter(Boolean);
    const remainingTotalSlots = Math.max(0, maxInputUploads - existingAssets.length);
    const currentTypeLimit = maxUploadsByType?.[assetPickerActiveType];
    if (!Number.isFinite(currentTypeLimit)) return remainingTotalSlots;
    const currentTypeCount = existingAssets.filter((asset) => asset.type === assetPickerActiveType).length;
    return Math.max(0, Math.min(remainingTotalSlots, currentTypeLimit - currentTypeCount));
  }, [assetPickerActiveType, canUseFileInput, maxInputUploads, maxUploadsByType, uploadedAssets]);

  const toggleDraftAssetSelection = useCallback((item) => {
    const key = getDraftAssetKey(item);
    setAssetPickerSelectedKeys((prev) => {
      if (prev.includes(key)) {
        return prev.filter((itemKey) => itemKey !== key);
      }
      if (prev.length >= maxSelectableDraftAssets) {
        return prev;
      }
      return [...prev, key];
    });
  }, [getDraftAssetKey, maxSelectableDraftAssets]);

  const formatDraftDurationLabel = useCallback((duration) => {
    const totalSeconds = Math.round(Number(duration));
    if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '';
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    return `${minutes}:${seconds}`;
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (requiresDraftSourceTaskId) {
      e.target.value = '';
      return;
    }

    const targetSlotIndex =
      functionMode === 'frames' && Number.isInteger(pendingUploadSlotIndexRef.current)
        ? pendingUploadSlotIndexRef.current
        : null;
    pendingUploadSlotIndexRef.current = null;

    if (!canUseFileInput || maxInputUploads === 0) {
      e.target.value = '';
      return;
    }

    const assetType = inferInputMediaType({
      mimeType: file.type,
      fileName: file.name,
    });
    if (!assetType || !allowedMediaTypes.includes(assetType)) {
      pushUploadError(t('errors.uploadTypeLimit') + ` (${formatAllowedTypesLabel(currentInputRules)})`);
      e.target.value = '';
      return;
    }

    if (uploadedAssets.length >= maxInputUploads) {
      pushUploadError(t('errors.uploadCountLimit', { count: maxInputUploads, s: maxInputUploads > 1 ? 's' : '' }));
      e.target.value = '';
      return;
    }

    const currentTypeCount = uploadedAssets.filter((asset) => asset.type === assetType).length;
    const currentTypeLimit = maxUploadsByType?.[assetType];
    if (Number.isFinite(currentTypeLimit) && currentTypeCount >= currentTypeLimit) {
      pushUploadError(t('errors.uploadTypeCountLimit', {
        count: currentTypeLimit,
        type: getAssetTypeLabel(assetType),
        s: currentTypeLimit > 1 ? 's' : '',
      }));
      e.target.value = '';
      return;
    }

    if (!isAcceptedInputFile({
      mediaType: assetType,
      mimeType: file.type,
      fileName: file.name,
      rules: currentInputRules,
    })) {
      pushUploadError(t('errors.uploadTypeLimit') + ` (${formatAllowedTypesLabel(currentInputRules)})`);
      e.target.value = '';
      return;
    }

    const maxFileSizeBytes = getMaxFileSizeBytesForMediaType(assetType, {
      modelName: selectedModel,
      creationType,
      functionMode,
    });
    if (maxFileSizeBytes > 0 && file.size > maxFileSizeBytes) {
      pushUploadError(t('errors.uploadSizeLimit', {
        type: getAssetTypeLabel(assetType),
        size: getMaxFileSizeLabelForMediaType(assetType, {
          modelName: selectedModel,
          creationType,
          functionMode,
        }),
      }));
      e.target.value = '';
      return;
    }

    setUploadError('');

    const uploadId = Date.now().toString();
    try {
      const preview = URL.createObjectURL(file);
      let assetMetadata = {};
      if (assetType === 'image') {
        assetMetadata = await readImageMetadata(file);
      } else if (assetType === 'video') {
        assetMetadata = await readVideoMetadata(file, preview, {
          requireFps: isSeedanceVideoModelKey(selectedModel) || isWanVideoModel(selectedModel),
        });
      } else if (assetType === 'audio') {
        assetMetadata = await readAudioMetadata(preview);
      }

      const inputAssetMetadata = {
        type: assetType,
        mimeType: file.type,
        name: file.name,
        fileSizeBytes: file.size,
        ...assetMetadata,
      };
      if (creationType === 'video') {
        const flashImageError = validateBotgoVideo25FlashInputAsset(inputAssetMetadata, { modelName: selectedModel });
        if (flashImageError) {
          URL.revokeObjectURL(preview);
          pushUploadError(t(`errors.${flashImageError.errorKey}`, flashImageError.values));
          e.target.value = '';
          return;
        }
      }

      if (isSeedanceVideoModelKey(selectedModel)) {
        const seedanceAssetError = validateSeedanceInputAsset({
          type: assetType,
          mimeType: file.type,
          name: file.name,
          fileSizeBytes: file.size,
          ...assetMetadata,
        }, { modelName: selectedModel });
        if (seedanceAssetError) {
          URL.revokeObjectURL(preview);
          pushUploadError(seedanceAssetError.message);
          e.target.value = '';
          return;
        }
      }

      if (isWanVideoModel(selectedModel)) {
        const wanError = validateWanVideoInputs([...uploadedAssets, { type: assetType, fileSizeBytes: file.size, ...assetMetadata }]);
        if (wanError) {
          URL.revokeObjectURL(preview);
          pushUploadError(wanError.message);
          e.target.value = '';
          return;
        }
      }
      const durationAssetError = validateVideoInputAssetDuration({
        type: assetType,
        mimeType: file.type,
        name: file.name,
        fileSizeBytes: file.size,
        ...assetMetadata,
      }, {
        modelName: selectedModel,
        creationType,
        functionMode,
      });
      if (durationAssetError) {
        URL.revokeObjectURL(preview);
        pushUploadError(durationAssetError.message);
        e.target.value = '';
        return;
      }

      const nextAsset = {
        id: uploadId,
        file,
        name: file.name,
        mimeType: file.type,
        preview,
        type: assetType,
        status: 'uploading',
        width: assetMetadata?.width || null,
        height: assetMetadata?.height || null,
        durationSeconds: assetMetadata?.durationSeconds || null,
        fps: assetMetadata?.fps || null,
        fileSizeBytes: file.size,
      };

      setUploadedAssets(prev => {
        const existingAssets = prev.filter(Boolean);
        if (functionMode === 'frames' && targetSlotIndex !== null) {
          const nextAssets = [...existingAssets];
          nextAssets[targetSlotIndex] = nextAsset;
          return nextAssets.filter(Boolean);
        }
        return [...existingAssets, nextAsset];
      });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('modelName', selectedModel);
      formData.append('creationType', creationType);
      formData.append('functionMode', functionMode);

      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message
          || errorData?.error
          || `Upload failed (${response.status})`
        );
      }

      const data = await response.json();

      setUploadedAssets(prev =>
        prev.map(asset =>
          asset.id === uploadId
            ? {
              ...asset,
              status: 'complete',
              url: data.url,
              type: data.mediaType || assetType,
              fileSizeBytes: data.fileSizeBytes || asset.fileSizeBytes || file.size,
            }
            : asset
        )
      );

      console.log('Reference asset uploaded successfully to Cloudflare R2:', data.url);
    } catch (error) {
      console.warn('Upload error:', error);
      pushUploadError(error.message || t('errors.uploadResultFailed'));

      setUploadedAssets(prev =>
        prev.map(asset =>
          asset.id === uploadId
            ? { ...asset, status: 'error' }
            : asset
        )
      );
    } finally {
      e.target.value = '';
    }
  };

  const removeUploadedAssetById = (id) => {
    setUploadedAssets(prev => prev.filter(asset => asset.id !== id));
  };

  const removeUploadedAssets = () => {
    setUploadedAssets([]);
  };

  const getModelLogo = (modelKey) => {
    if (modelKey.includes('botgo-')) return '/brand/botgo-symbol-white.svg?v=orbit-centered-03';
    if (modelKey.includes('sora-2')) return '/Logo/openai.svg';
    if (modelKey.includes('gpt-image')) return '/Logo/openai.svg';
    if (modelKey.includes('veo')) return '/Logo/google.svg';
    if (modelKey.includes('gemini-omni')) return '/Logo/google.svg';
    if (modelKey.startsWith('wan-')) return '/Logo/bailian-color.svg';
    if (modelKey.includes('minimax')) return '/Logo/minimax.svg';
    if (modelKey.includes('seedance')) return '/Logo/bytedance.svg';
    if (modelKey.includes('seedream')) return '/Logo/bytedance.svg';
    if (modelKey.includes('happyhorse')) return '/Logo/happyhorse.png';
    if (modelKey.includes('kling')) return '/Logo/Kling.svg';
    if (modelKey.includes('grok')) return '/Logo/grok.svg';
    // Add Google Logo for Nano Banana (Jimeng proxies often use Google cloud or similar, user requested Google logo)
    if (modelKey.includes('nano-banana')) return '/Logo/google.svg';
    return null;
  };

  const renderReferenceMentionIcon = (option) => {
    if (option.thumbnailUrl) {
      return (
        <img
          src={option.thumbnailUrl}
          alt=""
          className="h-9 w-9 rounded-md object-cover"
        />
      );
    }

    if (option.mediaType === 'video' && option.mediaUrl) {
      return (
        <video
          src={option.mediaUrl}
          poster={option.previewUrl && option.previewUrl !== option.mediaUrl ? option.previewUrl : undefined}
          className="h-9 w-9 rounded-md object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
        />
      );
    }

    const Icon = option.mediaType === 'video'
      ? IconVideo
      : option.mediaType === 'audio'
        ? IconMusic
        : IconPhoto;

    return (
      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-white/10 text-white/70">
        <Icon size={18} />
      </span>
    );
  };

  const renderReferenceMentionMenu = ({ className = '', style = undefined } = {}) => (
    <div
      className={`reference-mention-menu menu-dropdown-content z-[220] w-[360px] max-w-[calc(100vw-1rem)] overflow-hidden rounded-xl ${GENERATOR_MENU_PANEL_CLASS} ${className}`}
      style={style}
      onMouseDown={(event) => event.preventDefault()}
    >
      <div className="border-b border-[#44352D] px-3 py-2 text-xs font-medium text-white/70">
        {t('referenceMention.hint')}
      </div>
      <div className="max-h-64 overflow-y-auto p-1.5 custom-scrollbar">
        {filteredReferenceMentionOptions.length > 0 ? (
          filteredReferenceMentionOptions.map((option) => (
            <button
              key={option.assetId}
              type="button"
              className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition focus:bg-[#352820] focus:outline-none ${GENERATOR_MENU_ITEM_HOVER_CLASS}`}
              onMouseDown={(event) => {
                event.preventDefault();
                insertReferenceMention(option);
              }}
            >
              {renderReferenceMentionIcon(option)}
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-white">
                  @{option.referenceKey}
                </span>
                <span className="block truncate text-xs text-white/50">
                  {option.asset?.name || option.asset?.file?.name || getAssetTypeLabel(option.mediaType)}
                </span>
              </span>
            </button>
          ))
        ) : (
          <div className="px-3 py-5 text-center text-xs text-white/50">
            {referenceMentionOptions.length > 0
              ? t('referenceMention.noMatches')
              : t('referenceMention.empty')}
          </div>
        )}
      </div>
    </div>
  );

  const renderAssetSourceMenu = () => (
    <div
      className={`asset-source-menu menu-dropdown-content absolute ${openDropdownUp ? 'bottom-full mb-2' : 'top-full mt-2'} left-0 z-[230] w-[280px] max-w-[calc(100vw-1rem)] overflow-hidden rounded-xl ${GENERATOR_MENU_PANEL_CLASS}`}
      onMouseDown={(event) => event.preventDefault()}
    >
      <div className="p-1">
        {!requiresDraftSourceTaskId && (
          <button
            type="button"
            className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs text-white transition ${GENERATOR_MENU_ITEM_HOVER_CLASS}`}
            onClick={() => openUploadDialog()}
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-white/10 text-white/75">
              <IconUpload size={15} />
            </span>
            <span>{t('assetSource.uploadFromDevice')}</span>
          </button>
        )}
        <button
          type="button"
          className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs text-white transition ${GENERATOR_MENU_ITEM_HOVER_CLASS}`}
          onClick={openAssetPicker}
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-white/10 text-white/75">
            <IconFolderOpen size={15} />
          </span>
          <span>{t('assetSource.selectFromAssets')}</span>
        </button>
      </div>

      {isReferenceMentionEnabled && referenceMentionOptions.length > 0 && (
        <div className="border-t border-[#44352D] p-1">
          {referenceMentionOptions.slice(0, 6).map((option) => (
            <button
              key={option.assetId}
              type="button"
              className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left transition ${GENERATOR_MENU_ITEM_HOVER_CLASS}`}
              onClick={() => insertReferenceMention(option)}
            >
              {renderReferenceMentionIcon(option)}
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-semibold text-white">@{option.referenceKey}</span>
                <span className="block truncate text-xs text-white/50">
                  {option.asset?.name || option.asset?.file?.name || getAssetTypeLabel(option.mediaType)}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderUploadPreview = (asset) => {
    if (asset?.type === 'video') {
      return (
        <video
          src={asset.url || asset.preview}
          poster={asset.preview && asset.preview !== asset.url ? asset.preview : undefined}
          className="w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
        />
      );
    }

    if (asset?.type === 'audio') {
      return (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#1F1A17] text-white/80">
          <IconUpload size={16} />
          <span className="mt-1 text-[8px] font-semibold uppercase tracking-[0.18em]">
            {getAssetTypeLabel(asset.type)}
          </span>
        </div>
      );
    }

    return <img src={asset.preview || asset.url} alt="Upload" className="w-full h-full object-cover" />;
  };

  const renderUploadSlot = ({ asset = null, slotKey, label }) => {
    const isSlotFilled = !!asset;
    const referenceLabel = isReferenceMentionEnabled && asset ? referenceAssetLabelById.get(asset.id) : null;
    const isReferencedAsset = isReferenceMentionEnabled && asset ? referencedAssetIds.has(asset.id) : false;
    const isEmptySlotDisabled = !isSlotFilled && (isUploadDisabled || requiresDraftSourceTaskId);
    const slotIndex =
      slotKey === 'upload-slot-start'
        ? 0
        : slotKey === 'upload-slot-end'
          ? 1
          : slotKey.startsWith('upload-slot-kling-frame-') ? Number(slotKey.split('-').at(-1)) : null;

    return (
      <div
        key={slotKey}
        data-generation-upload-slot="true"
        className={[
          `relative group w-12 h-16 rounded-lg flex items-center justify-center overflow-hidden transition-colors ${GENERATOR_UPLOAD_SLOT_CLASS}`,
          isEmptySlotDisabled ? 'cursor-not-allowed opacity-65' : 'cursor-pointer',
          isReferencedAsset
            ? 'border-amber-400/80 ring-1 ring-amber-400/40 shadow-[0_0_18px_rgba(251,191,36,0.18)]'
            : isEmptySlotDisabled
              ? 'border-transparent'
              : 'hover:border-transparent hover:bg-white/[0.16]',
        ].join(' ')}
        aria-disabled={isEmptySlotDisabled}
        onClick={() => {
          if (isSlotFilled && asset) {
            setPreviewAsset(getAssetPreviewTarget(asset));
          } else if (!isEmptySlotDisabled) {
            if (functionMode === 'frames' && slotIndex === 1 && !uploadedAssets[0]) {
              pushUploadError('Please upload the start frame before the end frame.');
              return;
            }
            openUploadDialog(slotIndex);
          }
        }}
      >
        {isSlotFilled ? (
          <>
            {renderUploadPreview(asset)}
            <span
              className={[
                'absolute left-1 top-1 rounded px-1 py-0.5 text-[7px] font-semibold uppercase tracking-[0.1em]',
                isReferencedAsset
                  ? 'bg-amber-400 text-black'
                  : 'bg-black/70 text-white',
              ].join(' ')}
            >
              {selectedModel === 'kling-v4' && functionMode === 'frames' ? label : referenceLabel || getAssetTypeLabel(asset.type)}
            </span>
            {asset.status === 'uploading' && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            {asset.status === 'error' && (
              <div className="absolute inset-0 bg-red-900/50 flex items-center justify-center">
                <IconX size={16} className="text-red-500" />
              </div>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeUploadedAssetById(asset.id);
              }}
              className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <IconX size={10} />
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-1 text-white/58">
            <IconPlus size={16} />
            {label && <span className="text-[8px] uppercase font-bold tracking-wider scale-90">{label}</span>}
          </div>
        )}
      </div>
    );
  };

  const referenceUploadSlots = (() => {
    if (
      creationType !== 'video' ||
      !['reference', 'editing', 'extend', 'enhance'].includes(functionMode) ||
      maxInputUploads === 0
    ) {
      return [];
    }
    const visibleAssets = uploadedAssets.slice(0, maxInputUploads);
    return visibleAssets.length < maxInputUploads
      ? [...visibleAssets, null]
      : visibleAssets;
  })();

  const imageUploadSlots = (() => {
    if (creationType !== 'image' || maxInputUploads === 0) {
      return [];
    }
    const visibleAssets = uploadedAssets.slice(0, maxInputUploads);
    return visibleAssets.length < maxInputUploads
      ? [...visibleAssets, null]
      : visibleAssets;
  })();

  const functionModeOptions = useMemo(() => {
    if (creationType === 'image') {
      return [
        { id: 'general', labelKey: 'imageGeneral', Icon: IconWand },
        { id: 'editing', labelKey: 'imageEditing', Icon: IconPhotoEdit },
      ];
    }

    return [
      { id: 'general', labelKey: 'general', Icon: IconWand },
      { id: 'reference', labelKey: 'reference', Icon: IconLayoutGridAdd },
      { id: 'frames', labelKey: 'frames', Icon: IconArrowsRightLeft },
      { id: 'editing', labelKey: 'editing', Icon: VideoEditingModeIcon },
      { id: 'extend', labelKey: 'extend', Icon: VideoExtendModeIcon },
      { id: 'enhance', labelKey: 'enhance', Icon: IconRefresh },
    ];
  }, [creationType]);

  const activeFunctionModeOption = (
    functionModeOptions.find((option) => option.id === functionMode)
    || functionModeOptions[0]
  );
  const ActiveFunctionModeIcon = activeFunctionModeOption?.Icon || IconWand;

  const visibleInlineNotice = inlineNotice || (
    errors.prompt?.message && !isPromptNoticeDismissed
      ? {
        message: errors.prompt.message,
        variant: 'error',
        dismissible: true,
        source: 'prompt',
      }
      : null
  );
  const assetPickerSelectedCount = assetPickerSelectedKeys.length;
  const isAssetPickerActiveTypeAllowed = allowedMediaTypes.includes(assetPickerActiveType);

  const handleInlineNoticeDismiss = () => {
    if (inlineNotice) {
      clearInlineNotice();
      return;
    }

    setIsPromptNoticeDismissed(true);
    clearErrors('prompt');
  };

  const assetPreviewLabel = getGenerationStudioText(
    'assetPreview.label',
    'Reference asset preview'
  );
  const assetPreviewCloseLabel = getGenerationStudioText(
    'assetPreview.close',
    'Close preview'
  );
  const assetPreviewOverlay = previewAsset && typeof document !== 'undefined'
    ? createPortal(
      <div
        role="dialog"
        aria-modal="true"
        aria-label={previewAsset.name || assetPreviewLabel}
        data-asset-preview-overlay="true"
        className="fixed inset-0 z-[300] flex items-center justify-center overflow-hidden bg-black/90 p-4 backdrop-blur-md"
        onClick={() => setPreviewAsset(null)}
      >
        <button
          type="button"
          aria-label={assetPreviewCloseLabel}
          data-asset-preview-close="true"
          autoFocus
          className="absolute right-4 top-4 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-black/65 text-white shadow-xl backdrop-blur-md transition-all duration-200 hover:scale-105 hover:bg-black/80 hover:text-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
          style={{
            top: 'max(1rem, env(safe-area-inset-top))',
            right: 'max(1rem, env(safe-area-inset-right))',
          }}
          onClick={() => setPreviewAsset(null)}
        >
          <IconX size={24} />
        </button>

        <div className="flex h-full min-h-0 w-full min-w-0 items-center justify-center">
          {previewAsset.type === 'video' ? (
            <video
              src={previewAsset.url}
              className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
              controls
              autoPlay
              playsInline
              onClick={(event) => event.stopPropagation()}
            />
          ) : previewAsset.type === 'audio' ? (
            <div
              className="w-full max-w-md rounded-2xl border border-white/10 bg-[#211A16]/95 p-6 shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <p className="mb-4 text-sm text-white/70">
                {previewAsset.name || t('mediaTypes.audio')}
              </p>
              <audio src={previewAsset.url} controls autoPlay className="w-full" />
            </div>
          ) : (
            <img
              src={previewAsset.url}
              alt={previewAsset.name || assetPreviewLabel}
              className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            />
          )}
        </div>
      </div>,
      document.body
    )
    : null;

  return (
    <div
      className="max-w-[68rem] mx-auto"
      data-generation-studio="true"
      data-generation-has-input={hasGenerationInput ? 'true' : 'false'}
      data-generator-menu-direction={openDropdownUp ? 'up' : 'down'}
    >
      <Form {...form}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleGenerateClick();
          }}
          className="space-y-4"
        >

          {/* Main Input Container - All-in-Input */}
          <div
            data-generation-input-shell="true"
            data-generator-surface="full"
            className={`relative z-20 flex min-h-[140px] flex-col rounded-3xl p-4 transition-all duration-300 ${GENERATOR_FULL_SURFACE_CLASS}`}
            style={GENERATOR_FULL_SURFACE_STYLE}
          >
            <div ref={creationTypeTriggerRef} className="mb-4 flex items-center gap-5 border-b border-white/10 pb-3">
              {[
                { id: 'video', Icon: IconVideo },
                { id: 'image', Icon: IconPhoto },
              ].map(({ id, Icon }) => (
                <button
                  key={id}
                  type="button"
                  disabled={Boolean(lockedCreationType) && lockedCreationType !== id}
                  aria-pressed={creationType === id}
                  className={`flex items-center gap-2 border-b-2 px-2 py-1 text-sm font-semibold transition-colors ${creationType === id ? 'border-amber-400 text-white' : 'border-transparent text-neutral-400 hover:text-white'}`}
                  onClick={() => {
                    if (lockedCreationType && lockedCreationType !== id) return;
                    setFunctionMode(normalizeFunctionModeForCreationType(functionMode, id));
                    setCreationType(id);
                    setActiveMenu(null);
                  }}
                >
                  <Icon size={17} />
                  {t(`creationTypes.${id}`)}
                </button>
              ))}
            </div>

            {/* Top Section: Uploads + Textarea */}
            <div className={`flex gap-4 ${
              (creationType === 'image' && imageUploadSlots.length > 3)
              || (selectedModel?.startsWith('minimax-h3') && functionMode === 'reference')
              || (selectedModel === 'kling-v4' && ['frames', 'reference'].includes(functionMode))
                ? 'flex-col sm:flex-row'
                : ''
            }`}>

              {/* Dynamic Upload Area (Top-Left) */}
              {creationType === 'image' && (
                <div className="flex-shrink-0">
                  <div className="flex flex-wrap gap-2 max-w-[280px] sm:max-w-[360px]">
                    {imageUploadSlots.map((asset, index) => renderUploadSlot({
                      asset,
                      slotKey: asset?.id || `image-slot-${index}`,
                      label: asset ? '' : `${t('uploadSlot.reference')} ${index + 1}`,
                    }))}
                  </div>
                </div>
              )}

              {creationType === 'video' && functionMode === 'general' && (
                <div className="flex-shrink-0">
                  <div className="flex gap-2">
                    {renderUploadSlot({ asset: uploadedAssets[0], slotKey: 'upload-slot-image-1', label: 'Image1' })}
                  </div>
                </div>
              )}

              {creationType === 'video' && functionMode === 'frames' && (
                <div className="flex-shrink-0">
                  {selectedModel === 'kling-v4' ? (
                    <div className="flex flex-wrap items-center gap-2 max-w-[280px] sm:max-w-[360px]">
                      {Array.from({ length: Math.max(uploadedAssets.length, Math.min(getKlingV4MaxFrames(currentDurationObj?.value || 5), Math.max(2, uploadedAssets.length + 1))) }, (_, index) => renderUploadSlot({
                        asset: uploadedAssets[index],
                        slotKey: `upload-slot-kling-frame-${index}`,
                        label: index === 0 ? t('uploadSlot.klingFirstFrame') : index === uploadedAssets.length - 1 ? t('uploadSlot.klingLastFrame') : t('uploadSlot.klingKeyframe', { index: index + 1 }),
                      }))}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      {renderUploadSlot({ asset: uploadedAssets[0], slotKey: 'upload-slot-start', label: 'Start' })}
                      <IconArrowsRightLeft size={16} className="text-neutral-500" />
                      {renderUploadSlot({ asset: uploadedAssets[1], slotKey: 'upload-slot-end', label: 'End' })}
                    </div>
                  )}
                </div>
              )}

              {creationType === 'video' && ['reference', 'editing', 'extend', 'enhance'].includes(functionMode) && (
                <div className="flex-shrink-0">
                  <div className="flex flex-wrap gap-2 max-w-[240px]">
                    {referenceUploadSlots.map((asset, index) => renderUploadSlot({
                      asset,
                      slotKey: asset?.id || `${functionMode}-slot-${index}`,
                      label: asset ? '' : `${t('uploadSlot.reference')} ${index + 1}`,
                    }))}
                  </div>
                </div>
              )}

              {/* Text Input Area */}
              <div className="min-w-0 flex-grow pt-1">
                {isVsrMode ? (
                  <p className="min-h-[40px] text-left text-sm leading-6 text-neutral-400">
                    {t('promptPlaceholders.videoEnhance')}
                  </p>
                ) : (
                <FormField
                  control={control}
                  name="prompt"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div className="relative">
                          <Textarea
                            {...field}
                            id="image-prompt-textarea"
                            ref={(node) => {
                              textareaRef.current = node;
                              if (typeof field.ref === 'function') {
                                field.ref(node);
                              }
                            }}
                            placeholder={promptPlaceholderText}
                            className="w-full bg-transparent border-none text-white text-lg placeholder:text-neutral-500 focus:ring-0 focus:outline-none min-h-[40px] resize-none p-0 leading-relaxed custom-scrollbar"
                            style={{ boxShadow: 'none' }}
                            onChange={(event) => handlePromptFieldChange(event, field.onChange)}
                            onKeyUp={handlePromptFieldActivity}
                            onClick={handlePromptFieldActivity}
                            onFocus={handlePromptFieldActivity}
                            onBlur={() => {
                              field.onBlur();
                              window.setTimeout(closeReferenceMentionPicker, 120);
                            }}
                          />
                          {isReferenceMentionEnabled
                            && referenceMentionState.open
                            && referenceMentionState.source === 'typing'
                            && renderReferenceMentionMenu({
                              className: 'absolute',
                              style: {
                                left: referenceMentionState.anchor?.left ?? 0,
                                top: referenceMentionState.anchor?.top ?? 44,
                              },
                            })}
                        </div>
                      </FormControl>
                      {visibleInlineNotice && (
                        <div
                          role="alert"
                          className={[
                            'mt-2 flex w-full max-w-[760px] items-start gap-2 rounded-lg border px-3 py-2 text-xs leading-5 shadow-sm',
                            visibleInlineNotice.variant === 'error'
                              ? 'border-red-500/25 bg-red-950/20 text-red-200'
                              : 'border-amber-500/25 bg-amber-950/20 text-amber-100',
                          ].join(' ')}
                        >
                          <span className="min-w-0 flex-1 break-words">{visibleInlineNotice.message}</span>
                          {visibleInlineNotice.dismissible && (
                            <button
                              type="button"
                              aria-label="Dismiss message"
                              className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-current/55 transition-colors hover:bg-white/10 hover:text-current"
                              onClick={handleInlineNoticeDismiss}
                            >
                              <IconX size={12} />
                            </button>
                          )}
                        </div>
                      )}
                    </FormItem>
                  )}
                />
                )}
              </div>
            </div>

            {/* Hidden File Input */}
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              disabled={requiresDraftSourceTaskId}
              accept={[
                ...getAcceptedMimeTypesForMode({
                  modelName: selectedModel,
                  creationType,
                  functionMode,
                }),
                ...(currentInputRules?.allowedExts || []),
              ].join(',')}
              onChange={handleFileChange}
            />

            {/* Middle Spacer - Auto Fill Height */}
            <div className="flex-grow min-h-[20px]"></div>

            {/* Error Message Area */}
            {/* Bottom Toolbar Section */}
            <div className="relative mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.075] pt-4">

              {/* Left: 6-Level Menu */}
              <div className="flex flex-wrap items-center gap-2">

                {/* Function Mode */}
                <div ref={functionModeTriggerRef} className="relative menu-dropdown-trigger">
                  <button
                    type="button"
                    className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors ${GENERATOR_TOOLBAR_CONTROL_CLASS}`}
                    onClick={(e) => { e.stopPropagation(); toggleMenu('functionMode'); }}
                  >
                    <ActiveFunctionModeIcon size={14} />
                    <span>{t(`functionModes.${activeFunctionModeOption?.labelKey || 'general'}`)}</span>
                    <IconChevronDown size={12} />
                  </button>
                  {activeMenu === 'functionMode' && (
                    <div className={`absolute ${openDropdownUp ? 'bottom-full mb-2' : 'top-full mt-2'} left-0 z-50 w-48 overflow-hidden rounded-xl menu-dropdown-content ${GENERATOR_MENU_PANEL_CLASS}`}>
                      {functionModeOptions.map((option) => {
                        const OptionIcon = option.Icon;
                        return (
                          <button
                            key={option.id}
                            type="button"
                            className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs ${GENERATOR_MENU_ITEM_HOVER_CLASS} ${functionMode === option.id ? GENERATOR_MENU_ITEM_ACTIVE_CLASS : 'text-white/90'}`}
                            onClick={() => { handleFunctionModeSelect(option.id); toggleMenu('functionMode'); }}
                          >
                            <OptionIcon size={14} /> {t(`functionModes.${option.labelKey}`)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div ref={modelTriggerRef} className="relative menu-dropdown-trigger">
                  <button
                    type="button"
                    className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors ${GENERATOR_TOOLBAR_CONTROL_CLASS}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMenu('model');
                    }}
                  >
                    {getModelLogo(selectedModel) ? (
                      <img
                        src={getModelLogo(selectedModel)}
                        alt=""
                        className="w-4 h-4 object-contain"
                        style={selectedModel.includes('grok') ? { filter: 'invert(1) brightness(1.2)' } : {}}
                      />
                    ) : (
                      <IconBolt size={14} />
                    )}
                    <span className="min-w-0 truncate">{selectedModelConfig?.displayName || selectedModel}</span>
                    <IconChevronDown size={12} />
                  </button>
                  {activeMenu === 'model' && (isVsrMode ? (
                    <div
                      ref={modelMenuRef}
                      className={`absolute ${openDropdownUp ? 'bottom-full mb-2' : 'top-full mt-2'} left-0 z-50 w-72 max-w-[calc(100vw-2rem)] rounded-xl p-1 menu-dropdown-content ${GENERATOR_MENU_PANEL_CLASS}`}
                    >
                      {availableModels.map((model) => (
                        <button
                          key={model.key}
                          type="button"
                          data-model-key={model.key}
                          className={`flex w-full items-start gap-2 rounded-lg px-3 py-3 text-left text-xs ${GENERATOR_MENU_ITEM_HOVER_CLASS} ${selectedModel === model.key ? GENERATOR_MENU_ITEM_ACTIVE_CLASS : 'text-white/90'}`}
                          onClick={() => { setSelectedModel(model.key); toggleMenu('model'); }}
                        >
                          <img src={getModelLogo(model.key)} alt="" className="mt-0.5 h-4 w-4 shrink-0 object-contain" />
                          <span className="min-w-0">
                            <span className="flex flex-wrap items-center gap-1.5">
                              <span className="font-semibold">{model.displayName}</span>
                              {modelMenuBadgeKeys.freeModelKeys.has(model.key) && (
                                <span className={`inline-flex shrink-0 items-center rounded-md px-1.5 py-[1px] text-[9px] font-semibold leading-none ${MODEL_MENU_BADGE_STYLES.free}`}>
                                  {t('badges.free')}
                                </span>
                              )}
                              {modelMenuBadgeKeys.unlimitedModelKeys.has(model.key) && (
                                <span className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-[1px] text-[9px] font-semibold leading-none ${MODEL_MENU_BADGE_STYLES.unlimited}`} style={MODEL_MENU_UNLIMITED_BADGE_STYLE}>
                                  {t('badges.unlimitedFree')}
                                </span>
                              )}
                            </span>
                            <span className="mt-1 block text-[11px] leading-4 text-white/50">{getModelMenuDescription(model)}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div
                      className={`absolute ${
                        openDropdownUp
                          ? creationType === 'image'
                            ? 'bottom-full mb-2 sm:items-end'
                            : 'bottom-full mb-2 sm:h-[min(365px,calc(100vh-6rem))] sm:items-stretch'
                          : 'top-full mt-2 sm:items-start'
                      } left-0 z-50 flex w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] flex-col gap-2 overflow-visible sm:w-auto sm:flex-row menu-dropdown-content`}
                    >
                      <div className={`w-full rounded-xl p-1 sm:w-[310px] lg:w-[360px] ${GENERATOR_MENU_PANEL_CLASS} ${openDropdownUp && creationType !== 'image' ? 'sm:h-full' : ''}`}>
                        <div
                          className={`${
                            creationType === 'image'
                              ? 'sm:max-h-[244px]'
                              : openDropdownUp
                                ? 'sm:h-full sm:max-h-none'
                                : 'sm:max-h-[300px]'
                          } ${creationType === 'image' ? 'max-h-[244px]' : 'max-h-[300px]'} overflow-y-auto custom-scrollbar model-menu-scrollbar`}
                          onWheel={handleModelMenuWheel}
                        >
                          {modelMenuGroups.map((group) => {
                            const groupModel = group.models.find((model) => model.key === selectedModel) || group.models[0];
                            const logo = getModelLogo(groupModel?.key);
                            const isActiveGroup = activeModelGroup?.key === group.key;
                            const isSelectedGroup = selectedModelGroupKey === group.key;
                            const isGrok = groupModel?.key?.includes('grok');
                            const groupDescription = getModelMenuGroupDescription(group);

                            return (
                              <button
                                key={group.key}
                                data-model-group-key={group.key}
                                type="button"
                                className={`flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-xs transition-colors ${
                                  isActiveGroup
                                    ? GENERATOR_MENU_ITEM_ACTIVE_CLASS
                                    : isSelectedGroup
                                      ? GENERATOR_MENU_ITEM_SELECTED_CLASS
                                      : `text-white/80 ${GENERATOR_MENU_ITEM_HOVER_CLASS}`
                                }`}
                                onMouseEnter={() => setActiveModelGroupKey(group.key)}
                                onFocus={() => setActiveModelGroupKey(group.key)}
                                onClick={(event) => {
                                  event.preventDefault();
                                  setActiveModelGroupKey(group.key);
                                }}
                              >
                                {logo ? (
                                  <img
                                    src={logo}
                                    alt=""
                                    className="mt-0.5 h-4 w-4 shrink-0 object-contain"
                                    style={isGrok ? { filter: 'invert(1) brightness(1.2)' } : {}}
                                  />
                                ) : (
                                  <div className="mt-0.5 h-4 w-4 shrink-0"></div>
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                                    <span className="min-w-0 truncate font-medium">{group.label}</span>
                                    {group.hasFreeBadge && (
                                      <span
                                        className={`inline-flex shrink-0 items-center rounded-md px-1.5 py-[1px] text-[9px] font-semibold leading-none ${MODEL_MENU_BADGE_STYLES.free}`}
                                      >
                                        {t('badges.free')}
                                      </span>
                                    )}
                                    {group.hasUnlimitedBadge && (
                                      <span
                                        className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-[1px] text-[9px] font-semibold leading-none ${MODEL_MENU_BADGE_STYLES.unlimited}`}
                                        style={MODEL_MENU_UNLIMITED_BADGE_STYLE}
                                      >
                                        {t('badges.unlimitedFree')}
                                      </span>
                                    )}
                                    {group.hasHotBadge && (
                                      <span
                                        className={`inline-flex shrink-0 items-center rounded-md px-1.5 py-[1px] text-[9px] font-semibold leading-none ${MODEL_MENU_BADGE_STYLES.hot}`}
                                      >
                                        {t('badges.hot')}
                                      </span>
                                    )}
                                  </div>
                                  {groupDescription && (
                                    <p className="mt-0.5 whitespace-nowrap text-[11px] leading-4 text-white/45">
                                      {groupDescription}
                                    </p>
                                  )}
                                </div>
                                <IconChevronDown size={12} className="mt-1 hidden shrink-0 -rotate-90 text-white/45 sm:block" />
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div
                        ref={modelMenuRef}
                        className={`${creationType === 'image' ? `max-h-[244px] sm:absolute sm:left-full sm:ml-2 ${openDropdownUp && activeModelGroup?.key !== 'botgo-image' ? 'sm:bottom-0' : 'sm:top-0'}` : 'max-h-[365px]'} w-full overflow-y-auto rounded-xl p-1 custom-scrollbar model-menu-scrollbar sm:w-[390px] lg:w-[430px] ${GENERATOR_MENU_PANEL_CLASS} ${
                          openDropdownUp
                            ? creationType === 'image'
                              ? 'sm:max-h-[min(365px,calc(100dvh-6rem))]'
                              : 'sm:h-full sm:max-h-none'
                            : creationType === 'image'
                              ? 'sm:max-h-[min(365px,calc(100dvh-6rem))]'
                              : 'sm:max-h-[min(calc(100vh-6rem),365px)]'
                        }`}
                        onWheel={handleModelMenuWheel}
                      >
                        {(activeModelGroup?.models || []).map(model => {
                          const logo = getModelLogo(model.key);
                          const isGrok = model.key.includes('grok');
                          const modelDescription = getModelMenuDescription(model);
                          const modelSpecs = getModelMenuSpecs(model);

                          return (
                            <button
                              key={model.key}
                              data-model-key={model.key}
                              type="button"
                              className={`w-full rounded-lg px-3 py-2.5 text-left text-xs transition-colors ${GENERATOR_MENU_ITEM_HOVER_CLASS} ${selectedModel === model.key ? GENERATOR_MENU_ITEM_ACTIVE_CLASS : 'text-white/90'} ${model.comingSoon && selectedModel !== model.key ? 'text-white/80' : ''}`}
                              onClick={() => { setSelectedModel(model.key); toggleMenu('model'); }}
                            >
                              <div className="flex items-start gap-2">
                                {logo ? (
                                  <img
                                    src={logo}
                                    alt=""
                                    className="mt-0.5 h-4 w-4 shrink-0 object-contain"
                                    style={isGrok ? { filter: 'invert(1) brightness(1.2)' } : {}}
                                  />
                                ) : (
                                  <div className="mt-0.5 h-4 w-4 shrink-0"></div>
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                                    <span className="min-w-0 truncate font-medium">{model.displayName}</span>
                                    {model.key === 'minimax-h3-max' && <span className={`inline-flex shrink-0 items-center rounded-md px-1.5 py-[1px] text-[9px] font-semibold leading-none ${MODEL_MENU_BADGE_STYLES.new}`}>{t('badges.fast')}</span>}
                                    {NEW_BADGE_MODEL_KEYS.has(model.key) && (
                                      <span
                                        className={`inline-flex shrink-0 items-center rounded-md px-1.5 py-[1px] text-[9px] font-semibold leading-none ${MODEL_MENU_BADGE_STYLES.new}`}
                                      >
                                        {t('badges.new')}
                                      </span>
                                    )}
                                    {modelMenuBadgeKeys.freeModelKeys.has(model.key) && (
                                      <span
                                        className={`inline-flex shrink-0 items-center rounded-md px-1.5 py-[1px] text-[9px] font-semibold leading-none ${MODEL_MENU_BADGE_STYLES.free}`}
                                      >
                                        {t('badges.free')}
                                      </span>
                                    )}
                                    {modelMenuBadgeKeys.unlimitedModelKeys.has(model.key) && (
                                      <span
                                        className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-[1px] text-[9px] font-semibold leading-none ${MODEL_MENU_BADGE_STYLES.unlimited}`}
                                        style={MODEL_MENU_UNLIMITED_BADGE_STYLE}
                                      >
                                        {t('badges.unlimitedFree')}
                                      </span>
                                    )}
                                    {model.comingSoon && (
                                      <span
                                        className={`inline-flex shrink-0 items-center rounded-md px-1.5 py-[1px] text-[9px] font-semibold leading-none ${MODEL_MENU_BADGE_STYLES.comingSoon}`}
                                      >
                                        {t('badges.comingSoon')}
                                      </span>
                                    )}
                                  </div>
                                  {modelDescription && (
                                    <p className="mt-0.5 text-xs leading-4 text-white/45">
                                      {modelDescription}
                                    </p>
                                  )}
                                  <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[10px] leading-none text-white/65">
                                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                      <IconDeviceTv size={12} stroke={1.8} />
                                      {modelSpecs.resolution}
                                    </span>
                                    {model.type === 'video' && modelSpecs.duration && (
                                      <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                        <IconClock size={12} stroke={1.8} />
                                        {modelSpecs.duration}
                                      </span>
                                    )}
                                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                      <IconRefresh size={12} stroke={1.8} />
                                      {modelSpecs.generationTime}
                                    </span>
                                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                      <IconBolt size={12} stroke={1.8} />
                                      {modelSpecs.credits}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Level 4: Specs (Aspect & Quality) - 即梦风格 */}
                <div ref={specsTriggerRef} className="relative menu-dropdown-trigger z-[60]">
                  <button
                    type="button"
                    disabled={isSpecsMenuDisabled}
                    className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs transition-colors ${GENERATOR_TOOLBAR_CONTROL_CLASS} ${
                      isSpecsMenuDisabled
                        ? 'cursor-not-allowed opacity-85'
                        : 'hover:bg-white/[0.15]'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isSpecsMenuDisabled) {
                        toggleMenu('specs');
                      }
                    }}
                  >
                    {/* Dynamic Trigger Icon based on actual selected aspect ratio */}
                    <div className="border border-current rounded-[1px]" style={{
                      width: (() => {
                        const [w, h] = (watchedSize || '16:9').split(':').map(Number);
                        const r = (w || 16) / (h || 9);
                        return r === 1 ? '10px' : (r < 1 ? '8px' : '12px');
                      })(),
                      height: (() => {
                        const [w, h] = (watchedSize || '16:9').split(':').map(Number);
                        const r = (w || 16) / (h || 9);
                        return r === 1 ? '10px' : (r < 1 ? '12px' : '8px');
                      })()
                    }}></div>
                    <span>{specTriggerLabel}</span>
                    {!isSpecsMenuDisabled && (
                      <IconChevronDown size={12} className="ml-1" />
                    )}
                  </button>
                  {activeMenu === 'specs' && !isSpecsMenuDisabled && (() => {
                    // 动态计算菜单宽度 - 基于模型类别
                    // 窄: Sora 2系列
                    // 中宽: Grok Imagine (5项比例)
                    // 中等: Veo系列 (2项比例 + 3项分辨率)
                    // 宽: Nano Banana系列 (10项比例)
                    const isVeo = selectedModel.includes('veo');
                    const isNanoBanana = selectedModel.includes('nano-banana');

                    // 计算宽度 class
                    let menuWidthClass = 'w-[280px]'; // 默认窄
                    if (selectedModel === 'grok-imagine' || hasDenseAspectOptions) {
                      menuWidthClass = 'w-[360px] max-w-[calc(100vw-1rem)]';
                    }
                    if (isVeo) menuWidthClass = 'w-[320px]'; // 中等
                    if (isNanoBanana || creationType === 'image') menuWidthClass = 'w-[420px]'; // 宽 (Image模式也用宽)
                    const mobileFilteringMenuClass = (isBotgoContentFilteringModel(selectedModel) || selectedModel.startsWith('seedance-2.'))
                      ? 'max-sm:fixed max-sm:inset-x-2 max-sm:bottom-4 max-sm:top-auto max-sm:my-0 max-sm:w-auto max-sm:max-h-[calc(100dvh-2rem)] max-sm:overflow-y-auto'
                      : '';
                    if (selectedModel === 'nano-banana-2') menuWidthClass = 'w-[500px] max-w-[calc(100vw-1rem)]'; // Nano Banana 2 分辨率较多，额外加宽

                    return (
                      <div
                        className={`absolute ${openDropdownUp ? 'bottom-full mb-2' : 'top-full mt-2'} left-0 z-[200] rounded-xl p-3 menu-dropdown-content ${GENERATOR_MENU_PANEL_CLASS} ${menuWidthClass} ${mobileFilteringMenuClass}`}
                      >
                        {/* Layer 1: Aspect Ratio - 即梦风格：图标+文字 */}
                        {aspectOptions.length > 0 && (
                          <div className="mb-3">
                            <div className="text-left text-[10px] text-neutral-500 mb-1.5 px-1 uppercase tracking-wider font-semibold">Aspect Ratio</div>
                            {/* 根据选项数量决定布局：2项=左右，10项=上下 */}
                            <div
                              className={`rounded-lg p-1 ${GENERATOR_MENU_TRACK_CLASS} ${aspectOptions.length <= 3
                                ? 'flex'
                                : aspectOptions.length === 4
                                  ? 'grid grid-cols-4 gap-1'
                                  : 'grid grid-cols-5 gap-1'
                                }`}
                            >
                              {aspectOptions.map(opt => {
                                const isSelected = watchedSize === opt.id;
                                const isDisabled =
                                  opt.isDisabled || (sourceDrivenHasConcreteSourceSpecs
                                    ? !isAspectCompatible(opt.id)
                                    : false);
                                // 计算图标尺寸
                                const [w, h] = opt.id.split(':').map(Number);
                                const ratio = opt.id === 'adaptive' ? 1 : w / h;
                                const iconW = ratio >= 1 ? 16 : 12;
                                const iconH = ratio <= 1 ? 16 : 12;

                                // 2-3项时：图标在左，文字在右
                                if (aspectOptions.length <= 3) {
                                  return (
                                    <button
                                      key={opt.id}
                                      type="button"
                                      disabled={isDisabled}
                                      className={`flex-1 py-2.5 px-3 rounded-md text-xs font-medium transition-all flex items-center justify-center gap-2
                                      ${isSelected
                                          ? GENERATOR_MENU_TRACK_SELECTED_CLASS
                                          : isDisabled
                                            ? 'bg-transparent text-gray-600/70 cursor-not-allowed'
                                            : 'bg-transparent text-gray-500 hover:text-gray-300'}`}
                                      onClick={() => {
                                        if (isDisabled) return;
                                        setValue('size', opt.id);
                                        const nextResolution = getDefaultResolutionForAspect(opt.id);
                                        const nextSize = getSizeForSpec(
                                          selectedModelCaps.allowedSizesByAspect,
                                          opt.id,
                                          nextResolution
                                        );
                                        if (nextSize) {
                                          setIntendedResolutionLabel(nextResolution);
                                          setSelectedResolution(buildResolutionOptionId({
                                            aspectRatio: opt.id,
                                            resolutionLabel: nextResolution,
                                            apiSize: nextSize,
                                          }));
                                        }
                                      }}
                                    >
                                      <div
                                        className="border border-current rounded-[2px]"
                                        style={{ width: `${iconW}px`, height: `${iconH}px` }}
                                      />
                                      <span>{opt.label}</span>
                                    </button>
                                  );
                                }

                                // 多项时：图标在上，文字在下 (即梦风格)
                                return (
                                  <button
                                    key={opt.id}
                                    type="button"
                                    disabled={isDisabled}
                                    className={`min-w-0 py-2 px-1.5 rounded-md text-[10px] font-medium transition-all flex flex-col items-center justify-center gap-1
                                    ${isSelected
                                        ? GENERATOR_MENU_TRACK_SELECTED_CLASS
                                        : isDisabled
                                          ? 'bg-transparent text-gray-600/70 cursor-not-allowed'
                                          : 'bg-transparent text-gray-500 hover:text-gray-300'}`}
                                    onClick={() => {
                                      if (isDisabled) return;
                                      setValue('size', opt.id);
                                      const nextResolution = getDefaultResolutionForAspect(opt.id);
                                      const nextSize = getSizeForSpec(
                                        selectedModelCaps.allowedSizesByAspect,
                                        opt.id,
                                        nextResolution
                                      );
                                      if (nextSize) {
                                        setIntendedResolutionLabel(nextResolution);
                                        setSelectedResolution(buildResolutionOptionId({
                                          aspectRatio: opt.id,
                                          resolutionLabel: nextResolution,
                                          apiSize: nextSize,
                                        }));
                                      }
                                    }}
                                  >
                                    <div
                                      className="border border-current rounded-[2px]"
                                      style={{ width: `${iconW}px`, height: `${iconH}px` }}
                                    />
                                    <span>{opt.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Layer 2: Resolution - 即梦风格 (Video模式去掉了Video Length层) */}
                        <div className={creationType === 'image' ? 'mb-3' : ''}>
                          <div className="text-left text-[10px] text-neutral-500 mb-1.5 px-1 uppercase tracking-wider font-semibold">Resolution</div>
                          {/* 深灰色背景行 + 纯文字标签，增加行高 */}
                          <div className={`flex rounded-lg p-1 ${GENERATOR_MENU_TRACK_CLASS}`}>
                            {displayResolutionLabels.map(label => {
                              const sizesByAspect = selectedModelCaps.allowedSizesByAspect || {};
                              const resConstraints = selectedModelCaps.resolutionAspectConstraints || {};
                              const labelConstraints = resConstraints[label];
                              const currentResolutionOption = currentQualityOptions.find(
                                (option) => option.resolutionLabel === label
                              );
                              const isTemporarilyDisabled = temporarilyDisabledResolutionSet.has(normalizeValue(label)) || isVsrResolutionDisabled(label);
                              const compatibleAspect = sourceDrivenHasConcreteSourceSpecs
                                ? getCompatibleAspectForResolution(label)
                                : null;
                              const isResolutionDisabled =
                                isTemporarilyDisabled
                                || (
                                  sourceDrivenHasConcreteSourceSpecs
                                  && !compatibleAspect
                                );
                              const effectiveLabel = intendedResolutionLabel || currentResolutionObj?.resolutionLabel;
                              const isSelected = effectiveLabel === label;
                              return (
                                <button
                                  key={label}
                                  type="button"
                                  disabled={isResolutionDisabled}
                                  title={isTemporarilyDisabled ? 'Temporarily unavailable' : undefined}
                                  className={`flex-1 py-2.5 px-3 rounded-md text-xs font-medium transition-all text-center whitespace-nowrap
                                  ${isSelected
                                      ? GENERATOR_MENU_TRACK_SELECTED_CLASS
                                      : isResolutionDisabled
                                        ? 'bg-transparent text-gray-600/70 cursor-not-allowed'
                                        : 'bg-transparent text-gray-500 hover:text-gray-300'}`}
                                  onClick={() => {
                                    if (isResolutionDisabled) return;
                                    setIntendedResolutionLabel(label);
                                    if (sourceDrivenHasConcreteSourceSpecs && compatibleAspect) {
                                      const apiSize = getSizeForSpec(sizesByAspect, compatibleAspect, label);
                                      if (apiSize) {
                                        setValue('size', compatibleAspect);
                                        setSelectedResolution(buildResolutionOptionId({
                                          aspectRatio: compatibleAspect,
                                          resolutionLabel: label,
                                          apiSize,
                                        }));
                                      }
                                    } else if (currentResolutionOption) {
                                      // 当前比例支持此分辨率，直接设置
                                      setSelectedResolution(currentResolutionOption.id);
                                    } else if (labelConstraints) {
                                      // 当前比例不支持，自动切换到首个兼容比例（如 4K 自动换到 16:9）
                                      const allRatios = selectedModelCaps.allowedAspectRatios || [];
                                      const targetAspect = labelConstraints.find(r => allRatios.includes(r));
                                      if (targetAspect) {
                                        const newSize = (sizesByAspect[targetAspect] || {})[label];
                                        setValue('size', targetAspect);
                                        if (newSize) {
                                          setSelectedResolution(buildResolutionOptionId({
                                            aspectRatio: targetAspect,
                                            resolutionLabel: label,
                                            apiSize: newSize,
                                          }));
                                        }
                                      }
                                    }
                                  }}
                                >
                                  <span className={isResolutionDisabled ? 'line-through decoration-white/20' : ''}>
                                    {label}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {creationType === 'video' && selectedModel.startsWith('seedance-2.') && (
                          <ContentFilteringControl
                            kind="seedance"
                            checked={privacyUnrestricted}
                            onChange={setPrivacyUnrestricted}
                            trackClassName={GENERATOR_MENU_TRACK_CLASS}
                            tooltipClassName={GENERATOR_TOOLTIP_SURFACE_CLASS}
                          />
                        )}

                        {/* Layer 3: Size (Image Only) - 即梦风格统一样式，增加行高 */}
                        {creationType === 'image' && (
                          <div className="border-t border-[#44352D]/50 pt-2">
                            <div className="text-left text-[10px] text-neutral-500 mb-1.5 px-1 uppercase tracking-wider font-semibold">Size</div>
                            <div className={`flex items-center justify-center gap-3 rounded-lg p-1 py-2.5 ${GENERATOR_MENU_TRACK_CLASS}`}>
                              {isAutoAspectResolution ? (
                                <span className="text-white font-medium text-xs uppercase tracking-[0.18em]">Auto</span>
                              ) : (
                                <>
                                  <div className="flex items-center gap-1.5 text-xs">
                                    <span className="text-gray-500 font-medium">W</span>
                                    <span className="text-white font-mono">{currentResolutionObj?.apiSize?.split('x')[0] || '-'}</span>
                                  </div>
                                  <span className="text-gray-600">×</span>
                                  <div className="flex items-center gap-1.5 text-xs">
                                    <span className="text-gray-500 font-medium">H</span>
                                    <span className="text-white font-mono">{currentResolutionObj?.apiSize?.split('x')[1] || '-'}</span>
                                  </div>
                                  <span className="text-gray-600 text-[10px]">px</span>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                        {canSelectQuality && (
                          <div className="mt-4 border-t border-[#44352D]/60 pt-4">
                            <div className="text-left text-[10px] text-neutral-500 mb-1.5 px-1 uppercase tracking-wider font-semibold">{t('moreMenu.imageQuality.title')}</div>
                            <div className={`mt-2.5 grid grid-flow-col auto-cols-fr gap-1 rounded-xl p-0.5 ${GENERATOR_MENU_TRACK_CLASS}`}>
                              {selectedModelCaps.allowedQualities.map((q) => {
                                const isSelected = selectedQuality === q;
                                const label = t(`moreMenu.imageQuality.${q}`);
                                return (
                                  <button
                                    key={q}
                                    type="button"
                                    className={`h-9 rounded-lg px-1 py-0 text-xs font-medium transition-all ${isSelected
                                      ? GENERATOR_MENU_TRACK_SELECTED_CLASS
                                      : 'bg-transparent text-white/45 hover:text-white/80'
                                      }`}
                                    onClick={() => setSelectedQuality(q)}
                                  >
                                    {label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {isBotgoContentFilteringModel(selectedModel) && (
                          <ContentFilteringControl
                            checked={canUseNoContentFiltering && noContentFiltering}
                            onChange={(nextValue) => {
                              if (nextValue && !canUseNoContentFiltering) {
                                setShowUpgradeModal(true);
                                return;
                              }
                              setNoContentFiltering(nextValue);
                            }}
                            trackClassName={GENERATOR_MENU_TRACK_CLASS}
                            tooltipClassName={GENERATOR_TOOLTIP_SURFACE_CLASS}
                          />
                        )}

                      </div>
                    );
                  })()}
                </div>

	                {/* Level 5: Duration (Video Only) */}
	                {creationType === 'video' && !isVsrMode && currentDurationOptions.length > 0
                    && !(selectedModel.startsWith('gemini-omni') && functionMode === 'editing' && hasVideoReferenceInput) && (
	                  <div ref={durationTriggerRef} className="relative menu-dropdown-trigger">
	                    <button
	                      type="button"
	                      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors ${GENERATOR_TOOLBAR_CONTROL_CLASS} ${isSourceDrivenVideoDurationLocked
	                        ? 'cursor-not-allowed opacity-85'
	                        : 'hover:bg-white/[0.15]'
	                        }`}
	                      disabled={isSourceDrivenVideoDurationLocked}
	                      onClick={(e) => {
	                        e.stopPropagation();
	                        if (!isSourceDrivenVideoDurationLocked) {
	                          toggleMenu('duration');
	                        }
	                      }}
	                    >
	                      <IconClock size={14} />
	                      <span>{currentDurationObj?.label || selectedDuration}</span>
	                      {!isSourceDrivenVideoDurationLocked && <IconChevronDown size={12} />}
	                    </button>
	                    {activeMenu === 'duration' && !isSourceDrivenVideoDurationLocked && (
                      <div
                        className={`absolute ${openDropdownUp ? 'bottom-full mb-2' : 'top-full mt-2'} left-0 z-50 rounded-xl menu-dropdown-content ${GENERATOR_MENU_PANEL_CLASS} ${hasDenseDurationOptions
                          ? 'w-[320px] max-w-[calc(100vw-1rem)] p-3'
                          : 'w-24 overflow-hidden max-h-64 overflow-y-auto custom-scrollbar model-menu-scrollbar'
                          }`}
                      >
                        {hasDenseDurationOptions ? (
                          <>
                            <div className="text-left text-[10px] text-neutral-500 mb-1.5 px-1 uppercase tracking-wider font-semibold">
                              Duration
                            </div>
                            <div className={`grid ${durationGridCols} gap-1 rounded-lg p-1 ${GENERATOR_MENU_TRACK_CLASS}`}>
                              {currentDurationOptions.map((dur) => {
                                const isSelected = selectedDuration === dur.id;
                                return (
                                  <button
                                    key={dur.id}
                                    type="button"
                                    className={`rounded-md px-2 py-2 text-[11px] font-medium transition-all text-center ${isSelected
                                      ? GENERATOR_MENU_TRACK_SELECTED_CLASS
                                      : 'bg-transparent text-gray-400 hover:text-gray-200'
                                      }`}
                                    onClick={() => { setSelectedDuration(dur.id); toggleMenu('duration'); }}
                                  >
                                    {dur.label}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        ) : currentDurationOptions.map(dur => (
                          <button
                            key={dur.id}
                            type="button"
                            className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs ${GENERATOR_MENU_ITEM_HOVER_CLASS} ${selectedDuration === dur.id ? GENERATOR_MENU_ITEM_ACTIVE_CLASS : 'text-white/90'}`}
                            onClick={() => { setSelectedDuration(dur.id); toggleMenu('duration'); }}
                          >
                            <IconClock size={14} className={selectedDuration === dur.id ? 'text-white/70' : 'text-white/50'} />
                            {dur.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {!isVsrMode && (canOpenAssetSourceMenu || isReferenceMentionEnabled) && (
                  <div ref={assetSourceTriggerRef} className="relative menu-dropdown-trigger asset-source-trigger reference-mention-trigger">
                    <button
                      type="button"
                      data-generation-asset-source="true"
                      aria-label={t('assetSource.open')}
                      title={t('assetSource.open')}
                      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors ${GENERATOR_TOOLBAR_CONTROL_CLASS}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        toggleMenu('assetSource');
                        closeReferenceMentionPicker();
                      }}
                    >
                      <IconAt size={15} />
                    </button>

                    {activeMenu === 'assetSource' && renderAssetSourceMenu()}
                  </div>
                )}

                {isVsrMode && (
                  <div ref={fpsTriggerRef} className="relative menu-dropdown-trigger">
                    <button
                      type="button"
                      aria-label="Output frame rate"
                      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors ${GENERATOR_TOOLBAR_CONTROL_CLASS}`}
                      onClick={(event) => { event.stopPropagation(); toggleMenu('fps'); }}
                    >
                      <span>{selectedFps} fps</span>
                      <IconChevronDown size={12} />
                    </button>
                    {activeMenu === 'fps' && (
                      <div className={`absolute ${openDropdownUp ? 'bottom-full mb-2' : 'top-full mt-2'} left-0 z-50 w-28 overflow-hidden rounded-xl p-1 menu-dropdown-content ${GENERATOR_MENU_PANEL_CLASS}`}>
                        {[30, 60, 120].map((fps) => (
                          <button
                            key={fps}
                            type="button"
                            className={`w-full rounded-lg px-3 py-2 text-left text-xs ${selectedFps === fps ? GENERATOR_MENU_ITEM_ACTIVE_CLASS : `text-white/90 ${GENERATOR_MENU_ITEM_HOVER_CLASS}`}`}
                            onClick={() => { setSelectedFps(fps); toggleMenu('fps'); }}
                          >
                            {fps} fps
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {isVsrMode && (canOpenAssetSourceMenu || isReferenceMentionEnabled) && (
                  <div ref={assetSourceTriggerRef} className="relative menu-dropdown-trigger asset-source-trigger reference-mention-trigger">
                    <button
                      type="button"
                      data-generation-asset-source="true"
                      aria-label={t('assetSource.open')}
                      title={t('assetSource.open')}
                      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors ${GENERATOR_TOOLBAR_CONTROL_CLASS}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        toggleMenu('assetSource');
                        closeReferenceMentionPicker();
                      }}
                    >
                      <IconAt size={15} />
                    </button>
                    {activeMenu === 'assetSource' && renderAssetSourceMenu()}
                  </div>
                )}

                {/* Level 6: More */}
                {!isVsrMode && <div ref={moreMenuTriggerRef} className="relative menu-dropdown-trigger">
                  <button
                    type="button"
                    aria-label={t('moreMenu.more')}
                    className="flex items-center gap-1.5 rounded-full bg-white/[0.055] px-2 py-1.5 text-white/58 transition-colors hover:bg-white/[0.12] hover:text-white/88"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMenu('more');
                    }}
                  >
                    <IconDots size={16} />
                  </button>
                  {activeMenu === 'more' && (
                    <TooltipProvider delayDuration={150}>
                      <div className={`absolute ${openDropdownUp ? 'bottom-full mb-2' : 'top-full mt-2'} left-0 z-50 max-h-[min(calc(100vh-6rem),36rem)] w-[380px] max-w-[calc(100vw-1rem)] overflow-y-auto rounded-2xl p-4 menu-dropdown-content custom-scrollbar model-menu-scrollbar ${GENERATOR_MENU_PANEL_CLASS}`}>
                        <div>
                          <div>
                            <div className="text-left text-sm font-medium text-white/90">{t('moreMenu.outputNumber')}</div>
                            <div className={`mt-2.5 grid grid-cols-4 gap-1 rounded-xl p-0.5 ${GENERATOR_MENU_TRACK_CLASS}`}>
                              {OUTPUT_COUNT_OPTIONS.map((count) => {
                                const isSelected = effectiveOutputCount === count;
                                const isDisabled = isUnlimitedOutputCountLocked && count > 1;
                                return (
                                  <button
                                    key={count}
                                    type="button"
                                    disabled={isDisabled}
                                    className={`h-9 rounded-lg px-3 py-0 text-sm font-medium transition-all ${isSelected
                                      ? GENERATOR_MENU_TRACK_SELECTED_CLASS
                                      : isDisabled
                                        ? 'cursor-not-allowed bg-transparent text-white/20'
                                        : 'bg-transparent text-white/45 hover:text-white/80'
                                      }`}
                                    onClick={() => {
                                      if (!isDisabled) setOutputCount(count);
                                    }}
                                  >
                                    {count}
                                  </button>
                                );
                              })}
                            </div>
                            {isUnlimitedOutputCountLocked && (
                              <p className="mt-2 text-xs leading-relaxed text-white/45">
                                {t('moreMenu.unlimitedOutputLimitDescription')}
                              </p>
                            )}
                          </div>
                        </div>

                        {canSelectGrokMode && (
                          <div className="mt-4 border-t border-[#44352D]/60 pt-4">
                            <div className="flex items-center gap-1.5">
                              <div className="text-left text-sm font-medium text-white/90">{t('moreMenu.grokMode.title')}</div>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="text-white/35 transition-colors hover:text-white/60"
                                    aria-label={t('moreMenu.grokMode.info')}
                                  >
                                    <IconInfoCircle size={13} />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className={`max-w-80 ${GENERATOR_TOOLTIP_SURFACE_CLASS}`}>
                                  {t('moreMenu.grokMode.info')}
                                </TooltipContent>
                              </Tooltip>
                            </div>
                            <div className={`mt-2.5 grid grid-cols-3 gap-1 rounded-xl p-0.5 ${GENERATOR_MENU_TRACK_CLASS}`}>
                              {GROK_IMAGINE_MODE_OPTIONS.map((mode) => {
                                const isSelected = selectedGrokMode === mode;
                                const isDisabled = mode === 'spicy' && hasImageInputAssets;
                                return (
                                  <button
                                    key={mode}
                                    type="button"
                                    disabled={isDisabled}
                                    title={isDisabled ? t('moreMenu.grokMode.info') : undefined}
                                    className={`h-9 rounded-lg px-3 py-0 text-sm font-medium transition-all ${isSelected
                                      ? GENERATOR_MENU_TRACK_SELECTED_CLASS
                                      : isDisabled
                                        ? 'bg-transparent text-white/20 cursor-not-allowed'
                                        : 'bg-transparent text-white/45 hover:text-white/80'
                                      }`}
                                    onClick={() => {
                                      if (!isDisabled) {
                                        setSelectedGrokMode(mode);
                                      }
                                    }}
                                  >
                                    {t(`moreMenu.grokMode.${mode}`)}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {selectedModelCaps.allowedBackgrounds?.length > 0 && (
                          <div className="mt-4 border-t border-[#44352D]/60 pt-4 text-left">
                            <div className="flex items-center gap-1.5 text-sm font-medium text-white/90">
                              {t('moreMenu.background.title')}
                              <Tooltip><TooltipTrigger asChild><button type="button" aria-label={t('moreMenu.background.info')} className="text-white/45 hover:text-white/80"><IconInfoCircle size={13} /></button></TooltipTrigger><TooltipContent side="top" className={`max-w-80 whitespace-normal text-left ${GENERATOR_TOOLTIP_SURFACE_CLASS}`}>{t('moreMenu.background.info')}</TooltipContent></Tooltip>
                            </div>
                            <div className={`mt-2.5 grid grid-cols-3 gap-1 rounded-xl p-0.5 ${GENERATOR_MENU_TRACK_CLASS}`}>
                              {['transparent', 'opaque', 'auto'].map((value) => (
                                <button key={value} type="button" className={`h-9 rounded-lg px-2 text-xs font-medium ${selectedBackground === value ? GENERATOR_MENU_TRACK_SELECTED_CLASS : 'text-white/45 hover:text-white/80'}`} onClick={() => setSelectedBackground(value)}>{t(`moreMenu.background.${value}`)}</button>
                              ))}
                            </div>
                          </div>
                        )}

                        {canSelectModeration && (
                          <div className="mt-4 border-t border-[#44352D]/60 pt-4 text-left">
                            <div className="text-sm font-medium text-white/90">{t('moreMenu.moderation.title')}</div>
                            <div className={`mt-2.5 grid grid-cols-2 gap-1 rounded-xl p-0.5 ${GENERATOR_MENU_TRACK_CLASS}`}>
                              {selectedModelCaps.allowedModerations.map((value) => (
                                <button key={value} type="button" className={`h-9 rounded-lg px-3 text-sm font-medium ${selectedModeration === value ? GENERATOR_MENU_TRACK_SELECTED_CLASS : 'text-white/45 hover:text-white/80'}`} onClick={() => setSelectedModeration(value)}>{t(`moreMenu.moderation.${value}`)}</button>
                              ))}
                            </div>
                          </div>
                        )}

                        {canUseContextIr && (
                          <div className="mt-4 border-t border-[#44352D]/60 pt-4">
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-1.5 text-sm font-medium text-white/90">
                                {t('moreMenu.contextIr.title')}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button type="button" aria-label={t('moreMenu.contextIr.info')} className="text-white/45 hover:text-white/80"><IconInfoCircle size={13} /></button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className={`max-w-64 ${GENERATOR_TOOLTIP_SURFACE_CLASS}`}>{t('moreMenu.contextIr.info')}</TooltipContent>
                                </Tooltip>
                              </div>
                              <button type="button" role="switch" aria-label={t('moreMenu.contextIr.title')} aria-checked={contextIrEnabled && !isUnlimitedOutputCountLocked} disabled={isUnlimitedOutputCountLocked}
                                onClick={() => setContextIrEnabled((prev) => !prev)}
                                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${contextIrEnabled && !isUnlimitedOutputCountLocked ? menuToggleActiveClassName : 'border-white/10 bg-white/10'}`}>
                                <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${contextIrEnabled && !isUnlimitedOutputCountLocked ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                              </button>
                            </div>
                            <p className="mt-1 text-xs leading-5 text-white/45">{t(isUnlimitedOutputCountLocked ? 'moreMenu.contextIr.unavailable' : 'moreMenu.contextIr.price')}</p>
                          </div>
                        )}

                        {canToggleGenerateAudio && (
                          <div className="mt-4 border-t border-[#44352D]/60 pt-4">
                            {/* 标题行 + 开关 */}
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-1.5">
                                <div className="text-left text-sm font-medium text-white/90">{t(`moreMenu.${audioOptionMessageKey}.title`)}</div>
                                {isKlingOmniKeepAudioMode && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        className="text-white/35 transition-colors hover:text-white/60"
                                        aria-label={t('moreMenu.keepAudio.info')}
                                      >
                                        <IconInfoCircle size={13} />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className={`max-w-64 ${GENERATOR_TOOLTIP_SURFACE_CLASS}`}>
                                      {t('moreMenu.keepAudio.info')}
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                              <button
                                type="button"
                                role="switch"
                                aria-checked={generateAudio}
                                onClick={() => setGenerateAudio((prev) => !prev)}
                                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors ${generateAudio
                                  ? menuToggleActiveClassName
                                  : 'border-white/10 bg-white/10'
                                  }`}
                              >
                                <span
                                  className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${generateAudio ? 'translate-x-[18px]' : 'translate-x-0.5'
                                    }`}
                                />
                              </button>
                            </div>
                            {/* 说明文字 - 单行截断 + more/less */}
                            {!isKlingOmniKeepAudioMode && (
                              audioDescExpanded ? (
                                <p className="mt-1 whitespace-pre-line text-xs leading-5 text-white/45">
                                  {t('moreMenu.generateAudio.description')}{' '}
                                  <button type="button" onClick={() => setAudioDescExpanded(false)} className="underline underline-offset-2 text-white/40 hover:text-white/70 transition-colors">{t('moreMenu.less')}</button>
                                </p>
                              ) : (
                                <p className="mt-1 text-xs leading-5 text-white/45">
                                  {t('moreMenu.generateAudio.descriptionShort')}{' '}
                                  <button type="button" onClick={() => setAudioDescExpanded(true)} className="underline underline-offset-2 text-white/40 hover:text-white/70 transition-colors">{t('moreMenu.more')}</button>
                                </p>
                              )
                            )}
                          </div>
                        )}

                        {canUseSeed && (
                          <div className="mt-4 border-t border-[#44352D]/60 pt-4">
                            {/* HappyHorse / BotGo Video 2 始终使用 seed，不显示开关 */}
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-1.5">
                                <div className="text-left text-sm font-medium text-white/90">{t('moreMenu.seed.title')}</div>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      className="text-white/35 transition-colors hover:text-white/60"
                                      aria-label={t('moreMenu.seed.info')}
                                    >
                                      <IconInfoCircle size={13} />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className={`max-w-80 ${GENERATOR_TOOLTIP_SURFACE_CLASS}`}>
                                    {t('moreMenu.seed.info')}
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                              {!isAlwaysOnSeedModel && (
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={seedEnabled}
                                  onClick={() => setSeedEnabled((prev) => !prev)}
                                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors ${seedEnabled
                                    ? menuToggleActiveClassName
                                    : 'border-white/10 bg-white/10'
                                    }`}
                                >
                                  <span
                                    className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${seedEnabled ? 'translate-x-[18px]' : 'translate-x-0.5'
                                      }`}
                                  />
                                </button>
                              )}
                            </div>
                            {/* 数值框 + 刷新按钮（常开模型始终显示；其他模型开关 ON 时显示） */}
                            {(isAlwaysOnSeedModel || seedEnabled) && (
                              <div className={`group/seed mt-2 flex cursor-default items-center gap-2 rounded-xl px-3.5 py-2.5 transition-colors hover:bg-[#433229] ${GENERATOR_MENU_TRACK_CLASS}`}>
                                <span className="flex-1 font-mono text-sm text-white/60 tabular-nums select-all">{seed}</span>
                                <button
                                  type="button"
                                  title={t('moreMenu.seed.randomizeTitle')}
                                  aria-label={t('moreMenu.seed.randomizeTitle')}
                                  onClick={() => setSeed(createRandomSeed(selectedModel))}
                                  className="flex h-6 w-6 shrink-0 items-center justify-center text-white/35 transition-all group-hover/seed:text-white/80"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="1 4 1 10 7 10" /><polyline points="23 20 23 14 17 14" />
                                    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
                                  </svg>
                                </button>
                              </div>
                            )}
                            {/* 非常开模型且开关 OFF 时显示说明文案 */}
                            {!isAlwaysOnSeedModel && !seedEnabled && (
                              seedDescExpanded ? (
                                <p className="mt-1 text-xs leading-5 text-white/40">
                                  {t('moreMenu.seed.description')}{' '}
                                  <button type="button" onClick={() => setSeedDescExpanded(false)} className="underline underline-offset-2 text-white/35 hover:text-white/70 transition-colors">{t('moreMenu.less')}</button>
                                </p>
                              ) : (
                                <p className="mt-1 text-xs leading-5 text-white/40">
                                  {t('moreMenu.seed.descriptionShort')}{' '}
                                  <button type="button" onClick={() => setSeedDescExpanded(true)} className="underline underline-offset-2 text-white/35 hover:text-white/70 transition-colors">{t('moreMenu.more')}</button>
                                </p>
                              )
                            )}
                          </div>
                        )}

                        {canShowWebSearchOption && (
                          <div className="mt-4 border-t border-[#44352D]/60 pt-4">
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-1.5">
                                <div className="text-left text-sm font-medium text-white/90">{t('moreMenu.webSearch.title')}</div>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      className="text-white/35 transition-colors hover:text-white/60"
                                      aria-label={t('moreMenu.webSearch.info')}
                                    >
                                      <IconInfoCircle size={13} />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className={`max-w-56 ${GENERATOR_TOOLTIP_SURFACE_CLASS}`}>
                                    {t('moreMenu.webSearch.info')}
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                              <button
                                type="button"
                                role="switch"
                                aria-checked={isWebSearchEligible ? webSearchEnabled : false}
                                disabled={!isWebSearchEligible}
                                onClick={() => {
                                  if (!isWebSearchEligible) return;
                                  setWebSearchEnabled((prev) => !prev);
                                }}
                                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors ${isWebSearchEligible
                                  ? (webSearchEnabled
                                    ? menuToggleActiveClassName
                                    : 'border-white/10 bg-white/10')
                                  : 'cursor-not-allowed border-white/10 bg-white/5 opacity-60'
                                  }`}
                              >
                                <span
                                  className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${(isWebSearchEligible && webSearchEnabled) ? 'translate-x-[18px]' : 'translate-x-0.5'
                                    }`}
                                />
                              </button>
                            </div>
                            {webSearchDescExpanded ? (
                              <p className="mt-1 whitespace-pre-line text-xs leading-5 text-white/45">
                                {t('moreMenu.webSearch.description')}{' '}
                                <button type="button" onClick={() => setWebSearchDescExpanded(false)} className="underline underline-offset-2 text-white/40 hover:text-white/70 transition-colors">{t('moreMenu.less')}</button>
                              </p>
                            ) : (
                              <p className="mt-1 text-xs leading-5 text-white/45">
                                {t('moreMenu.webSearch.descriptionShort')}{' '}
                                <button type="button" onClick={() => setWebSearchDescExpanded(true)} className="underline underline-offset-2 text-white/40 hover:text-white/70 transition-colors">{t('moreMenu.more')}</button>
                              </p>
                            )}
                          </div>
                        )}

                        {/* Public Visibility 单开关（PR-05）：与 Settings Account
                            共用同一展示组件与 i18n keys；API available=false 时
                            整体隐藏，不阻断旧 Generator。 */}
                        {publicVisibilityAvailable && (
                          <PublicVisibilityToggle
                            variant="menu"
                            className="mt-4 border-t border-[#44352D]/60 pt-4"
                            value={publicVisibilityValue}
                            onChange={setPublicVisibilityLocalValue}
                          />
                        )}

                        <div className="mt-4 border-t border-[#44352D]/60 pt-4 text-left">
                          <div className="text-left text-sm font-medium text-white/90">{t('moreMenu.randomPrompt.title')}</div>
                          <button
                            type="button"
                            className={`mt-2.5 flex h-10 w-full items-center justify-center gap-2 rounded-xl px-4 py-0 text-sm font-medium text-white/85 transition-all hover:bg-[#433229] hover:text-white focus:outline-none focus-visible:ring-1 focus-visible:ring-white/[0.22] ${GENERATOR_MENU_TRACK_CLASS}`}
                            onClick={() => { handleRandom(); toggleMenu('more'); }}
                          >
                            <IconRefresh size={14} />
                            {t('moreMenu.randomPrompt.button')}
                          </button>
                        </div>
                      </div>
                    </TooltipProvider>
                  )}
                </div>}

              </div>

              {/* Right: Generation Mode & Submit */}
              <div className="flex items-end gap-1">
                <GenerationModeControl
                  mode={selectedGenerationMode}
                  canSelectUnlimitedMode={canSelectUnlimitedMode}
                  disabled={isButtonBusy}
                  creditCostLabel={creditModeCostLabel}
                  creditTooltip={creditPricingTooltip || null}
                  triggerLabel={t('generationMode.trigger')}
                  creditTitle={t('generationMode.creditTitle')}
                  creditInstantDescription={t(isPaidMember
                    ? 'generationMode.creditInstantDescription'
                    : 'generationMode.freeCreditQueueDescription')}
                  creditSpeedDescription={t(isPaidMember
                    ? 'generationMode.paidCreditSpeedDescription'
                    : 'generationMode.freeCreditSpeedDescription')}
                  unlimitedTitle={t('generationMode.unlimitedTitle')}
                  unlimitedSubtitle={generationModeUnlimitedSubtitle}
                  unlimitedNote={unlimitedModeNote}
                  unlimitedAvailableDescription={t('generationMode.unlimitedAvailableDescription')}
                  unlimitedQueueNote={t('generationMode.unlimitedQueueNote')}
                  unlimitedLockedDescription={generationModeLockedDescription}
                  statusOnLabel={t('generationMode.statusOn')}
                  busyLabel={t('generationMode.busyBadge')}
                  upgradeLabel={t('generationMode.upgrade')}
                  onModeChange={handleGenerationModeChange}
                  onUpgrade={handleGenerationModeUpgrade}
                  openUp={openDropdownUp}
                  className="max-sm:ml-6"
                />

                <div
                  data-generate-submit-wrapper
                  tabIndex={isGenerateDisabled && !isButtonBusy ? 0 : undefined}
                  aria-label={isGenerateDisabled && !isButtonBusy ? generateDisabledTooltip : undefined}
                  className="group relative focus:outline-none"
                >
                  <Button
                    type="submit"
                    aria-label={t('generateButton')}
                    disabled={isGenerateDisabled}
                    className={`
                        w-10 h-[38px] rounded-[15px] p-0 flex items-center justify-center transition-all shadow-lg
                        ${isGenerateDisabled
                        ? 'cursor-not-allowed border border-[#44352D] bg-[#352820] text-gray-500 hover:bg-[#352820] disabled:pointer-events-auto'
                        : 'bg-amber-500 text-white hover:bg-amber-600 shadow-amber-900/40 hover:scale-105 active:scale-95'}
                    `}
                  >
                    {isButtonBusy ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      // Custom Bold Arrow SVG
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 4L12 20M12 4L5 11M12 4L19 11" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </Button>

                  {/* Tooltip for Disabled State (Z-index Fix) */}
                  {(isGenerateDisabled && !isButtonBusy) && (
                    <div
                      role="tooltip"
                      className={`pointer-events-none absolute bottom-full left-1/2 right-auto z-[100] mb-2 w-[min(18rem,calc(100vw-2rem))] -translate-x-1/2 whitespace-normal rounded-lg px-3 py-2 text-xs font-normal leading-4 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 sm:left-auto sm:right-0 sm:w-auto sm:translate-x-0 sm:whitespace-nowrap xl:left-1/2 xl:right-auto xl:-translate-x-1/2 ${GENERATOR_TOOLTIP_SURFACE_CLASS}`}
                    >
                      {generateDisabledTooltip}
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>

          {/* Turnstile Widget (Inline/Overlay) */}
          {shouldShowTurnstileInline && (
            <div className="my-4 flex justify-center">
              <TurnstileWidget
                key={`turnstile-${turnstileInstanceKey}`}
                ref={turnstileRef}
                action="generate"
                onVerify={handleTurnstileVerify}
                onExpire={handleTurnstileExpire}
                onError={handleTurnstileError}
                theme="dark"
              />
            </div>
          )}
          {turnstileOverlay}

        </form>
      </Form>

      {assetPickerOpen && (
        <div
          data-generation-asset-picker="true"
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/75 p-4 backdrop-blur-md"
          onClick={closeAssetPicker}
        >
          <div
            className={`flex h-[min(78vh,680px)] w-full max-w-[960px] flex-col overflow-hidden rounded-2xl ${GENERATOR_MENU_PANEL_CLASS}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[#44352D] px-6 py-4">
              <div className="flex items-center gap-2">
                {assetPickerTabs.map((tab) => {
                  const isActive = assetPickerActiveType === tab.type;
                  return (
                    <button
                      key={tab.type}
                      type="button"
                      disabled={!tab.allowed}
                      className={[
                        'rounded-lg px-3 py-2 text-sm font-semibold transition',
                        isActive
                          ? 'bg-white/10 text-white'
                          : 'text-white/55 hover:bg-white/5 hover:text-white/80',
                        !tab.allowed ? 'cursor-not-allowed opacity-35 hover:bg-transparent hover:text-white/55' : '',
                      ].join(' ')}
                      onClick={() => handleAssetPickerTypeChange(tab.type)}
                    >
                      {tab.label}
                      {tab.count > 0 && (
                        <span className="ml-1.5 text-xs font-medium text-white/40">{tab.count}</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                className="rounded-full p-1.5 text-white/70 transition hover:bg-white/10 hover:text-white"
                onClick={closeAssetPicker}
              >
                <IconX size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
              {assetPickerLoading ? (
                <div className="flex h-full min-h-64 items-center justify-center">
                  <LoadingSpinner size="sm" />
                </div>
              ) : assetPickerError ? (
                <div className="rounded-xl border border-red-400/20 bg-red-500/10 px-4 py-6 text-center text-sm text-red-100">
                  {assetPickerError}
                </div>
              ) : activeAssetPickerItems.length === 0 ? (
                <div className={`flex h-full min-h-64 items-center justify-center rounded-xl px-4 py-8 text-center text-sm text-white/55 ${GENERATOR_MENU_INSET_CLASS}`}>
                  {t('assetPicker.empty')}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                  {activeAssetPickerItems.map((item) => {
                    const type = getDraftAssetType(item);
                    const assetKey = getDraftAssetKey(item);
                    const url = getDraftAssetUrl(item);
                    const previewUrl = item.thumbnailUrl || url;
                    const selected = assetPickerSelectedKeySet.has(assetKey);
                    const durationLabel = type === 'video' ? formatDraftDurationLabel(item.duration) : '';
                    const disabled =
                      !isAssetPickerActiveTypeAllowed ||
                      (!selected && assetPickerSelectedCount >= maxSelectableDraftAssets);
                    return (
                      <button
                        key={assetKey}
                        type="button"
                        disabled={disabled}
                        className={[
                          'group relative aspect-square overflow-hidden rounded-xl border bg-[#2B211B] text-left transition',
                          selected ? 'border-amber-400 shadow-[0_0_0_1px_rgba(251,191,36,0.6)]' : '',
                          disabled
                            ? 'cursor-not-allowed opacity-45'
                            : 'border-[#46372E] hover:border-amber-400/70 hover:shadow-[0_18px_40px_-26px_rgba(245,158,11,0.8)]',
                        ].join(' ')}
                        onClick={() => toggleDraftAssetSelection(item)}
                      >
                        {type === 'video' ? (
                          <video
                            src={url}
                            poster={item.thumbnailUrl || undefined}
                            className="h-full w-full object-cover"
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="metadata"
                          />
                        ) : type === 'audio' ? (
                          <span className="flex h-full w-full flex-col items-center justify-center bg-[#2B211C] text-white/55">
                            <IconMusic size={28} />
                            <span className="mt-2 text-xs font-medium">{item.prompt || item.modelKey || t('assetPicker.untitled')}</span>
                          </span>
                        ) : previewUrl ? (
                          <img src={previewUrl} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center text-white/40">
                            <IconPhoto size={24} />
                          </span>
                        )}
                        {selected && (
                          <span className="absolute left-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-white text-black shadow">
                            <IconCheck size={13} stroke={3} />
                          </span>
                        )}
                        {durationLabel && (
                          <span className="absolute bottom-2 right-2 rounded-md bg-black/70 px-1.5 py-0.5 text-[11px] font-medium text-white/90">
                            {durationLabel}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t border-[#44352D] px-6 py-4">
              <div className="text-sm text-white/55">
                {t('assetPicker.selectedCount', { count: assetPickerSelectedCount })}
              </div>
              <button
                type="button"
                disabled={assetPickerSelectedCount === 0}
                className={[
                  'rounded-lg px-5 py-2 text-sm font-semibold transition',
                  assetPickerSelectedCount === 0
                    ? 'cursor-not-allowed bg-white/10 text-white/35'
                    : 'bg-white text-black hover:bg-white/90',
                ].join(' ')}
                onClick={handleConfirmDraftAssets}
              >
                {t('assetPicker.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Asset Preview Modal */}
      {assetPreviewOverlay}

      {/* Modals */}
      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        onClose={() => setShowInsufficientCreditsModal(false)}
        onTopUp={() => {
          setShowInsufficientCreditsModal(false);
          setShowTopUpModal(true);
        }}
        canUpgrade={canUpgradeForInsufficientCredits}
      />
      <TopUpModal
        isOpen={showTopUpModal}
        onClose={() => setShowTopUpModal(false)}
        isPaidMember={isPaidMember}
      />
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        redirectPath={pathname}
      />
      <PricingModal
        isOpen={isPricingModalOpen}
        onClose={() => setIsPricingModalOpen(false)}
      />

      <AlertDialog
        open={Boolean(generationModeFallback)}
        onOpenChange={handleGenerationModeFallbackOpenChange}
      >
        <AlertDialogContent className={GENERATOR_MENU_PANEL_CLASS}>
          <AlertDialogHeader className="space-y-3 text-left">
            <AlertDialogTitle className="text-[20px] font-semibold text-white">
              {generationModeFallback?.title || t('generationMode.fallbackTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-6 text-white/68">
              {generationModeFallback?.message || t('generationMode.fallbackDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4 gap-3 sm:justify-end sm:space-x-0">
            <AlertDialogCancel
              onClick={handleGenerationModeFallbackTryLater}
              className={`mt-0 rounded-xl text-white/80 ${GENERATOR_MENU_ACTION_CLASS}`}
            >
              {t('generationMode.tryLater')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleGenerationModeFallbackPrimaryAction}
              className="rounded-xl border-none bg-amber-500 text-white hover:bg-amber-400"
            >
              {generationModeFallbackPrimaryLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upgrade Modal */}
      <AlertDialog open={showUpgradeModal} onOpenChange={handleUpgradeModalOpenChange}>
        <AlertDialogContent className={GENERATOR_MENU_PANEL_CLASS}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('upgradeModal.title')}</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400 pb-3">
              <span className="block">{upgradeDescriptionLine1}</span>
              {upgradeDescriptionLine2 ? (
                <span className="block mt-1">{upgradeDescriptionLine2}</span>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-3 gap-5 sm:gap-6">
            <AlertDialogCancel className={`text-white ${GENERATOR_MENU_ACTION_CLASS}`}>
              {t('upgradeModal.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleUpgradeModalOpenChange(false);
                setIsPricingModalOpen(true);
              }}
              className="bg-amber-600 hover:bg-amber-700 text-white border-none"
            >
              {upgradeModalConfirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
} 
